/**
 * BloomQuiz — headless quiz engine.
 *
 * Pure logic, no DOM. Host page (nav.html, Shopify page, any landing) is
 * responsible for rendering UI on top of this engine.
 *
 * Usage:
 *   const engine = BloomQuiz.createEngine(BLOOM_GIFTS); // or BLOOM_SELF
 *   engine.steps                  // array of step configs for rendering
 *   engine.getStep(i)             // step config at index i
 *   engine.answer(key, value)     // record an answer
 *   engine.getState()             // { key: value, ... }
 *   engine.isComplete()           // all steps answered?
 *   engine.score()                // ranked [{ id, score, collection }, ...]
 *   engine.getAnswerLabels()      // human labels of selected options
 *   engine.reset()                // clear answers
 *
 * Data contract: `data` is a full BLOOM_* object with `quiz` and `collections`.
 * `quiz.map` values are *keys into `collections`* — in the current nav.html
 * `collections` is indexed by title, so map values are titles. If host
 * indexes collections by id, map values must be ids.
 */
(function (global) {
	"use strict";

	function createEngine(data) {
		if (!data || !data.quiz || !data.collections) {
			throw new Error("BloomQuiz: data must have { quiz, collections }");
		}
		const quiz = data.quiz;
		const collections = data.collections;
		let state = {};

		return {
			steps: quiz.steps,
			stepCount: quiz.steps.length,

			getStep: function (i) {
				return quiz.steps[i];
			},

			answer: function (key, value) {
				state[key] = value;
				return this;
			},

			getState: function () {
				return Object.assign({}, state);
			},

			reset: function () {
				state = {};
				return this;
			},

			isComplete: function () {
				for (const s of quiz.steps) if (state[s.key] == null) return false;
				return true;
			},

			score: function () {
				const scores = {};
				for (const [k, v] of Object.entries(state)) {
					const bucket = quiz.map[k] && quiz.map[k][v];
					if (!bucket) continue;
					for (const id of bucket) {
						if (collections[id]) scores[id] = (scores[id] || 0) + 1;
					}
				}
				return Object.entries(scores)
					.sort(function (a, b) {
						if (b[1] !== a[1]) return b[1] - a[1];
						const ap = (collections[a[0]].products || []).length;
						const bp = (collections[b[0]].products || []).length;
						return bp - ap;
					})
					.map(function (entry) {
						return { id: entry[0], score: entry[1], collection: collections[entry[0]] };
					});
			},

			getAnswerLabels: function () {
				const labels = [];
				for (const [k, v] of Object.entries(state)) {
					const step = quiz.steps.find(function (s) { return s.key === k; });
					const opt = step && step.options.find(function (o) { return o.id === v; });
					if (opt) labels.push(opt.title);
				}
				return labels;
			},
		};
	}

	const api = { createEngine: createEngine };

	if (typeof module !== "undefined" && module.exports) {
		module.exports = api;
	}
	global.BloomQuiz = api;
})(typeof window !== "undefined" ? window : globalThis);

/**
 * BloomBrowse — headless guided-navigation helper.
 *
 * Pure data layer, no DOM. Host page renders its own UI — this module
 * returns structured data for each screen of the 3-step browse flow:
 *   categories → sections → collections.
 *
 * Usage:
 *   const nav = BloomBrowse.createNavigator(BLOOM_GIFTS); // or BLOOM_SELF
 *
 *   // Screen 1 — list categories
 *   nav.categories();
 *     → [{ name, icon, count }, ...]
 *
 *   // Screen 2 — list sections inside a category
 *   nav.sections("By Mood & Aesthetic");
 *     → { skip: false, items: [{ label, count, index }, ...] }
 *     // when category has a single section → { skip: true, sectionIndex: 0 }
 *
 *   // Screen 3 — list collections inside a section
 *   nav.items("By Mood & Aesthetic", 0);
 *     → { category, section, items: [{ title, subtitle, id, exists }, ...] }
 *
 *   // Helpers
 *   nav.getMenu();                // raw menu object
 *   nav.getIcons();               // raw icons map
 *   nav.getCategoryNames();       // [names, ...]
 *   nav.findCollection(title);    // DATA[title] or null
 *
 * Data contract: `data` is a full BLOOM_* object with `browse` and `collections`.
 * `browse.menu[cat].sections[i].items[j]` values are keys into `collections`
 * (titles, like `quiz.map`). If host indexes collections by id, pre-build a
 * title-keyed DATA before calling createNavigator.
 */
(function (global) {
	"use strict";

	function createNavigator(data) {
		if (!data || !data.browse || !data.collections) {
			throw new Error("BloomBrowse: data must have { browse, collections }");
		}
		const menu = data.browse.menu || {};
		const icons = data.browse.icons || {};
		const collections = data.collections;

		function countItems(cat) {
			const c = menu[cat];
			if (!c) return 0;
			let n = 0;
			for (const sec of c.sections) n += sec.items.length;
			return n;
		}

		return {
			getMenu: function () { return menu; },
			getIcons: function () { return icons; },

			getCategoryNames: function () {
				return Object.keys(menu);
			},

			categories: function () {
				return Object.keys(menu).map(function (name) {
					return {
						name: name,
						icon: icons[name] || "",
						count: countItems(name),
					};
				});
			},

			sections: function (cat) {
				const c = menu[cat];
				if (!c) return { skip: false, items: [] };
				if (c.sections.length === 1) {
					return { skip: true, sectionIndex: 0 };
				}
				return {
					skip: false,
					items: c.sections.map(function (sec, i) {
						return { label: sec.label, count: sec.items.length, index: i };
					}),
				};
			},

			items: function (cat, secIdx) {
				const c = menu[cat];
				const sec = c && c.sections[secIdx];
				if (!sec) return { category: cat, section: null, items: [] };
				return {
					category: cat,
					section: sec.label,
					items: sec.items.map(function (title) {
						const coll = collections[title];
						return {
							title: title,
							subtitle: coll ? coll.subtitle : "",
							id: coll ? coll.id : null,
							exists: !!coll,
						};
					}),
				};
			},

			findCollection: function (title) {
				return collections[title] || null;
			},
		};
	}

	const api = { createNavigator: createNavigator };

	if (typeof module !== "undefined" && module.exports) {
		module.exports = api;
	}
	global.BloomBrowse = api;
})(typeof window !== "undefined" ? window : globalThis);

const BLOOM_SELF = {
	"updated_at": "2026-11-05T15:17:15.255Z",
	"total_collections": 83,
	"total_unique_products": 354,
	"products": {
		"202": {
			"title": "202 Frutti Solari",
			"brand": "Bon Parfumeur",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/202_500x.jpg?v=1779904762",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"301": {
			"title": "301 Noces de Santal",
			"brand": "Bon Parfumeur",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/301_500x.jpg?v=1779905209",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"302": {
			"title": "302 Groovy Amber",
			"brand": "Bon Parfumeur",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/302_500x.jpg?v=1779905244",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"565": {
			"title": "565",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/565_3ea757ea-98c0-44ea-a3ac-b4921c01b8a6_500x.jpg?v=1632583902",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"602": {
			"title": "602 Bois Narcotique",
			"brand": "Bon Parfumeur",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/602_500x.jpg?v=1779906219",
			"gender": "Male",
			"collection_handle": "bon-parfumeur",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"803": {
			"title": "803 Givre Patchouli",
			"brand": "Bon Parfumeur",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/803_500x.jpg?v=1779898265",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"1993": {
			"title": "1993",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1993_500x.jpg?v=1750077107",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"20359": {
			"title": "20359",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_20359_500x.jpg?v=1788782995",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vanille-glace": {
			"title": "Vanille Glacé",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanille_500x.jpg?v=1783068498",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"ruby": {
			"title": "Ruby",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ruby_500x.jpg?v=1765390184",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vanhera": {
			"title": "Vanhera",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/vanhera_5e196cc6-073e-4f33-aff5-86840e8d5535_500x.jpg?v=1669649566",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"happy-nuts": {
			"title": "NUTS (formerly known as HAPPY NUTS)",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/HAPPYNUTS_BOTTLE_500x.png?v=1710143839",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sadonaso": {
			"title": "Sadonaso",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sadonaso_500x.jpg?v=1756321000",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"nightfall-in-the-city-of-kings": {
			"title": "Nightfall in the City of Kings",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_nightfallt_500x.jpg?v=1776444005",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dark-honey": {
			"title": "Dark Honey",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dark_honey_new_500x.jpg?v=1792512680",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"mooncake": {
			"title": "Mooncake | 月餅",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bc-mooncake_500x.jpg?v=1760113353",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"nebula-viridis": {
			"title": "Nebula Viridis",
			"brand": "Aura Eximia",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_f5c43f2bb55f4efbb06d6e3623690bcb_mv2_1_500x.png?v=1785402457",
			"gender": "Unisex",
			"collection_handle": "aura-eximia",
			"price_ol": 165,
			"price_ol_ml": 50,
			"price_tf": 137.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"masa": {
			"title": "Masa",
			"brand": "Kajal",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/masa_500x.jpg?v=1765389953",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"resonant": {
			"title": "Resonant",
			"brand": "Oddity",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/resonant_oddity_500x.jpg?v=1678910627",
			"gender": "Unisex",
			"collection_handle": "oddity",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"sal-y-limon": {
			"title": "Sal y Limón",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/carner_sal_y_limon_500x.jpg?v=1660318127",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"neroli-petitgrain": {
			"title": "Neroli & Petitgrain",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neroli_petit_grain_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"thirsty": {
			"title": "Thirsty",
			"brand": "Fugazzi",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/100_thirsty_500x.png?v=1745859441",
			"gender": "Unisex",
			"collection_handle": "fugazzi",
			"price_ol": 205,
			"price_ol_ml": 100,
			"price_tf": 170.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"portamarina": {
			"title": "Portamarina",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/portamania_500x.jpg?v=1764500453",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"a-grove-by-the-sea": {
			"title": "A Grove By The Sea",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ARQUISTE_A_GROVE_BY_THE_SEA_500x.jpg?v=1722411367",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg16-jardins-de-kerylos": {
			"title": "PG16 Jardins de Kérylos",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PG16_Jardin_de_Kerylos_500x.jpg?v=1589662162",
			"gender": "Female",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"playground-1": {
			"title": "Playground",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/der_duft_playground_6443ec99-dc9f-44c5-8c9a-3733b5c80df2_500x.png?v=1747051567",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"moonlight-ceremony": {
			"title": "Moonlight Ceremony",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_moonlight_500x.jpg?v=1776443717",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"black": {
			"title": "Black",
			"brand": "Comme Des Garcons",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_8652c882-6fb1-47d4-b691-a30985352886_500x.jpg?v=1769330871",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"oud-imperial-extrait": {
			"title": "Oud Imperial Extrait",
			"brand": "Perris Monte Carlo",
			"price": 250,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Extrait-Oud-Imperial_500x.jpg?v=1609164109",
			"gender": "Unisex",
			"collection_handle": "perris-monte-carlo",
			"price_ol": 250,
			"price_ol_ml": 50,
			"price_tf": 208.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vi-et-armis": {
			"title": "Vi et Armis",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BeauFort_London_Vi_et_Armis_Bloom_500x.jpg?v=1623765299",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"corpus-equus": {
			"title": "Corpus Equus",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_25252BCORPUS_25252BEQUUS_500x.jpg?v=1633940026",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"honey-and-tar": {
			"title": "Honey and Tar",
			"brand": "Anna Zworykina",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/anna_honey_500x.jpg?v=1777360645",
			"gender": "Unisex",
			"collection_handle": "anna-zworykina",
			"price_ol": 95,
			"price_ol_ml": 10,
			"price_tf": 79.17,
			"price_tf_ml": 10,
			"concentration": "EdP"
		},
		"beso-de-fuego": {
			"title": "Beso de Fuego",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/beso_fuego_500x.jpg?v=1783354059",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cruelle": {
			"title": "Cruelle",
			"brand": "Wesker",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Cruelle-Product-Page-1800-1_500x.jpg?v=1747312888",
			"gender": "Unisex",
			"collection_handle": "wesker",
			"price_ol": 220,
			"price_ol_ml": 75,
			"price_tf": 183.33,
			"price_tf_ml": 75,
			"concentration": "Extrait de Parfum"
		},
		"cuoium": {
			"title": "Cuoium",
			"brand": "Orto Parisi",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orto_cuoium_275a3644-0d9d-41e9-8901-669b0ab2542d_500x.jpg?v=1758620326",
			"gender": "Unisex",
			"collection_handle": "orto-parisi",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"cashmere-100": {
			"title": "Cashmere 100",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cashmere_500x.jpg?v=1783068412",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"white-rice": {
			"title": "White Rice",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/White-Rice_500x.jpg?v=1746626005",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg-41-le-musc-la-peau": {
			"title": "PG4.1 Le Musc & La Peau",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/le_musc_at_la_peau_pierre_guillaume_500x.jpg?v=1589662178",
			"gender": "Female",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"the-ghost-in-the-shell": {
			"title": "The Ghost In The Shell",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Ghost-in-the-Shell-flacon-100-ml_1800x1800_6a38eab9-76bd-4fc7-a90d-b9d697d75280_500x.jpg?v=1632307386",
			"gender": "Female",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"les-cahiers-secrets": {
			"title": "Les Cahiers Secrets (Secret Diaries)",
			"brand": "Jouissance",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/les_cahiers_500x.jpg?v=1792439386",
			"gender": "Female",
			"collection_handle": "jouissance",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"merina": {
			"title": "Merina",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/merina_500x.jpg?v=1781437552",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"yawahada": {
			"title": "Yawahada (Soft skin)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/yawahada-01_500x.jpg?v=1741339211",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"blanc-sada": {
			"title": "Blanc Sada",
			"brand": "L'Entropiste",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/blanc_sada_500x.jpg?v=1766576180",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 185,
			"price_ol_ml": 50,
			"price_tf": 154.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"intime-extime": {
			"title": "Intime-Extime",
			"brand": "Pierre Guillaume White Collection",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PIERRE-GUILLAUME-PARIS-PARFUM-COLLECTION-BLANCHE-INTIME-EXTIME-100ML_500x.png?v=1652202927",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-white-collection",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"jany": {
			"title": "Jany",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/jany_500x.jpg?v=1781697454",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"vietnamese-coffee": {
			"title": "Vietnamese Coffee",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Vietnamese-Coffee_500x.jpg?v=1746626102",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"celestial-object": {
			"title": "Celestial Object",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_CelestialObject_500x.jpg?v=1764835866",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"calisson": {
			"title": "Calisson",
			"brand": "Karzaz",
			"price": 69,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/calisson_500x.jpg?v=1773068174",
			"gender": "Unisex",
			"collection_handle": "karzaz",
			"price_ol": 69,
			"price_ol_ml": 50,
			"price_tf": 57.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"vanilla-for-breakfast": {
			"title": "Vanilla For Breakfast",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanilla_for_break_02dfd976-5533-49e3-b5d3-a6e77b00306f_500x.jpg?v=1783184168",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"davana-vanille": {
			"title": "Davana & Vanille",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/davana_vanilla_500x.jpg?v=1731682971",
			"gender": "Female",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"dominanti-santi": {
			"title": "Dominanti Santi",
			"brand": "Santi Peccatori",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dominanti_500x.jpg?v=1786728365",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"yuzu": {
			"title": "Yuzu",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/yuzu-01_500x.jpg?v=1741339275",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"tennis-club": {
			"title": "Tennis Club",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/carner_tennis_club_500x.jpg?v=1660317978",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"this-is-not-a-blue-bottle-1-6": {
			"title": "This is Not a Blue Bottle 1/.6 – Love",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_06_74efa7f4-ba07-4d26-896d-2e1761e7458f_500x.jpg?v=1757946455",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"kajal-iii": {
			"title": "Kajal III",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kajal_3_500x.jpg?v=1786565193",
			"gender": "Male",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"elisa": {
			"title": "Elisa",
			"brand": "Marie Jeanne",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/elisa_500x.jpg?v=1758616405",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"far": {
			"title": "Far",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/image_50379521_1d5acf8f-6014-4855-b122-2e742174ec50_500x.jpg?v=1718889300",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"still-life-in-rio": {
			"title": "Still Life in Rio",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/still_life_in_Rio_500x.jpg?v=1589665496",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lumire-blanche": {
			"title": "Lumiere Blanche",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/lb_500x.jpg?v=1589662069",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rose-shiso": {
			"title": "Rose Shiso ローズ シソ",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Rose-Shiso_zoom_500x.jpg?v=1738674696",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"flow": {
			"title": "Flow",
			"brand": "Stereotype",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Sterotype_Flow_500x.jpg?v=1776443110",
			"gender": "Female",
			"collection_handle": "stereotype",
			"price_ol": 130,
			"price_ol_ml": 75,
			"price_tf": 108.33,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"match": {
			"title": "Match",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Foto_DER_DUFT_MATCH_500x.jpg?v=1630486299",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"lola-at-coat-check": {
			"title": "Lola at Coat Check",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lola_4d3c0145-98a1-44fd-adac-685dee6dd578_500x.jpg?v=1768129234",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"eastern-veil": {
			"title": "Eastern Veil",
			"brand": "Ylem",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eastern_veil_500x.jpg?v=1777037850",
			"gender": "Unisex",
			"collection_handle": "ylem",
			"price_ol": 190,
			"price_ol_ml": 50,
			"price_tf": 158.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"oblivion": {
			"title": "Oblivion",
			"brand": "Circle of Lim",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oblivion_500x.jpg?v=1775823323",
			"gender": "Unisex",
			"collection_handle": "circle-of-lim",
			"price_ol": 110,
			"price_ol_ml": 30,
			"price_tf": 91.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"ramune": {
			"title": "Ramune (Japanese soda)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ramune-01_500x.jpg?v=1741339409",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"coton-egyptien": {
			"title": "Coton Egyptien",
			"brand": "Phaedon Paris",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/COTON-EGYPTIEN_100ML_070_500x.jpg?v=1625875901",
			"gender": "Unisex",
			"collection_handle": "phaedon-paris",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"sun-bleached": {
			"title": "Sun Bleached",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sun_500x.jpg?v=1783068206",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"this-is-not-a-blue-bottle-1-5": {
			"title": "This is Not a Blue Bottle 1/.5 – Yang",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_05_a1717a78-2081-4768-9928-54443c305edb_500x.jpg?v=1757946373",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"bo-bo": {
			"title": "Bo-Bo",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/bobo_new_carner_b5163c6c-252d-4539-b962-0c8af844155a_500x.jpg?v=1625873621",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vetiver-santal": {
			"title": "Vetiver Santal",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Vetiver-Santal_shadow_500x.jpg?v=1738671355",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bohemian": {
			"title": "Bohemian",
			"brand": "SweDoft",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BOHEMIAN_cd233cae-ece3-46c3-8b18-bd8d33a2cb45_500x.jpg?v=1632582931",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 185,
			"price_ol_ml": 100,
			"price_tf": 154.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"coeur-de-noir-1": {
			"title": "Coeur de Noir",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_BeauFort_London_Coeur_de_Noir_Bloom_500x.jpg?v=1623765413",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mora": {
			"title": "Mora",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-MORA-PERFUME-50ml-white_ae9de80c-3bcb-4924-95ac-8680c188f74c_500x.jpg?v=1742991705",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"fauno": {
			"title": "Fauno",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/fauno_500x.jpg?v=1657827144",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"smokin-gun": {
			"title": "SMOKIN' GUN",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SMOKINGUN_BOTTLE_500x.png?v=1710232432",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"all-black": {
			"title": "All Black",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/all_black_500x.jpg?v=1783068081",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"au-pied-du-rosier": {
			"title": "Au Pied du Rosier",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Au-pied-du-rosier_500x.jpg?v=1738674757",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"eye-contact": {
			"title": "Eye Contact",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eye_300ecb6f-3af2-4b4f-b910-737ee4c3e5d8_500x.jpg?v=1768129262",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"hey-bitch": {
			"title": "Hey Bxtch",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/hey_bitch_new_500x.jpg?v=1792512562",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"nuit-de-baklite": {
			"title": "Nuit de Bakélite",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_NUIT_DE_BAKELITE_2019_bloom_500x.png?v=1589662126",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"agrani": {
			"title": "Agrani",
			"brand": "Byermia",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/agrani_6e9a96b0-b83b-4837-bc09-c8f6ef1ac5c1_500x.jpg?v=1792073237",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 155,
			"price_ol_ml": 30,
			"price_tf": 129.17,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"cavallo-di-fuoco": {
			"title": "火马 - Cavallo di fuoco",
			"brand": "Zanini",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cavalio_500x.jpg?v=1789409626",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 210,
			"price_ol_ml": 30,
			"price_tf": 175,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"cuirs": {
			"title": "CUIRS",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Carner_CUIRS_500x.jpg?v=1589660934",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kamel-oud": {
			"title": "Kamel Oud",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kamel_oud_500x.jpg?v=1781698015",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"styrax": {
			"title": "STYRAX",
			"brand": "Headspace",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Styrax_Face_2380ac65-1d67-45c4-823e-9b496acf5eb0_500x.jpg?v=1699872642",
			"gender": "Unisex",
			"collection_handle": "headspace",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dangerosa": {
			"title": "Dangerosa",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dangerosa_500x.jpg?v=1783328654",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ara": {
			"title": "ARС",
			"brand": "Mallo",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mallo_arc_500x.jpg?v=1775660659",
			"gender": "Unisex",
			"collection_handle": "mallo",
			"price_ol": 220,
			"price_ol_ml": 30,
			"price_tf": 183.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"divina-flora": {
			"title": "Divina Flora",
			"brand": "Aura Eximia",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_bac3c41327f14615a069600a780e1889_mv2_1_500x.png?v=1785402567",
			"gender": "Unisex",
			"collection_handle": "aura-eximia",
			"price_ol": 225,
			"price_ol_ml": 50,
			"price_tf": 187.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"rusalka-s-embrace": {
			"title": "Rusalka’s Embrace",
			"brand": "Pictura Fragrans",
			"price": 177,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/RusalkasEmbracePicturaFragrans_500x.jpg?v=1775728815",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 177,
			"price_ol_ml": 50,
			"price_tf": 147.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"debonaire": {
			"title": "Debonaire",
			"brand": "Plowden & Fallow",
			"price": 108,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pf_15_500x.jpg?v=1716723817",
			"gender": "Unisex",
			"collection_handle": "plowden-fallow",
			"price_ol": 108,
			"price_ol_ml": 100,
			"price_tf": 90,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"immortali-santi": {
			"title": "Immortali Santi",
			"brand": "Santi Peccatori",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/immortali_8ba78e9d-320c-49bd-99cb-f5e93d1b1985_500x.jpg?v=1786728538",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"talento": {
			"title": "Talento",
			"brand": "Mendittorosa",
			"price": 315,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Talento_Mendittorosa_500x.jpg?v=1614678204",
			"gender": "Female",
			"collection_handle": "mendittorosa",
			"price_ol": 315,
			"price_ol_ml": 100,
			"price_tf": 262.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"nerosa": {
			"title": "Nerosa",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/nerosa_500x.jpg?v=1708450533",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"al-sarab": {
			"title": "Al Sarab",
			"brand": "Maysson",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/al_sarab_500x.jpg?v=1789046723",
			"gender": "Unisex",
			"collection_handle": "maysson",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"tiger-rouge": {
			"title": "Tiger Rouge",
			"brand": "Byermia",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tiger_rouge_500x.jpg?v=1792072917",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 160,
			"price_ol_ml": 30,
			"price_tf": 133.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"502-iris-cartagena": {
			"title": "502 Iris Cartagena",
			"brand": "Bon Parfumeur",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/502_iris_cartagena_500x.jpg?v=1739211593",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"fathom-v-directors-cut": {
			"title": "Fathom V Director's Cut",
			"brand": "Beaufort",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fathom_v_02_53b46293-1d32-434a-bb69-8af9023eb362_500x.jpg?v=1774687223",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 195,
			"price_ol_ml": 50,
			"price_tf": 162.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"le-mat": {
			"title": "Le Mat",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Le_Mat_1_biale_resize_500x.jpg?v=1589662051",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"devoti-peccatori": {
			"title": "Devoti Peccatori",
			"brand": "Santi Peccatori",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/devoti_7a69a5fd-156d-497b-8262-eefa0206d9c1_500x.jpg?v=1786727813",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 210,
			"price_ol_ml": 50,
			"price_tf": 175,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"lumieres": {
			"title": "Lumières",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lumieres_500x.jpg?v=1781437469",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"st-pauls-1": {
			"title": "St. Paul's",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pauls_500x.jpg?v=1780922153",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"rainy-city": {
			"title": "Rainy City",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rainy_city_500x.jpg?v=1737306038",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"santal": {
			"title": "Santal",
			"brand": "Floris",
			"price": 100,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/floris_santal_500x.jpg?v=1787327450",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 100,
			"price_ol_ml": 100,
			"price_tf": 83.33,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"albatros": {
			"title": "Albatros",
			"brand": "Mendittorosa",
			"price": 200,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mendittorosa-albatros-01_8f217198-cb0c-4628-8842-6e5eed3c23bc_500x.jpg?v=1705512881",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 200,
			"price_ol_ml": 100,
			"price_tf": 166.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"beso-pasion": {
			"title": "Beso Pasión",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/beso-pasion_500x.jpg?v=1610206676",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"floating": {
			"title": "Floating",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_Floating_500x.jpg?v=1764836387",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"castiamanti": {
			"title": "Castiamanti",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/castiamanti_500x.jpg?v=1657827333",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"the-empress-kiss": {
			"title": "The Empress Kiss",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Mystikum_empress_b7a030d0-8b09-4b98-b2bd-3627cec7577c_500x.jpg?v=1776443907",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lamar": {
			"title": "Lamar",
			"brand": "Kajal",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lamar_500x.jpg?v=1765390324",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 170,
			"price_ol_ml": 100,
			"price_tf": 141.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ta-if": {
			"title": "Ta’if",
			"brand": "Ormonde Jayne",
			"price": 235,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ta-if-88ml-front_1020x1530_1_500x.jpg?v=1711456555",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 235,
			"price_ol_ml": 88,
			"price_tf": 195.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"drunk-saffron": {
			"title": "DRUNK SAFFRON",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/DRUNKSAFFRON_BOTTLE_500x.png?v=1710232337",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rouge-avignon": {
			"title": "Rouge Avignon",
			"brand": "Phaedon Paris",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PHAEDON_ROUGE_AVIGNON_20201_500x.jpg?v=1589665446",
			"gender": "Unisex",
			"collection_handle": "phaedon-paris",
			"price_ol": 130,
			"price_ol_ml": 100,
			"price_tf": 108.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sirio": {
			"title": "Sirio",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Sirio_white_HD_500x.jpg?v=1589665482",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"honey-oud": {
			"title": "Honey Oud",
			"brand": "Floris",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/floris_honey_500x.jpg?v=1787327796",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cherry-passion": {
			"title": "Cherry Passion",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CHERRY_20PASSION-1006x698_2f9e4c76-a475-43da-8314-d6cb75d71697_500x.jpg?v=1661530569",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cloud-essence-28-08": {
			"title": "Cloud Essence 28.08",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_cloud_1a934fe1-5740-42fa-8bcb-3b97d3db4657_500x.jpg?v=1770373674",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"tonkade": {
			"title": "Tonkade",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tonkade_18414833-44f7-481b-93fa-583752ff357d_500x.jpg?v=1708450480",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"figlia-di-eva": {
			"title": "Figlia di Eva",
			"brand": "SweDoft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/FIGLIA-DI-EVA_500x.jpg?v=1632583623",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"mauna": {
			"title": "Mauna",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Mauna-01-main_48e5206b-9a96-41de-b5e0-42bf72820392_500x.jpg?v=1712072738",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"packed-your-holiday-perfume-wardrobe-unisex": {
			"title": "Packed: your holiday perfume wardrobe | Unisex",
			"brand": "Bloom Sample Packs",
			"price": 29,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/holiday_unisex_500x.jpg?v=1690809862",
			"gender": "Unisex",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 29,
			"price_ol_ml": 6,
			"price_tf": 24.17,
			"price_tf_ml": 6,
			"concentration": "6"
		},
		"niche-discovery-box": {
			"title": "Niche Discovery Box",
			"brand": "Ortir Apothecari",
			"price": 30,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/IMG_8890_2200x_b3129e42-76aa-44e9-a0ef-31225d89b658_500x.jpg?v=1711569326",
			"gender": "Unisex",
			"collection_handle": "ortir-apothecari",
			"price_ol": 30,
			"price_ol_ml": 4,
			"price_tf": 25,
			"price_tf_ml": 4,
			"concentration": "EdP"
		},
		"matcha-mist": {
			"title": "Brume Matcha",
			"brand": "Marie Jeanne",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Mists_Brume-matcha_shadow_500x.jpg?v=1738669327",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 60,
			"price_ol_ml": 100,
			"price_tf": 50,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"eau-de-the-gingembre": {
			"title": "Eau de thé & Gingembre",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eau_de_the_gingembre_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"jawa-cologne": {
			"title": "Jawa Cologne",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_jawa_500x.jpg?v=1754922475",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"exploration-kit": {
			"title": "Exploration Kit",
			"brand": "Commodity",
			"price": 38,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/commodity_kit_500x.jpg?v=1755167390",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 38,
			"price_ol_ml": 18,
			"price_tf": 31.67,
			"price_tf_ml": 18,
			"concentration": "EdP"
		},
		"firenze": {
			"title": "Firenze",
			"brand": "Ampersand",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/firenze_500x.jpg?v=1778351061",
			"gender": "Unisex",
			"collection_handle": "ampersand",
			"price_ol": 60,
			"price_ol_ml": 50,
			"price_tf": 50,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mister-bungalow": {
			"title": "Mister Bungalow",
			"brand": "Circle of Lim",
			"price": 70,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mister_bungalow_03010c03-9dfd-406f-8160-e9836a09f6ed_500x.jpg?v=1775823138",
			"gender": "Unisex",
			"collection_handle": "circle-of-lim",
			"price_ol": 70,
			"price_ol_ml": 30,
			"price_tf": 58.33,
			"price_tf_ml": 30,
			"concentration": "EdC"
		},
		"blueforest": {
			"title": "Blueforest",
			"brand": "Perroy",
			"price": 70,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/perroy_blueforest_500x.jpg?v=1781190511",
			"gender": "Unisex",
			"collection_handle": "perroy",
			"price_ol": 70,
			"price_ol_ml": 50,
			"price_tf": 58.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"monopteros": {
			"title": "Monopteros",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Foto_DER_DUFT_MONOPTEROS_Schatten_74d5be6e-e22b-4182-a819-c577107bbf21_500x.jpg?v=1625874166",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"black-oregano": {
			"title": "Black Oregano",
			"brand": "Atelier Materi",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_oregano_500x.jpg?v=1781694266",
			"gender": "Unisex",
			"collection_handle": "atelier-materi",
			"price_ol": 240,
			"price_ol_ml": 50,
			"price_tf": 200,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"rouge": {
			"title": "Rouge",
			"brand": "Comme Des Garcons",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/comme_rouge_500x.jpg?v=1773779864",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"boar": {
			"title": "Boar",
			"brand": "Wolf Brothers",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/boar_wolf-brothers_500x.jpg?v=1671451038",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"indian-grass": {
			"title": "Indian Grass",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/INDIAN_20GRASS-1006x698_a5a51507-cdbd-4a46-8a1c-4e9fbb575541_500x.jpg?v=1661530659",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"v-i-t-r-i-o-l": {
			"title": "V.I.T.R.I.O.L.",
			"brand": "Maqueda",
			"price": 290,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/maqueda_r_500x.jpg?v=1776442490",
			"gender": "Unisex",
			"collection_handle": "maqueda",
			"price_ol": 290,
			"price_ol_ml": 50,
			"price_tf": 241.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"iris-altesse": {
			"title": "Iris Altesse",
			"brand": "Danner & Flemming",
			"price": 280,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_altesse_500x.jpg?v=1781384722",
			"gender": "Unisex",
			"collection_handle": "danner-flemming",
			"price_ol": 280,
			"price_ol_ml": 50,
			"price_tf": 233.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"sand": {
			"title": "Sand",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SAND-50ML-MASTER_500x.jpg?v=1717843271",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"seychelles-breeze": {
			"title": "Seychelles Breeze",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/SEYCHELLES_20BREEZE-1006x698_e327e3fa-1246-4576-93c2-25060e9b85c2_500x.jpg?v=1661531038",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg191-neroli-ad-astra": {
			"title": "PG19.1 Neroli ad Astra",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/191_neroli_ad_astra_parfumerie_generale_500x.jpg?v=1589662166",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"in-this-world": {
			"title": "In This World",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_InThisWorld_500x.jpg?v=1764836604",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"osang": {
			"title": "Osang",
			"brand": "Mendittorosa",
			"price": 315,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Osang_1_1500px_wyszparowany_500x.jpg?v=1614678157",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 315,
			"price_ol_ml": 100,
			"price_tf": 262.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"myrrh-blood-drop": {
			"title": "No. 8 – Drop of Blood",
			"brand": "Binet-Papillon",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/binet-papillon-8_500x.jpg?v=1764007714",
			"gender": "Unisex",
			"collection_handle": "binet-papillon",
			"price_ol": 215,
			"price_ol_ml": 100,
			"price_tf": 179.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"skin-fetish": {
			"title": "Skin Fetish",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/skin_fetish_new_500x.jpg?v=1792512389",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"velvet-bold": {
			"title": "Velvet | Bold",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Velvet_500x.png?v=1740648640",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ambre-de-noir": {
			"title": "Ambre de Noir",
			"brand": "Maksim Perfume",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ambe_de_noir_500x.jpg?v=1793039606",
			"gender": "Unisex",
			"collection_handle": "maksim-perfume",
			"price_ol": 240,
			"price_ol_ml": 50,
			"price_tf": 200,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"cambodian-oud": {
			"title": "Cambodian Oud",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CAMBODIAN-OUD_500x.jpg?v=1632583742",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"high-to-lotus": {
			"title": "High to Lotus",
			"brand": "Space Fluid",
			"price": 290,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lotus_01_500x.jpg?v=1770030576",
			"gender": "Unisex",
			"collection_handle": "space-fluid",
			"price_ol": 290,
			"price_ol_ml": 50,
			"price_tf": 241.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rosa-umami-v2": {
			"title": "Rosa umami V2",
			"brand": "Zanini",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rosa_umami_500x.jpg?v=1789410210",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 305,
			"price_ol_ml": 30,
			"price_tf": 254.17,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"a-masquerade": {
			"title": "A Masquerade",
			"brand": "Plowden & Fallow",
			"price": 108,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pf_08_500x.jpg?v=1716723960",
			"gender": "Unisex",
			"collection_handle": "plowden-fallow",
			"price_ol": 108,
			"price_ol_ml": 100,
			"price_tf": 90,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"athanor": {
			"title": "Athanor",
			"brand": "Mendittorosa",
			"price": 430,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Mendittorosa-Athanor-01_500x.jpg?v=1625873603",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 430,
			"price_ol_ml": 100,
			"price_tf": 358.33,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"mathilde": {
			"title": "Mathilde",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mathilde_500x.jpg?v=1779881448",
			"gender": "Female",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"guava-sorbet": {
			"title": "Guava Sorbet",
			"brand": "Brandt",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/guava_sorbet_500x.jpg?v=1761236084",
			"gender": "Unisex",
			"collection_handle": "brandt",
			"price_ol": 185,
			"price_ol_ml": 100,
			"price_tf": 154.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"damask": {
			"title": "Damask",
			"brand": "Ormonde Jayne",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/damask-88ml-front_1020x1530_1_500x.jpg?v=1711455708",
			"gender": "Female",
			"collection_handle": "ormonde-jayne",
			"price_ol": 175,
			"price_ol_ml": 88,
			"price_tf": 145.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"asoral": {
			"title": "Asoral",
			"brand": "Mendittorosa",
			"price": 260,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/freedom-asoral-01_1_500x.jpg?v=1789568167",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 260,
			"price_ol_ml": 100,
			"price_tf": 216.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ensang-noir": {
			"title": "Ensang Noir",
			"brand": "L'Entropiste",
			"price": 260,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ensang_eccccf9d-0736-466f-ab16-c9856bb296a5_500x.jpg?v=1766576633",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 260,
			"price_ol_ml": 100,
			"price_tf": 216.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"juice-personal": {
			"title": "Juice | Personal",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Juice_500x.png?v=1740650774",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"koiame": {
			"title": "恋雨 Koiame (It's Raining Love)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/koiame-01_500x.jpg?v=1741339492",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"ziveli": {
			"title": "Živeli",
			"brand": "Boka",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/boka_ziveli_bb007d5c-9e42-44d8-aed1-d3c0500c3290_500x.jpg?v=1786786659",
			"gender": "Unisex",
			"collection_handle": "boka",
			"price_ol": 215,
			"price_ol_ml": 50,
			"price_tf": 179.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vettii": {
			"title": "Vettii",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vetti_500x.jpg?v=1764500554",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"andevalo": {
			"title": "Andevalo",
			"brand": "Nissaba",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/andevalo_500x.jpg?v=1788434336",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"closeup": {
			"title": "Close-Up",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/close_up_olfcative_studio_bloom_500x.jpg?v=1589660908",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"a-pas-de-loup": {
			"title": "À Pas De Loup (On Tiptoe)",
			"brand": "Milleseve",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/APDL-100ml---3_500x.jpg?v=1789384159",
			"gender": "Unisex",
			"collection_handle": "milleseve",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"japanese-whiskey": {
			"title": "Japanese Whiskey",
			"brand": "d'Annam",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/japanese_whiskey_500x.jpg?v=1747316222",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 295,
			"price_ol_ml": 150,
			"price_tf": 245.83,
			"price_tf_ml": 150,
			"concentration": "EdP"
		},
		"moko": {
			"title": "Moko",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-MOKO-PERFUME-50ml-white_29bde728-40a1-40ab-825c-adefd8c67f67_500x.jpg?v=1742991648",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"monsieur": {
			"title": "Monsieur",
			"brand": "Pierre Guillaume Black Collection",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Parfumerie_black_Monsieur_500x.png?v=1589662103",
			"gender": "Male",
			"collection_handle": "pierre-guillaume-black-collection",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg-111-indian-wood": {
			"title": "PG11.1 Indian Wood",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/pg11_1_indian_wood_pierre_guillaume_500x.jpg?v=1589662157",
			"gender": "Male",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dancing-light": {
			"title": "Dancing Light",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Dancing-Light-100-ML-bottle_500x.jpg?v=1654970114",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"hiraeth": {
			"title": "Hiraeth",
			"brand": "Ortir Apothecari",
			"price": 114,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ORTIRHIRAETH_500x.png?v=1711459620",
			"gender": "Unisex",
			"collection_handle": "ortir-apothecari",
			"price_ol": 114,
			"price_ol_ml": 50,
			"price_tf": 95,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"flower-frost-19-12": {
			"title": "Flower Frost 19.12",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1912_895ae71f-1e66-4931-a98a-787f58ad9c50_500x.jpg?v=1793130848",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"lacura": {
			"title": "Lacura",
			"brand": "Mendittorosa",
			"price": 200,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_Lacura_05_500x.jpg?v=1589662046",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 200,
			"price_ol_ml": 100,
			"price_tf": 166.67,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"chateau-de-velours": {
			"title": "Château de Velours",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/chateu_500x.jpg?v=1783068143",
			"gender": "",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"tokyo-nights": {
			"title": "Tokyo Nights | ナイト",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Tokyo-Nights_500x.jpg?v=1746626539",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"juice-balanced": {
			"title": "Juice | Balanced & Expressive",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Juice_99918fb3-bf67-42ed-8dd9-2af32f425966_500x.png?v=1740650789",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fraisi": {
			"title": "Fraisi",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fraisi_500x.jpg?v=1750076034",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"matcha-soft-serve": {
			"title": "Matcha Soft Serve | 抹茶ソフト",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Matcha-Soft-Serve_500x.jpg?v=1746627479",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"osmanthus-oolong": {
			"title": "Osmanthus & Oolong",
			"brand": "NuWilds",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oolong_500x.jpg?v=1788191620",
			"gender": "Unisex",
			"collection_handle": "nu-wilds",
			"price_ol": 80,
			"price_ol_ml": 30,
			"price_tf": 66.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"culot-the": {
			"title": "Culot The",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/culot_the_500x.jpg?v=1790357607",
			"gender": "",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tea-rock-n-roll": {
			"title": "Tea & Rock ‘n‘ Roll",
			"brand": "Voyages Imaginaires",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tea_999x1500_50197860-f8bb-4dbf-b654-41528ee640bd_500x.webp?v=1717745705",
			"gender": "Unisex",
			"collection_handle": "voyages-imaginaires",
			"price_ol": 175,
			"price_ol_ml": 75,
			"price_tf": 145.83,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"leaf-crush": {
			"title": "Leaf Crush",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/leaf_500x.jpg?v=1783068296",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"chaco": {
			"title": "Chaco",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/chaco_500x.jpg?v=1757078342",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"moonmilk": {
			"title": "Moonmilk",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/moon_500x.jpg?v=1764953808",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"konya-konya-kolonyasi": {
			"title": "Konya Kolonyasi",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/konya_500x.jpg?v=1781690585",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"liquid-incense": {
			"title": "Liquid Incense",
			"brand": "Maksim Perfume",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/liquid_500x.jpg?v=1793040801",
			"gender": "Unisex",
			"collection_handle": "maksim-perfume",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sunken-tobacco": {
			"title": "Sunken Tobacco",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sunken_tobacco_500x.jpg?v=1792010392",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"berbera": {
			"title": "Berbera",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/berbera_500x.jpg?v=1757077260",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"volcano": {
			"title": "Volcano",
			"brand": "CARNER",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/volcano_carner_500x.jpg?v=1589665551",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"woodoo": {
			"title": "Woodoo",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/woodoo_500x.jpg?v=1783328394",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bois-dascse": {
			"title": "Bois d’Ascese",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_BOIS_D_ASCESE_2019_500x.png?v=1604928347",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"anti-blues": {
			"title": "Anti-Blues",
			"brand": "Pierre Guillaume Black Collection",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/anti-blues-100_500x.jpg?v=1689022528",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-black-collection",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vanille-creme": {
			"title": "Vanille Creme",
			"brand": "Karzaz",
			"price": 69,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanille_cream_500x.jpg?v=1773068117",
			"gender": "Unisex",
			"collection_handle": "karzaz",
			"price_ol": 69,
			"price_ol_ml": 50,
			"price_tf": 57.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"bake": {
			"title": "Bake",
			"brand": "Akro",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Akro_100-ML-BAKE_500x.jpg?v=1756809437",
			"gender": "Unisex",
			"collection_handle": "akro",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"god-bless-cola": {
			"title": "God Bless Cola",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/god_bless_cola_500x.jpg?v=1790357365",
			"gender": "Female",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"play-green": {
			"title": "Play Green",
			"brand": "Comme Des Garcons",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/heart_02_e41f32d0-ef5f-4cca-87bd-83ff785a948b_500x.jpg?v=1773780011",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"you-or-someone-like-you": {
			"title": "You or Someone Like You",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/You_Or_Someone_Like_You_100_ml_1_946b1c29-12a6-444f-9b02-7c047eded2ab_500x.jpg?v=1625876471",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fig-lemonade": {
			"title": "Fig Lemonade",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fig_lemonade_73b129dd-8c1b-4caf-9eec-0bdd3adb9e3d_500x.jpg?v=1783184407",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"hakka-japanese-mint": {
			"title": "Hakka (Japanese Mint)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/hakka-01_500x.jpg?v=1741339802",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"provence": {
			"title": "Provence",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/provence_500x.jpg?v=1757078761",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"entre-ciel-et-mer": {
			"title": "Entre Ciel et Mer",
			"brand": "Pierre Guillaume Cruise/Croisiere",
			"price": 144,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/ENTRE_CIEL_ET_MER-100ML_500x.jpg?v=1589660953",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-cruise-croisiere",
			"price_ol": 144,
			"price_ol_ml": 100,
			"price_tf": 120,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"falaise-49-5-n": {
			"title": "Falaise 49.5°N (Cliff 49.5°N)",
			"brand": "Milleseve",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/FA-100ml---4_500x.jpg?v=1789385407",
			"gender": "Unisex",
			"collection_handle": "milleseve",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sous-le-pont-mirabeau": {
			"title": "Sous Le Pont Mirabeau",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SouslepontMirabeau100ml_5ac7f65b-8205-4d1f-81e1-70fd89082f81_500x.jpg?v=1684152518",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"beso-de-sal": {
			"title": "Beso de Sal",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/beso_de_sal_500x.jpg?v=1783354178",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"breathe-out-01-02": {
			"title": "Breathe Out 01.02",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_breath_500x.jpg?v=1770373220",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sydney-rock-pool": {
			"title": "Sydney Rock Pool",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/sydney_rockpool_arquiste_c1df3be0-2ab0-4f24-b272-33798ef6b37d_500x.jpg?v=1625876155",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rouge-karkade": {
			"title": "Rouge Karkade",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.RougeKarkade_Bottle_500x.png?v=1754905004",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"agarwood": {
			"title": "Agarwood",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/agrawod-01_4db3c9a9-fe68-4896-95ce-bd583f77bf0c_500x.jpg?v=1741339740",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"black-afgano": {
			"title": "Black Afgano",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_afgano_500x.jpg?v=1756323933",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"in-principio-v2": {
			"title": "In principio V2",
			"brand": "Zanini",
			"price": 255,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/in-principio_500x.jpg?v=1789410401",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 255,
			"price_ol_ml": 30,
			"price_tf": 212.5,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"musc-oli": {
			"title": "Musc Oli",
			"brand": "Caron",
			"price": 275,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/musc_oli_500x.jpg?v=1789849912",
			"gender": "Female",
			"collection_handle": "caron",
			"price_ol": 275,
			"price_ol_ml": 100,
			"price_tf": 229.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"rosi-rosa": {
			"title": "Rosi Rosa",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rosi_rosa_500x.jpg?v=1752667965",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"experimentum-crucis": {
			"title": "Experimentum Crucis",
			"brand": "Etat Libre d'Orange",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Experimentum_Crucis_flacon_100_ml_04b0f3a3-a95e-484c-b596-a8fbfb7c8e9c_500x.jpg?v=1625875926",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 240,
			"price_ol_ml": 100,
			"price_tf": 200,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"narcotic-v": {
			"title": "Narcotic V",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/narcoticv_500x.jpg?v=1756322874",
			"gender": "Female",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"rose-cardamome": {
			"title": "Rose Cardamome",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rose_cardamome_500x.jpg?v=1790695570",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"delirium": {
			"title": "Delirium",
			"brand": "Wesker",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Delirium-Product-Page-1800-1_500x.jpg?v=1747312820",
			"gender": "Unisex",
			"collection_handle": "wesker",
			"price_ol": 220,
			"price_ol_ml": 75,
			"price_tf": 183.33,
			"price_tf_ml": 75,
			"concentration": "Extrait de Parfum"
		},
		"fleur-de-rocaille": {
			"title": "Fleur de Rocaille",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fleurs_de_rocaille_500x.jpg?v=1789849155",
			"gender": "Female",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"suze": {
			"title": "Suze",
			"brand": "Boka",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/boka_suze_500x.jpg?v=1786786465",
			"gender": "Unisex",
			"collection_handle": "boka",
			"price_ol": 215,
			"price_ol_ml": 50,
			"price_tf": 179.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"jasmin-ylang": {
			"title": "Jasmin & Ylang",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/jasmin_ylang_500x.jpg?v=1731682971",
			"gender": "Female",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"fathom-v": {
			"title": "Fathom V",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_new_beaufort_fathom_5_bloom_500x.jpg?v=1623765447",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"or-du-srail": {
			"title": "Or du Sérail",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_OR_DU_SERAIL_2019_500x.jpg?v=1589662135",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"milk": {
			"title": "Milk | Personal",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Milk_45819a57-e3fc-46b8-8b01-faed8e357ad3_500x.png?v=1740651017",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg10-aomassa": {
			"title": "PG10 Aomassaï",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PG10_Aomassai_500x.jpg?v=1592593845",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"roissant-afe": {
			"title": "Сroissant Сafe",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/croissant_cafe_500x.jpg?v=1790357723",
			"gender": "",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"cacao-pimento": {
			"title": "Cacao Pimento",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cacao_pimento_8ff21320-e107-4b4c-8fa8-1254960a4598_500x.jpg?v=1790694364",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"deer": {
			"title": "Deer",
			"brand": "Wolf Brothers",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/deer_wolf-brothers_500x.jpg?v=1671450860",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sugar-addict": {
			"title": "SUGAR ADDICT",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SUGARADDICT_BOTTLE_500x.png?v=1710231628",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"23-59": {
			"title": "23:59",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_2359_500x.jpg?v=1788783112",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"pg04-musc-maori": {
			"title": "PG04 Musc Maori",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/pg04_musc_maori_500x.jpg?v=1589662150",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"boccanera": {
			"title": "Boccanera",
			"brand": "Orto Parisi",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orto_bocanera_500x.jpg?v=1758619581",
			"gender": "Unisex",
			"collection_handle": "orto-parisi",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"07-orris-cacao": {
			"title": "07 Orris+Cacao",
			"brand": "Raer",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orris_500x.jpg?v=1789414289",
			"gender": "Unisex",
			"collection_handle": "raer",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tabac-exquis": {
			"title": "Tabac Exquis",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tabac_exquis_500x.jpg?v=1789849442",
			"gender": "Unisex",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"mezcal-chocolat": {
			"title": "Mezcal Chocolat",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mezcal_chocolat_500x.jpg?v=1773068653",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"almond-suede": {
			"title": "Almond Suede",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/almond_suede_500x.gif?v=1728847398",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"flight-mode": {
			"title": "Flight Mode",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/flight_mode_500x.jpg?v=1737305704",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"orange-patina": {
			"title": "Orange Patina",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orange_500x.jpg?v=1783068174",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-tannat": {
			"title": "Iris Tannat",
			"brand": "Danner & Flemming",
			"price": 280,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_tannat_b2151c4c-4c3e-44fe-ac0b-93dcb46941d5_500x.jpg?v=1793647899",
			"gender": "Unisex",
			"collection_handle": "danner-flemming",
			"price_ol": 280,
			"price_ol_ml": 50,
			"price_tf": 233.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"malt-night-fighter": {
			"title": "No. 10 – Night Warrior",
			"brand": "Binet-Papillon",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/binet-papillon-10_892de856-3f0b-4282-9261-be6f19e48905_500x.jpg?v=1764008363",
			"gender": "Unisex",
			"collection_handle": "binet-papillon",
			"price_ol": 215,
			"price_ol_ml": 100,
			"price_tf": 179.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kar-wai": {
			"title": "Kar-Wai",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/9eba791468f6eb4f332b4cf6c8a2b1c0d4011a9a_kar-wai-no-background-reduced_500x.jpg?v=1632307163",
			"gender": "Unisex",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"alyazia": {
			"title": "Alyazia",
			"brand": "Maysson",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/alyazia_500x.jpg?v=1789046156",
			"gender": "Unisex",
			"collection_handle": "maysson",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"almaz": {
			"title": "Almaz",
			"brand": "Kajal",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/almaz_27124158-ddbd-4c08-a6c1-0cc8603e5a8f_500x.jpg?v=1765389857",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"afrique-en-fleurs": {
			"title": "Afrique en Fleurs",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.Reserve_en_Afrique_Afrique_en_Fleurs_100ml_Bottle_500x.png?v=1754905382",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"baise-moi-on-the-dancefloor": {
			"title": "Baise Moi On The Dancefloor",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/baise_moi_500x.jpg?v=1768128001",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-cendr": {
			"title": "Iris Cendré",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/iris_cendre_naomi_goodsir_500x.jpg?v=1589662020",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-coriandre": {
			"title": "Iris Coriandre",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_coriandre_500x.jpg?v=1790695633",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pour-un-homme": {
			"title": "Pour Un Homme de Caron",
			"brand": "Caron",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pour_un_homme_01_500x.jpg?v=1792507504",
			"gender": "Male",
			"collection_handle": "caron",
			"price_ol": 125,
			"price_ol_ml": 125,
			"price_tf": 104.17,
			"price_tf_ml": 125,
			"concentration": "EdT"
		},
		"brume-dete": {
			"title": "Brume d'été",
			"brand": "Marie Jeanne",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Mists_Brume-d-ete_shadow_500x.jpg?v=1738671197",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 60,
			"price_ol_ml": 100,
			"price_tf": 50,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"forbidden-rouge": {
			"title": "Forbidden Rouge",
			"brand": "Mystikum",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/forbidden_a8dc0b7e-4539-49f8-84a0-61e6c58ecfd7_500x.jpg?v=1791808008",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 210,
			"price_ol_ml": 100,
			"price_tf": 175,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cape-wrath": {
			"title": "Cape Wrath",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cape_wrath_500x.jpg?v=1728510293",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"afternoon-tea": {
			"title": "Afternoon Tea",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tea_500x.jpg?v=1780922426",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"montabaco": {
			"title": "Montabaco",
			"brand": "Ormonde Jayne",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/montabaco-intensivo-88ml-flat_1020x1530_1_500x.jpg?v=1711456746",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 295,
			"price_ol_ml": 88,
			"price_tf": 245.83,
			"price_tf_ml": 88,
			"concentration": "Extrait de Parfum"
		},
		"mulberry-fig": {
			"title": "Mulberry Fig",
			"brand": "Floris",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/MulberryFigEDPBottleWEB_500x.jpg?v=1678814657",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"monkeyflower": {
			"title": "Monkeyflower",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/monkey_629c066c-c1c3-4911-a0bc-d67074e30483_500x.jpg?v=1764953685",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"arashiyama": {
			"title": "Arashiyama | 嵐山",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Arashiyama_500x.jpg?v=1746626744",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"patchouli-labdanum": {
			"title": "Patchouli & Labdanum",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pathouli_labdanum_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"shikoku-cologne": {
			"title": "Shikoku Cologne | 四国ケルン",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_shikoku_500x.jpg?v=1754923384",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"naju-spring": {
			"title": "Naju Spring",
			"brand": "Pictura Fragrans",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/NajuSpringPicturaFragrans_500x.jpg?v=1775728469",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 150,
			"price_ol_ml": 50,
			"price_tf": 125,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"195-a-c": {
			"title": "195 A.C.",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/195-AC_500x.jpg?v=1657827237",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"baraonda": {
			"title": "Baraonda",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/baraonda_500x.jpg?v=1756323552",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"sacreste": {
			"title": "Sacreste",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sacreste_30537c77-f38c-4df0-8eda-aa4948cefa75_500x.jpg?v=1708450496",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"via-dell-incenso": {
			"title": "Via dell’Incenso",
			"brand": "Farmacia SS. Annunziata dal 1561",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/via_dellincenso_100ml_500x.jpg?v=1772737720",
			"gender": "Unisex",
			"collection_handle": "farmacia-ss-annunziata-dal-1561",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"arabesque": {
			"title": "Arabesque",
			"brand": "Ormonde Jayne",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/arabesque-88ml_1020x1530_5b8da67a-e434-4fe6-bd40-5aaba43b4088_500x.jpg?v=1696788803",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 195,
			"price_ol_ml": 88,
			"price_tf": 162.5,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"ruby-wood": {
			"title": "Ruby Wood",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ruby_wood_28492c81-4baa-40fc-b269-7a1e03e750f7_500x.jpg?v=1780922026",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"parfum-sacre": {
			"title": "Parfum Sacré",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sacre_500x.jpg?v=1789848682",
			"gender": "Unisex",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg0-4-le-musc-la-peau": {
			"title": "Le Musc & La Peau Extrait",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Pierre-Guillaume-Paris-Le-Musc-et-La-Peau-Bouteille-Extrait-de-Parfum-100ml_1_500x.jpg?v=1712072353",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"blond-redhead-16-11": {
			"title": "Blond Redhead 16.11",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_blond_500x.jpg?v=1770373615",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mansa-musa": {
			"title": "Mansa Musa",
			"brand": "SweDoft",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/swedoft-mans-musa_500x.jpg?v=1638376966",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"muguet-fleuri": {
			"title": "Muguet Fleuri",
			"brand": "Oriza L. Legrand",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/muguet_500x.jpg?v=1788436361",
			"gender": "Female",
			"collection_handle": "oriza-l-legrand",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bamboo": {
			"title": "Bamboo",
			"brand": "NuWilds",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bamboo_bfdb0893-c20f-40db-8a65-4980cc59dba3_500x.jpg?v=1788191309",
			"gender": "Unisex",
			"collection_handle": "nu-wilds",
			"price_ol": 80,
			"price_ol_ml": 30,
			"price_tf": 66.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"neon-night": {
			"title": "Neon Night",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neon_night_500x.jpg?v=1761555011",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"juli": {
			"title": "Juli",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_juli_500x.jpg?v=1788783233",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"shaft-of-light": {
			"title": "Shaft of Light (Sunbeams)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/shaftoflight-01_500x.jpg?v=1741339345",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"32-c": {
			"title": "32°C",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_32_500x.jpg?v=1788784252",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"fiery-fig-24-05": {
			"title": "Fiery Fig 24.05",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_fiery_500x.jpg?v=1770373380",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"kolada": {
			"title": "Kolada",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kolada_500x.jpg?v=1765390870",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"santorini": {
			"title": "Santorini",
			"brand": "Ampersand",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/santorini_500x.jpg?v=1778351025",
			"gender": "Unisex",
			"collection_handle": "ampersand",
			"price_ol": 60,
			"price_ol_ml": 50,
			"price_tf": 50,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"in-love-with-the-cocos": {
			"title": "In Love With the Cocos",
			"brand": "Fugazzi",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/100_Fugazzi_productfotos2022_coco_500x.png?v=1745850603",
			"gender": "Unisex",
			"collection_handle": "fugazzi",
			"price_ol": 205,
			"price_ol_ml": 100,
			"price_tf": 170.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"this-is-not-a-blue-bottle-1-4": {
			"title": "This is Not a Blue Bottle 1/.4 – Yin",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_04_500x.jpg?v=1757945935",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"drunk-lovers": {
			"title": "DRUNK LOVERS",
			"brand": "BORNTOSTANDOUT",
			"price": 262,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/borntostandout_drunk_lovers_500x.jpg?v=1680168958",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 262,
			"price_ol_ml": 100,
			"price_tf": 218.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"patchouli-cannelle": {
			"title": "Patchouli Cannelle",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/patchouli_cannele_500x.jpg?v=1790694990",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vanille-mafonja": {
			"title": "Vanille Mafonja",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.Reserve_en_Afrique_VanilleMafonja_100ml_Bottle_500x.png?v=1754904945",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"wolf-extrait": {
			"title": "Wolf (Extrait)",
			"brand": "Wolf Brothers",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/wold_01_500x.jpg?v=1737390854",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"lu-bu": {
			"title": "Lü Bu",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lubu_500x.jpg?v=1781697322",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"milk-bold": {
			"title": "Milk | Bold",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Milk_a9553e1d-50b3-4c02-8ebf-e07057b7850f_500x.png?v=1740650988",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rasputin": {
			"title": "Rasputin",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/RASPUTIN_500x.jpg?v=1632583409",
			"gender": "Male",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"veles": {
			"title": "Veles",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-VELES-PERFUME-50ml_932cf17a-af19-4d67-baa6-6dd1018bb352_500x.jpg?v=1742991780",
			"gender": "",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"fat-cola": {
			"title": "Fat Cola",
			"brand": "Ourte",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fat_cola_205f209d-9a65-4cc8-8626-ea0f78e9d2a3_500x.jpg?v=1793559383",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"ladano": {
			"title": "Ladano",
			"brand": "D:SOL MMXVI",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ladano_500x.jpg?v=1769108500",
			"gender": "Unisex",
			"collection_handle": "d-sol",
			"price_ol": 140,
			"price_ol_ml": 75,
			"price_tf": 116.67,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"meek-passion": {
			"title": "Meek Passion",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MEEK_PASSION_500x.jpg?v=1729348188",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"aurantium-jasmine": {
			"title": "Aurantium Jasmine",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/aurantiumjasmine-01_500x.jpg?v=1741339104",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"the-nirvana": {
			"title": "The Nirvana",
			"brand": "Param Sara",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/The-Nirvana_500x.jpg?v=1778077865",
			"gender": "Unisex",
			"collection_handle": "param-sara",
			"price_ol": 140,
			"price_ol_ml": 50,
			"price_tf": 116.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"awake": {
			"title": "Awake",
			"brand": "Akro",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Akro_100-ML-Awake_500x.jpg?v=1756809370",
			"gender": "Unisex",
			"collection_handle": "akro",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"grande-lle": {
			"title": "Grande Ile",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/grandeile_500x.jpg?v=1757077764",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"1805-tonnerre": {
			"title": "Tonnerre",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BeauFort_London_1805_Tonnerre_Bloom_500x.jpg?v=1623765370",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pear-pavlova": {
			"title": "Pear Pavlova",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pear_pavlova_500x.jpg?v=1773068554",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pink-sweats": {
			"title": "Pink Sweats",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pink_sweats_500x.jpg?v=1783068572",
			"gender": "Female",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"figo": {
			"title": "Figo",
			"brand": "Granado",
			"price": 65,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/granado_figo_608a1cf1-c948-4858-999c-b18b53950242_500x.jpg?v=1753451126",
			"gender": "Unisex",
			"collection_handle": "granado",
			"price_ol": 65,
			"price_ol_ml": 100,
			"price_tf": 54.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rose-de-taif": {
			"title": "Rose de Taif",
			"brand": "Perris Monte Carlo",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Black-Rose-de-Taif_100_ml_8c8c6a2d-afcb-44f3-82e6-21fc28998642_500x.jpg?v=1625876109",
			"gender": "Unisex",
			"collection_handle": "perris-monte-carlo",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"arcadia-1": {
			"title": "Arcadia",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/arcadia_74a68793-b18a-42ed-a0ed-e320d8ca1c0b_500x.jpg?v=1781438125",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"concrete": {
			"title": "Concrete",
			"brand": "Comme Des Garcons",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/concrete_500x.jpg?v=1769329773",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 160,
			"price_ol_ml": 80,
			"price_tf": 133.33,
			"price_tf_ml": 80,
			"concentration": "EdP"
		},
		"oud-cannelle": {
			"title": "Oud Cannelle",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oud_cannele_500x.jpg?v=1790694720",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"tears-on-mystic-sands": {
			"title": "Tears On Mystic Sands",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_sands_500x.jpg?v=1776444081",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fantomas": {
			"title": "Fantomas",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fanromas_500x.jpg?v=1756323244",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"scrtions-magnifiques": {
			"title": "Secretions Magnifiques",
			"brand": "Etat Libre d'Orange",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/SECRETIONS-MAGNIFIQUES_c927c398-d206-42ff-8753-228ac755a81e_500x.jpg?v=1625876131",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"solar-wind": {
			"title": "Solar Wind",
			"brand": "Pictura Fragrans",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SolarWindPicturaFragrans_500x.jpg?v=1775728618",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 150,
			"price_ol_ml": 50,
			"price_tf": 125,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"comme-des-garcons-2": {
			"title": "Comme des Garcons 2",
			"brand": "Comme Des Garcons",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/two_500x.jpg?v=1769329950",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"mistpouffer": {
			"title": "Mistpouffer",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mist_2f0023df-1444-469f-8d90-0ee31e526ce6_500x.jpg?v=1764953526",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"cashmere-beige": {
			"title": "Cashmere Beige",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CASHMERE_BEIGE-2_500x.jpg?v=1589660895",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"dawn-whispers": {
			"title": "Dawn Whispers",
			"brand": "L'Entropiste",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dawn_500x.jpg?v=1766576329",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 185,
			"price_ol_ml": 50,
			"price_tf": 154.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sirenide": {
			"title": "Sirenide",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Sirenide_18206527-e2d7-4982-81c0-088e1839b75a_500x.jpg?v=1760544266",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"princess-of-china": {
			"title": "Princess of China | 中國公主",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bc-princess-china_500x.jpg?v=1762937954",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"01-nagarmotha": {
			"title": "01 Nagarmotha",
			"brand": "Raer",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/nagarmotha_500x.jpg?v=1789414597",
			"gender": "Unisex",
			"collection_handle": "raer",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tears": {
			"title": "Tears",
			"brand": "Comme Des Garcons",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tears_8226a79b-1acc-4a50-94ce-ab85bc92ec4c_500x.jpg?v=1789757776",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 135,
			"price_ol_ml": 50,
			"price_tf": 112.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"ayiti": {
			"title": "Ayiti",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ayiti_500x.jpg?v=1750076414",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"mango-sticky-rice": {
			"title": "Mango Sticky Rice",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mango_sticky_500x.jpg?v=1774364519",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"panorama": {
			"title": "Panorama",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Olfactive_studio_Panorama_500x.jpg?v=1589662143",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fig-porn": {
			"title": "FIG PORN",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/FIGPORN_BOTTLE_500x.png?v=1710232567",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"josephine": {
			"title": "Joséphine",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/josephine_500x.jpg?v=1779881662",
			"gender": "Unisex",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"lances": {
			"title": "Lances",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lances_500x.jpg?v=1781437392",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"fawaki-fawaki": {
			"title": "Fawaki Fawaki",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fawaki_500x.jpg?v=1750076161",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"2025-bestsellers-for-her": {
			"title": "2025 Bestsellers | For Her",
			"brand": "Bloom Sample Packs",
			"price": 49,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bestsellers_2025_for_her_500x.jpg?v=1765740616",
			"gender": "Female",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 49,
			"price_ol_ml": 9,
			"price_tf": 40.83,
			"price_tf_ml": 9,
			"concentration": "9"
		},
		"j-scent-discovery-set": {
			"title": "J-Scent Discovery Set",
			"brand": "J-Scent",
			"price": 40,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/j-scent-discovery-set_angle_30f4af3e-a50a-4441-960a-774f28462eef_500x.jpg?v=1775226811",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 40,
			"price_ol_ml": 6,
			"price_tf": 33.33,
			"price_tf_ml": 6,
			"concentration": "EdP"
		},
		"series-3-incense-avignon": {
			"title": "Series 3: Incense | Avignon",
			"brand": "Comme Des Garcons",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/avignon_500x.jpg?v=1769331129",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 90,
			"price_ol_ml": 50,
			"price_tf": 75,
			"price_tf_ml": 50,
			"concentration": "EdT"
		},
		"misty-cypress-ash": {
			"title": "Misty Cypress Ash 青岚柏烬",
			"brand": "Zhiwuzhi 挚物志",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/zhiwuzhi_ash_500x.jpg?v=1785434148",
			"gender": "",
			"collection_handle": "zhiwuzhi",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"paper-soap": {
			"title": "Soap Paper",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/papersoap-01_500x.jpg?v=1741339437",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg31-tonka-bodycon": {
			"title": "PG31 Tonka Bodykon",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/tonkabodykon_500x.jpg?v=1668777890",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bubble-tea": {
			"title": "Bubble Tea",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Bubble_Tea_500x.jpg?v=1784204596",
			"gender": "Female",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"gratia-aestiva": {
			"title": "Gratia Aestiva",
			"brand": "Aura Eximia",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_efd3f13da2cb4c4a92318998e826c52d_mv2_1_500x.png?v=1785402710",
			"gender": "Female",
			"collection_handle": "aura-eximia",
			"price_ol": 205,
			"price_ol_ml": 50,
			"price_tf": 170.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"roasted-green-tea": {
			"title": "ほうじ茶 Hojicha (Roasted Green Tea)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/roastedgreentea-01_500x.jpg?v=1741340199",
			"gender": "",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"apotheose": {
			"title": "Apothéose",
			"brand": "Oriza L. Legrand",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/apotheose_500x.jpg?v=1788435151",
			"gender": "",
			"collection_handle": "oriza-l-legrand",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kajal-i": {
			"title": "Kajal I",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kajal_1_a04718e6-7b08-42e3-ae42-47e3fbdd6aec_500x.jpg?v=1786565608",
			"gender": "Male",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"paradoxial": {
			"title": "Paradoxical",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PARADOXICAL-1006x698_998e89a2-942c-411a-9b46-91fe83b416ef_500x.jpg?v=1661531302",
			"gender": "Male",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dark-drunk-discovery-kit": {
			"title": "DARK & DRUNK DISCOVERY KIT",
			"brand": "BORNTOSTANDOUT",
			"price": 61,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dark_drunk_500x.jpg?v=1706518116",
			"gender": "",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 61,
			"price_ol_ml": 8,
			"price_tf": 50.83,
			"price_tf_ml": 8,
			"concentration": "EdP"
		},
		"oddity-discovery-set": {
			"title": "Oddity discovery set",
			"brand": "Oddity",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Oddity_set_500x.jpg?v=1755168359",
			"gender": "Unisex",
			"collection_handle": "oddity",
			"price_ol": 60,
			"price_ol_ml": 4,
			"price_tf": 50,
			"price_tf_ml": 4,
			"concentration": "EdP"
		},
		"sample-set-1": {
			"title": "Sample set",
			"brand": "Stora Skuggan",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sample_500x.jpg?v=1764954233",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 60,
			"price_ol_ml": 9,
			"price_tf": 50,
			"price_tf_ml": 9,
			"concentration": "EdP"
		},
		"iris-pallida": {
			"title": "Iris Pallida",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Iris-Pallidai_shadow_500x.jpg?v=1738670754",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"carine": {
			"title": "Carine",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/carine_1cfce458-6e61-4626-9e9a-b09793b6d178_500x.jpg?v=1761229136",
			"gender": "Unisex",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"luca-turin-loves-sample-pack": {
			"title": "Luca Turin Loves Sample Pack (2020 Update)",
			"brand": "Bloom Sample Packs",
			"price": 45,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/table_08_05_19_500x.jpg?v=1589662067",
			"gender": "Unisex",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 45,
			"price_ol_ml": 8,
			"price_tf": 37.5,
			"price_tf_ml": 8,
			"concentration": "8"
		},
		"marrakech-cologne": {
			"title": "Marrakech Cologne",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_marrakech_500x.jpg?v=1754916621",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"come-hell-or-high-water-discovery-set": {
			"title": "Come Hell or High Water discovery set",
			"brand": "Beaufort",
			"price": 45,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/BFL_Come_Hell_Dicso_aa1070db-5bff-4320-a536-23bf778c7143_500x.jpg?v=1714511330",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 45,
			"price_ol_ml": 5,
			"price_tf": 37.5,
			"price_tf_ml": 5,
			"concentration": "EdP"
		},
		"iris-trousseau": {
			"title": "Iris Trousseau",
			"brand": "Danner & Flemming",
			"price": 280,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_trousseau_500x.jpg?v=1781384517",
			"gender": "Unisex",
			"collection_handle": "danner-flemming",
			"price_ol": 280,
			"price_ol_ml": 50,
			"price_tf": 233.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vanille-des-afriques": {
			"title": "Vanille des Afriques",
			"brand": "Ormonde Jayne",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ormonde_jayne_vanille_8bf1eebd-e2fd-4105-86d1-dcc91787b550_500x.jpg?v=1736515254",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 295,
			"price_ol_ml": 88,
			"price_tf": 245.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"dirty-rice": {
			"title": "DIRTY RICE",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/DIRTYRICE_BOTTLE_500x.png?v=1710231978",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"book-personal": {
			"title": "Book | Personal",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Book_b88ab5fe-1cd7-40c4-867e-c7da9f112031_500x.png?v=1740649756",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sombra-24": {
			"title": "Sombra",
			"brand": "D:SOL MMXVI",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sombra_500x.jpg?v=1769107807",
			"gender": "Unisex",
			"collection_handle": "d-sol",
			"price_ol": 170,
			"price_ol_ml": 75,
			"price_tf": 141.67,
			"price_tf_ml": 75,
			"concentration": "Extrait de Parfum"
		},
		"no-89": {
			"title": "No. 89",
			"brand": "Floris",
			"price": 100,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/floris_89_500x.jpg?v=1787327688",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 100,
			"price_ol_ml": 100,
			"price_tf": 83.33,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"soap-bubble": {
			"title": "Soap Bubble",
			"brand": "Ylem",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/soap_bubble_500x.jpg?v=1777037619",
			"gender": "",
			"collection_handle": "ylem",
			"price_ol": 190,
			"price_ol_ml": 50,
			"price_tf": 158.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"the-grudge": {
			"title": "The Grudge",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/grudge_500x.jpg?v=1728510494",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"cologne-d-agar": {
			"title": "Cologne d’Agar",
			"brand": "Maksim Perfume",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cologne_dagar_500x.jpg?v=1793039477",
			"gender": "Unisex",
			"collection_handle": "maksim-perfume",
			"price_ol": 150,
			"price_ol_ml": 50,
			"price_tf": 125,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"h-o": {
			"title": "H₂O³",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_h203_500x.jpg?v=1788784430",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"mad": {
			"title": "Mad",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MAD-50ML-MASTER_500x.jpg?v=1717843533",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"elise": {
			"title": "Élise",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/elise_500x.jpg?v=1779881308",
			"gender": "Unisex",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"nettuno": {
			"title": "Nettuno",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Nettuno__talismans_100_ml_bloom_perfumery_500x.jpg?v=1589662118",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"mainstream": {
			"title": "Mainstream",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mainstream_9e0ca46e-6892-4cee-a1bd-a004fc21fb58_500x.jpg?v=1749156795",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"marcel": {
			"title": "Marcel",
			"brand": "Marie Jeanne",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/marcell_500x.jpg?v=1636650775",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"gold-balanced": {
			"title": "Gold | Balanced & Expressive",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/GOld_b68dbf50-e636-4905-9bec-9ec7ac9a6b43_500x.png?v=1740650427",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"terram": {
			"title": "Terram",
			"brand": "D:SOL MMXVI",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/terram_500x.jpg?v=1769108220",
			"gender": "Unisex",
			"collection_handle": "d-sol",
			"price_ol": 140,
			"price_ol_ml": 75,
			"price_tf": 116.67,
			"price_tf_ml": 75,
			"concentration": "EdP"
		}
	},
	"collections": {
		"cosy-comfort": {
			"title": "Cosy Comfort",
			"subtitle": "Warm, soft, enveloping fragrances",
			"category": "By Mood",
			"description": "Wrap yourself in fragrances that feel like your softest sweater, your favorite dessert, and a quiet moment made just for you.",
			"products": [
				{
					"handle": "vanille-glace",
					"description": "Vanille Glacé is ideal if you want a vanilla that feels truly comforting rather than flatly sweet; the espresso, madeleine and sandalwood make it smell like a luxe café treat with a polished, cosy finish."
				},
				{
					"handle": "ruby",
					"description": "Ruby suits you if you like your comfort scents with a brighter, more playful glow: cherry, berries and pineapple make it juicy and cheerful, while cream, amber and musk keep it warm and indulgent."
				},
				{
					"handle": "vanhera",
					"description": "Vanhera is a great pick if you love vanilla but want a little edge; the spices and woody base give it a darker, more textured coziness that feels sophisticated and self-assured."
				},
				{
					"handle": "happy-nuts",
					"description": "NUTS is for the days you want something generously creamy and delicious, with pistachio, sesame and caramel giving a roasted, nutty richness that feels both playful and deeply satisfying."
				},
				{
					"handle": "sadonaso",
					"description": "Sadonaso works beautifully if you want comfort with a sensual, intimate twist; coffee, tobacco and tonka make it warm and enveloping, while the sandalwood keeps it smooth and quietly provocative."
				},
				{
					"handle": "nightfall-in-the-city-of-kings",
					"description": "Nightfall in the City of Kings is a lovely choice if you enjoy soft, old-world elegance; its peachy spice and velvety woods create a cosy skin scent with a romantic, twilight mood."
				},
				{
					"handle": "dark-honey",
					"description": "Dark Honey is for when you want your comfort fragrance to feel decadent and a little wild; the boozy fruit, dark vanilla and animalic honey make it lush, warm and unapologetically rich."
				},
				{
					"handle": "mooncake",
					"description": "Mooncake is a tender, nostalgic option if you love edible scents with a gentle sweetness; it captures the cosy pleasure of cake, honey and vanilla in a way that feels intimate and heartfelt."
				},
				{
					"handle": "nebula-viridis",
					"description": "Nebula Viridis suits you if you prefer your warmth with a brighter, more luminous structure; the citrus, saffron and amber create a golden, resinous softness that feels quietly luxurious."
				}
			]
		},
		"fresh-escape": {
			"title": "Fresh Escape",
			"subtitle": "Clean, airy, holiday-feeling compositions",
			"category": "By Mood",
			"description": "Treat yourself to a breath of holiday air with scents that feel crisp, radiant, and effortlessly uplifting.",
			"products": [
				{
					"handle": "masa",
					"description": "Masa is for the self-gift that feels polished but easy: grapefruit, bergamot, and ginger sparkle at first, then settle into a warm amber-vanilla glow that still feels fresh."
				},
				{
					"handle": "resonant",
					"description": "Resonant suits you if you love a cooler, more modern escape—its yuzu, ginger, and metallic shimmer feel bracing and clean, with marine depth that lingers elegantly."
				},
				{
					"handle": "sal-y-limon",
					"description": "Sal y Limón is the brightest pick for when you want instant sunshine on your skin; fizzy citrus and sea salt make it feel like a carefree Mediterranean getaway in a bottle."
				},
				{
					"handle": "neroli-petitgrain",
					"description": "Neroli & Petitgrain is a lovely choice if you want something soft, familiar, and low-key luxurious—airy citrus and orange blossom give it that freshly washed comfort you can wear every day."
				},
				{
					"handle": "thirsty",
					"description": "Thirsty feels like the reward after a swim: green apple, bamboo, and fig give it a crisp, restorative lift, while the woody drydown keeps it smooth and wearable."
				},
				{
					"handle": "portamarina",
					"description": "Portamarina is ideal if you’re drawn to quiet refinement, with salty citrus, sage, and iris creating a serene, architectural freshness that feels composed rather than loud."
				},
				{
					"handle": "a-grove-by-the-sea",
					"description": "A Grove By The Sea is for the person who likes their freshness textured and natural—fig leaves, rosemary, sea salt, and olive nuances make it feel sunlit, green, and a little savory."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"description": "PG16 Jardins de Kérylos offers a softer, more elegant escape, where green fig and white peach rest over woods and musk for a tender, shaded Mediterranean feel."
				},
				{
					"handle": "playground-1",
					"description": "Playground is the easy-going holiday scent for anyone who wants airiness with a soft finish—cold marine notes, melon, and musk make it feel clean, breezy, and quietly addictive."
				}
			]
		},
		"dark-smoky": {
			"title": "Dark & Smoky",
			"subtitle": "Smoke, leather, incense, evening character",
			"category": "By Mood",
			"description": "For the nights you want to wear your own mystery, these smoky leathers and incense-dark scents feel like a private ritual just for you.",
			"products": [
				{
					"handle": "moonlight-ceremony",
					"description": "If you want a true after-dark signature, Moonlight Ceremony feels like slipping into a velvet coat by firelight: birch tar, smoked tea and incense make it intimate, while amber keeps it inviting."
				},
				{
					"handle": "black",
					"description": "Black is perfect when you want something raw and effortlessly cool, with peppery smoke and leather that feels more lived-in than polished—like your favourite jacket with a story."
				},
				{
					"handle": "oud-imperial-extrait",
					"description": "Oud Imperial Extrait suits you if you love depth without harshness; its smoky oud and birch tar are rich and dramatic, but the ambery roundness makes it feel indulgent rather than severe."
				},
				{
					"handle": "vi-et-armis",
					"description": "Vi et Armis is for the evenings when you want your scent to make a statement: bitter tea, pepper and incense create a brooding trail that feels intellectual, daring and slightly untamed."
				},
				{
					"handle": "corpus-equus",
					"description": "Corpus Equus works beautifully if you’re drawn to bold, tactile leathers; it has a fiery, animalic energy that feels powerful and sensual, like wearing confidence as fragrance."
				},
				{
					"handle": "honey-and-tar",
					"description": "Honey and Tar is a great choice if you want smoke with comfort, because the honey and vanilla soften the tarry leather into something warm, plush and quietly addictive."
				},
				{
					"handle": "beso-de-fuego",
					"description": "Beso de Fuego gives you a darker, spicier mood without going fully heavy, with frankincense and pepper over woody smoke that feels elegant, nocturnal and a little dangerous."
				},
				{
					"handle": "cruelle",
					"description": "Cruelle is ideal if you like your dark scents with a twist of fruit and velvet; blackberry, violet and tobacco make it lush and seductive, while the rubbery edge keeps it intriguing."
				},
				{
					"handle": "cuoium",
					"description": "Cuoium is for the self-purchaser who wants uncompromising leather: smoky, green and rugged, it has a fierce, tactile presence that feels both primal and beautifully self-assured."
				}
			]
		},
		"soft-skin-scents": {
			"title": "Soft Skin Scents",
			"subtitle": "Musks, close-to-skin, delicate fragrances",
			"category": "By Mood",
			"description": "For the days you want your fragrance to feel like a secret only your skin knows, these soft musks wrap you in quiet, intimate comfort.",
			"products": [
				{
					"handle": "cashmere-100",
					"description": "Cashmere 100 feels like a luxe knit against bare skin: the orris, white musk and sandalwood give it that polished softness, while vanilla and humid wood keep it gently cocooning. If you want to smell composed but still tenderly close, this is a beautiful self-treat."
				},
				{
					"handle": "white-rice",
					"description": "White Rice is a comforting skin scent with a subtle savory twist, like warm rice steamed with pandan and softened by jasmine. It’s ideal if you like fragrances that feel clean, edible and quietly personal rather than obviously perfumed."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"description": "PG4.1 Le Musc & La Peau is for the days you want pure skin intimacy, with seven musks creating a sheer, powdery halo that feels almost second-skin. The touch of tonka, cedar and vanilla gives it just enough shape to wear alone or layer under your favorites."
				},
				{
					"handle": "the-ghost-in-the-shell",
					"description": "The Ghost In The Shell suits you if you love a fresh, modern scent that still feels soft and wearable, with metallic yuzu and rain-bright florals settling into a milky skin accord. It has a cool, futuristic edge that never loses its intimacy."
				},
				{
					"handle": "les-cahiers-secrets",
					"description": "Les Cahiers Secrets feels like a private ritual: powdery orris and heliotrope are made more sensual by cumin and musk, so the scent moves between delicate cosmetics and warm skin. It’s a lovely choice if you want something quietly provocative for yourself."
				},
				{
					"handle": "merina",
					"description": "Merina is a refined comfort scent, with milky almond and fennel giving a creamy, softly aromatic opening before jasmine and iris turn it into a smooth skin veil. If you want something elegant, soothing and a little unusual, this is a standout."
				},
				{
					"handle": "yawahada",
					"description": "Yawahada is an easygoing soft-skin perfume with a gentle sweetness from pear and rice powder, then sandalwood, amber and musk adding warmth. It’s a great pick if you want a fragrance that feels naturally intimate, like your own skin but softer."
				},
				{
					"handle": "blanc-sada",
					"description": "Blanc Sada is perfect if you love a clean, powdery scent with texture: milky rice and iris feel silky, while straw and tatami add a dry, woven realism. It’s understated, calming and quietly sensual, like a fresh white shirt on warm skin."
				},
				{
					"handle": "intime-extime",
					"description": "Intime-Extime balances softness and depth beautifully, with iris, incense and vanilla floating over a milk accord and woody base. Choose it if you want a skin scent that feels polished and serene, but still has a little smoky intrigue."
				}
			]
		},
		"sweet-but-sophisticated": {
			"title": "Sweet but Sophisticated",
			"subtitle": "Gourmand fragrances with depth and complexity",
			"category": "By Mood",
			"description": "Treat yourself to gourmands that feel indulgent and polished—sweet enough to delight, with enough depth to keep you coming back for another wear.",
			"products": [
				{
					"handle": "vanille-glace",
					"description": "If you want a vanilla that feels rich rather than simple, Vanille Glacé is a beautiful self-indulgence: brown sugar, espresso and sandalwood give it a grown-up, cozy finish."
				},
				{
					"handle": "jany",
					"description": "Jany is for the days you want comfort with a little luxury; its apple tart heart, buttery pastry and caramelized depth make it feel like a nostalgic treat with elegance."
				},
				{
					"handle": "happy-nuts",
					"description": "NUTS is the most playful kind of splurge: roasted pistachio, sesame and caramel feel generous and decadent, while guaiac wood keeps it smoky and stylish."
				},
				{
					"handle": "vietnamese-coffee",
					"description": "Vietnamese Coffee suits you if you love your sweetness edged with intensity—its condensed milk creaminess is balanced by dark coffee, chocolate and warm woods for a very intimate wear."
				},
				{
					"handle": "celestial-object",
					"description": "Celestial Object is ideal if you prefer a gourmand that stays close and soft; the almond-milky sweetness and airy rice-paper texture make it feel delicate, skin-like and quietly addictive."
				},
				{
					"handle": "calisson",
					"description": "Calisson is a lovely choice when you want confectionery charm without heaviness: citrus brightness, orange-blossom biscuit and creamy sandalwood create a refined pastry-like glow."
				},
				{
					"handle": "vanilla-for-breakfast",
					"description": "Vanilla For Breakfast is for the unapologetic vanilla lover who wants presence; its layered vanilla, almond and tonka feel warm, toasted and immediately comforting."
				},
				{
					"handle": "davana-vanille",
					"description": "Davana & Vanille is the lighter, more effortless option here—fruity davana and soft vanilla give you a sweet, airy gourmand that feels cheerful and easy to wear."
				},
				{
					"handle": "dominanti-santi",
					"description": "Dominanti Santi offers the most sensual balance of sweet and savory: orange blossom, crème brûlée vanilla and salted-skin nuance create a luminous gourmand with real sophistication."
				}
			]
		},
		"joyful-citrus": {
			"title": "Joyful Citrus",
			"subtitle": "Bright citrus and sunny mood",
			"category": "By Mood",
			"description": "Treat yourself to a little sunshine in a bottle—these joyful citruses feel bright, uplifting, and made for your own happy moments.",
			"products": [
				{
					"handle": "yuzu",
					"description": "Yuzu by J-Scent is for the citrus lover who wants sparkle with edge: tart peel, sharp bergamot and thyme keep it vivid, clean and a little addictive rather than sweet. It feels like a bright mood you can wear all day."
				},
				{
					"handle": "neroli-petitgrain",
					"description": "Neroli & Petitgrain by 100BON is a lovely self-gift if you want something gentle, easy and comforting. Its mandarin, neroli and petitgrain feel freshly washed and quietly warm, like a familiar ritual that lifts your spirits."
				},
				{
					"handle": "tennis-club",
					"description": "Tennis Club by CARNER suits you if you like your citrus to feel breezy, polished and sunlit. Green neroli and salty marine notes give it a Mediterranean ease, while the woody musk keeps it chic and wearable."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-6",
					"description": "This is Not a Blue Bottle 1/.6 – Love by Histoires de Parfums is a beautiful pick if you want citrus with a softer, more intimate glow. Yuzu and grapefruit shine brightly at first, then orange blossom, musks and vetiver make it feel affectionate and close to the skin."
				},
				{
					"handle": "kajal-iii",
					"description": "Kajal III is ideal if you like your brightness with a confident, energizing twist. Lemon soda, grapefruit and ginger give it a sparkling lift, while amber and musk add a warm backbone that makes it feel bold and self-assured."
				},
				{
					"handle": "elisa",
					"description": "Elisa by Marie Jeanne is a sweetly reassuring choice for treating yourself to something fresh but cozy. Candied bergamot and yuzu bring sunny lift, while sandalwood and benzoin leave a skin-like softness that feels comforting and elegant."
				},
				{
					"handle": "far",
					"description": "Far by And Fragrance is perfect if you enjoy citrus with a playful gourmand side. The yuzu and lime are mouthwatering and bright, then vanilla, cream and biscuit notes turn it into a cheerful, dessert-like scent that still feels airy."
				},
				{
					"handle": "resonant",
					"description": "Resonant by Oddity is for the person who wants citrus to feel modern and a little cool. Yuzu and ginger flash over a metallic, ozonic texture, and the petitgrain and ambergris give it a crisp, intriguing depth."
				},
				{
					"handle": "still-life-in-rio",
					"description": "Still Life in Rio by Olfactive Studio is a great self-indulgence if you want your citrus to feel tropical and relaxed. Yuzu, mandarin and ginger open brightly, then coconut and rum add a warm, sun-kissed richness that lingers beautifully."
				}
			]
		},
		"quiet-luxury": {
			"title": "Quiet Luxury",
			"subtitle": "Understated elegance, whispered opulence",
			"category": "By Aesthetic",
			"description": "For the moments you want to feel quietly expensive, these scents drape you in softness, polish, and a little private indulgence.",
			"products": [
				{
					"handle": "cashmere-100",
					"description": "Cashmere 100 feels like the fragrance equivalent of your favorite knit: intimate, textured, and effortlessly refined. If you want something that whispers luxury close to the skin, this is the one to buy for yourself."
				},
				{
					"handle": "lumire-blanche",
					"description": "Lumiere Blanche is perfect when you want comfort with a crisp, modern edge. Its almond-milk softness and cool cardamom make it feel polished and soothing, like a beautifully tailored neutral you can wear every day."
				},
				{
					"handle": "white-rice",
					"description": "White Rice is a subtle treat for days when you want to smell clean, warm, and quietly memorable. The rice-and-jasmine softness gives it a comforting, skin-like charm that feels personal rather than performative."
				},
				{
					"handle": "rose-shiso",
					"description": "Rose Shiso suits you if you love elegance with a twist. The rose is lush, but the shiso and cedar keep it fresh and composed, giving you a distinctive signature that still feels understated."
				},
				{
					"handle": "flow",
					"description": "Flow is ideal if your style leans minimal and serene. It has that effortless, airy quality that makes you feel put together without ever trying too hard."
				},
				{
					"handle": "match",
					"description": "Match is for someone who likes their luxury a little dry, a little tactile, and very close to the skin. The suede and orris give it a refined, modern softness with just enough intrigue to keep it interesting."
				},
				{
					"handle": "lola-at-coat-check",
					"description": "Lola at Coat Check is a lovely self-purchase if you want quiet seduction with a brighter opening. The cardamom and white chocolate feel playful at first, then settle into a smooth, intimate warmth that lingers beautifully."
				},
				{
					"handle": "eastern-veil",
					"description": "Eastern Veil is the choice for treating yourself to something enveloping and unusual. Its savory sweetness and soft woods create a cocooning effect that feels expensive, sensual, and beautifully offbeat."
				},
				{
					"handle": "oblivion",
					"description": "Oblivion is a poised, easy-to-wear fragrance for when you want freshness with old-world composure. The bergamot and lavender are crisp, while the orris and woods give it a soft, powdery finish that feels effortlessly elegant."
				}
			]
		},
		"clean-girl-aesthetic": {
			"title": "Clean Girl Aesthetic",
			"subtitle": "Fresh, soapy, just-showered scents",
			"category": "By Aesthetic",
			"description": "For the days you want to smell freshly showered, polished, and quietly expensive, this edit is all soft skin, clean laundry, and airy glow.",
			"products": [
				{
					"handle": "the-ghost-in-the-shell",
					"description": "If you want clean with a futuristic twist, this is the one: metallic yuzu and rain-kissed florals feel crisp and luminous, while the milky skin accord keeps it intimate. It’s fresh, but not sterile."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"description": "This is your skin-but-better scent: a halo of transparent musks with a powdery softness that feels like the aftermath of a perfect shower. Wear it solo for a polished, personal finish or layer it effortlessly."
				},
				{
					"handle": "ramune",
					"description": "If you love a playful clean scent, this sparkling ramune-inspired blend gives you lemon, mint, and fizzy aldehydes that feel instantly uplifting. The soft musk and vanilla make it charmingly wearable, not sugary."
				},
				{
					"handle": "coton-egyptien",
					"description": "For the person who loves the smell of freshly washed sheets, Coton Egyptien is the easiest yes. Airy white musks and green freshness make it feel crisp and comforting, with enough structure to stay present all day."
				},
				{
					"handle": "flow",
					"description": "Flow is the understated option for someone who wants to smell serene rather than perfumed. Lily of the valley, herbs, and clean woods create a calm, effortless aura that reads refined and modern."
				},
				{
					"handle": "sun-bleached",
					"description": "Sun Bleached feels like clean skin after a bright day outside, with citrus and ginger opening into soft florals and a sheer musky drydown. It’s ideal if you want your freshness to feel sunlit and a little sensual."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-5",
					"description": "If you like your clean scents airy and abstract, Yang gives you that washed, luminous effect with ozonic florals and mineral brightness. The ambroxan and white musk add a soft warmth that feels smooth on skin."
				},
				{
					"handle": "bo-bo",
					"description": "Bo-Bo is for the clean girl who still wants a little charm: mandarin and lily of the valley keep it fresh, while jasmine and musk make it feel relaxed, breezy, and easy to wear every day."
				},
				{
					"handle": "vetiver-santal",
					"description": "If you prefer your freshness with more depth, Vetiver Santal balances citrus brightness with dry vetiver and creamy sandalwood. It’s clean, elegant, and quietly grounding without losing that just-showered feel."
				}
			]
		},
		"dark-academia": {
			"title": "Dark Academia",
			"subtitle": "Old libraries, leather-bound books, tweed",
			"category": "By Aesthetic",
			"description": "For late-night reading, ink-stained pages, and the pleasure of dressing your own mood, these scents feel like a private library with the lights low.",
			"products": [
				{
					"handle": "black",
					"description": "Black is for when you want your dark academia to feel lived-in rather than precious: pepper, incense and birch tar give it the scent of old leather chairs and smoke-dark corners."
				},
				{
					"handle": "honey-and-tar",
					"description": "Honey and Tar softens the study with a golden glow, blending honey, vanilla and incense over tarry leather so it feels like a beloved book warmed by a fire."
				},
				{
					"handle": "bohemian",
					"description": "Bohemian leans more rugged and intense, with oud, smoke and leather creating a serious, scholarly darkness that suits someone who likes their elegance a little untamed."
				},
				{
					"handle": "coeur-de-noir-1",
					"description": "Cœur de Noir is the most bookish of the bunch, with black ink, leather-bound paper and tobacco making it smell like a treasured first edition hidden in a mahogany desk."
				},
				{
					"handle": "mora",
					"description": "Mora offers a cooler, sharper take on the theme: grapefruit, herbs and vetiver keep it brisk, while leather and incense add just enough shadow for a thoughtful, solitary mood."
				},
				{
					"handle": "fauno",
					"description": "Fauno feels like a torchlit reading room after midnight, where tobacco, frankincense and supple leather create a warm, instinctive richness with real character."
				},
				{
					"handle": "smokin-gun",
					"description": "SMOKIN' GUN brings the ambience of a damp woodland fire to the library mood, with peated whisky and incense giving you something intimate, smoky and a little rebellious."
				},
				{
					"handle": "all-black",
					"description": "All Black is understated and confident, pairing tobacco, incense and dark woods with pepper and cinnamon for a restrained scent that still feels deeply atmospheric."
				},
				{
					"handle": "beso-de-fuego",
					"description": "Beso de Fuego is the most nocturnal choice here, with jasmine, frankincense and charred woods giving you a dramatic, smouldering finish that feels perfect for solitary evenings."
				}
			]
		},
		"main-character-energy": {
			"title": "Main Character Energy",
			"subtitle": "Bold, confident, impossible to ignore",
			"category": "By Aesthetic",
			"description": "For the days you want to walk in like the headline, these scents give you bold presence, magnetic warmth, and zero need to ask permission.",
			"products": [
				{
					"handle": "au-pied-du-rosier",
					"description": "Au Pied du Rosier gives you a rose with edge: honeyed elegance meets new-trainer leather, so it feels polished, sensual, and unmistakably self-assured."
				},
				{
					"handle": "eye-contact",
					"description": "Eye Contact is for when you want your fragrance to do the flirting for you—spicy pepper, saffron, and leather create that charged, can’t-look-away energy."
				},
				{
					"handle": "hey-bitch",
					"description": "Hey Bxtch balances bite and softness beautifully, with orange blossom, rhubarb, and leather making it feel playful, provocative, and confidently put-together."
				},
				{
					"handle": "nuit-de-baklite",
					"description": "Nuit de Bakélite suits the person who prefers striking over pretty; its green, leathery tuberose feels uncompromising, modern, and impossible to ignore."
				},
				{
					"handle": "agrani",
					"description": "Agrani wraps cherry, almond, tobacco, and oud into something warm and commanding, like a vintage glamour piece worn with total conviction."
				},
				{
					"handle": "cavallo-di-fuoco",
					"description": "火马 - Cavallo di fuoco is for the bold collector who likes their perfume unexpected—bright herbs and fruit collide with smoky oud for a vivid, untamed signature."
				},
				{
					"handle": "cuirs",
					"description": "CUIRS feels like stepping into a beautifully worn atelier, where smoky leather, saffron, and woods give you a refined but powerful presence."
				},
				{
					"handle": "kamel-oud",
					"description": "Kamel Oud is rich, dark, and alluring, with raspberry and rose lifting the smoky oud so it lands as sensual rather than heavy, perfect for after-dark confidence."
				},
				{
					"handle": "styrax",
					"description": "STYRAX is the one to choose when you want raw magnetism; its animalic amber and leathery depth feel wild, warm, and totally in command."
				}
			]
		},
		"cottagecore": {
			"title": "Cottagecore",
			"subtitle": "Wildflowers, herbs, honey and meadow warmth",
			"category": "By Aesthetic",
			"description": "For your cottagecore moment, these scents gather wildflowers, herbs, honeyed light and meadow-green warmth into something you can wear just for yourself.",
			"products": [
				{
					"handle": "dangerosa",
					"description": "Dangerosa is for when you want your garden roses with a little attitude: the bitter herbs, peppery sparkle and resinous woods keep it fresh, thorny and beautifully untamed."
				},
				{
					"handle": "ara",
					"description": "ARС leans into the herb garden fantasy so well, with basil, thyme, lavender and moss giving it that old-world, hand-picked-from-the-border feeling. Its smoky sweetness adds a cozy, lived-in warmth that suits a quiet self-treat."
				},
				{
					"handle": "divina-flora",
					"description": "Divina Flora feels like walking through dew-bright blooms after sunrise, with peony, jasmine and narcissus softened by herbs and creamy woods. It’s a lovely choice if you want your cottagecore to feel luminous, romantic and gently polished."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"description": "PG16 Jardins de Kérylos captures the shade of a cultivated garden, where fig, peach and green woods feel sun-warmed but restrained. It’s perfect if you like your sweetness subtle, leafy and elegant rather than jammy."
				},
				{
					"handle": "rusalka-s-embrace",
					"description": "Rusalka’s Embrace takes the meadow fantasy underwater, with seaweed, mint, pine and moss creating something cool, green and haunting. Choose it if you want your self-purchase to feel a little secretive and otherworldly, not just pretty."
				},
				{
					"handle": "debonaire",
					"description": "Debonaire gives you a crisp herbal-citrus opening with lavender and rosemary, then settles into soft woods and musk like a well-loved linen shirt. It’s an easy, comforting pick for an everyday cottagecore scent with quiet charm."
				},
				{
					"handle": "immortali-santi",
					"description": "Immortali Santi is the brightest, most celebratory floral here, with sparkling citrus and airy white petals that feel like flowers in a sunlit kitchen window. If you want your treat to feel uplifting and pretty without heaviness, this is a graceful choice."
				},
				{
					"handle": "talento",
					"description": "Talento turns rose into a fresh green talisman, with mint and aldehydes making it feel cool, protected and almost dew-lit. It suits someone who wants their florals to feel serene, intimate and a little magical."
				},
				{
					"handle": "nerosa",
					"description": "Nerosa is the richest, darkest rose in the set, wrapped in saffron, oud and leather for a more dramatic cottage garden after dusk. Pick it if your self-indulgence leans plush, spicy and unapologetically enveloping."
				}
			]
		},
		"long-lasting-perfumes": {
			"title": "Long-Lasting Perfumes",
			"subtitle": "Beast-mode sillage that goes the distance",
			"category": "By Function",
			"description": "If you want a signature that lingers like a memory, these beast-mode beauties are made to stay close, speak loudly, and carry you through the night.",
			"products": [
				{
					"handle": "al-sarab",
					"description": "Al Sarab is for the days you want to feel enveloped in something rare and commanding; its oud-heavy, leathery depth gives you that luxurious, desert-at-dusk drama that lasts for hours."
				},
				{
					"handle": "hey-bitch",
					"description": "Hey Bxtch! suits you if you like your self-gift with attitude: orange blossom and rhubarb keep it vivid and flirtatious, while leather, iris and vanilla make it sensuous enough to leave a memorable trail."
				},
				{
					"handle": "tiger-rouge",
					"description": "Tiger Rouge is the one to choose when you want boldness with polish; its spicy citrus opening and oud-incense depth create a fierce, confident scent that feels instantly statement-making."
				},
				{
					"handle": "502-iris-cartagena",
					"description": "502 Iris Cartagena is ideal if you love a cozy but elevated gourmand, with rum, coffee and cocoa wrapped in iris and woods for a rich, intimate perfume that stays beautifully close and long-lasting."
				},
				{
					"handle": "fathom-v-directors-cut",
					"description": "Fathom V Director's Cut is perfect for someone drawn to crisp, untamed scents with real presence; its green marine intensity feels like a stormy walk through a forest, with impressive staying power."
				},
				{
					"handle": "le-mat",
					"description": "Le Mat will suit you if you want something sensual, mysterious and a little unconventional; the rose, patchouli and immortelle give it a dark floral richness that lingers with elegant persistence."
				},
				{
					"handle": "devoti-peccatori",
					"description": "Devoti Peccatori is a decadent pick for when you want your fragrance to feel warm, seductive and unforgettable; leather, cocoa and ambergris create a plush, intimate trail with serious longevity."
				},
				{
					"handle": "lumieres",
					"description": "Lumières is for the person who prefers a cooler kind of power—mineral, musky and quietly radiant—yet still wants a scent that clings to skin and clothing with refined endurance."
				},
				{
					"handle": "st-pauls-1",
					"description": "St. Paul's is the choice if you love opulence and drama in equal measure; its glowing florals, fruits and frankincense give you a baroque, cathedral-like richness that lasts beautifully all day."
				}
			]
		},
		"best-for-the-office": {
			"title": "Best for the Office",
			"subtitle": "Professional, inoffensive, quietly impressive",
			"category": "By Function",
			"description": "A polished scent wardrobe for the days you want to feel composed, capable, and just a little more memorable at your desk.",
			"products": [
				{
					"handle": "vetiver-santal",
					"description": "Vetiver Santal feels like a crisp shirt with excellent tailoring: bergamot keeps it bright, while vetiver, sandalwood, and musk stay refined and never loud. It’s ideal if you want a scent that reads confident, clean, and quietly expensive."
				},
				{
					"handle": "rainy-city",
					"description": "Rainy City is perfect for the office if you like a calm, thoughtful presence rather than a statement. The tea notes feel polished and modern, with vetiver and transparent woods adding a cool, professional edge."
				},
				{
					"handle": "santal",
					"description": "Santal gives you that smart, put-together sandalwood effect with enough citrus and spice to keep it lively. It’s a strong choice for someone who wants warmth and depth without ever crossing into heavy territory."
				},
				{
					"handle": "albatros",
					"description": "Albatros is for the person who likes their freshness with a sleek, airy sophistication. The marine lift and dry woods feel immaculate and spacious, making it especially good when you want to smell clean, composed, and subtly distinctive."
				},
				{
					"handle": "beso-pasion",
					"description": "Beso Pasión brings a more effortless, sunny confidence to office wear, with citrus, ginger, and fig that feel polished rather than playful. It’s a nice pick if you want something fresh and warm that still stays workplace-friendly."
				},
				{
					"handle": "bo-bo",
					"description": "Bo-Bo has a bright, easy charm that makes it feel instantly approachable in close quarters. The mandarin and soft florals keep it light and tidy, while vetiver gives it enough structure to feel grown-up."
				},
				{
					"handle": "602",
					"description": "602 Bois Narcotique is a great choice if you prefer something understated but not bland. Its dry cedar, patchouli, and pepper create a neat woody frame that feels intelligent, grounded, and quietly memorable."
				},
				{
					"handle": "coton-egyptien",
					"description": "Coton Egyptien is the fragrance equivalent of freshly pressed linens and a clear desk. Its airy musks and soft woods make it wonderfully inoffensive, but the lasting presence means it still feels considered and intentional."
				},
				{
					"handle": "floating",
					"description": "Floating suits someone who wants to feel softly polished all day, with a gentle, modern femininity or neutrality. Peach, linen, and musk create a clean, relaxed aura that’s understated enough for work but still beautifully personal."
				}
			]
		},
		"date-night-scents": {
			"title": "Date Night Scents",
			"subtitle": "Seductive, memorable, close-quarters magnetic",
			"category": "By Function",
			"description": "For the nights you want to feel irresistible to yourself first, these scents wrap you in warmth, glow, and a little dangerous charm.",
			"products": [
				{
					"handle": "castiamanti",
					"description": "Castiamanti feels like candlelight on skin: its saffron, rose, and incense create a plush, intimate aura that lingers beautifully at close range. If you want something richly sensual and polished, this is a decadent choice."
				},
				{
					"handle": "the-empress-kiss",
					"description": "The Empress Kiss gives you that commanding, flirtatious energy without losing softness, thanks to its rose-oud heart and caramel-kissed sweetness. It suits a date night when you want to feel powerful, polished, and a little addictive."
				},
				{
					"handle": "lamar",
					"description": "Lamar is for when you want your date-night scent to feel radiant and welcoming, with juicy fruits that bloom into creamy florals and a smooth amber-vanilla finish. It’s lush, flattering, and easy to fall for on your own skin."
				},
				{
					"handle": "au-pied-du-rosier",
					"description": "Au Pied du Rosier is a bold pick if you like your romance with edge, pairing honeyed rose with saffron, oud, and that unexpected sporty-leather twist. It’s distinctive, sensual, and memorable in a way that feels very self-assured."
				},
				{
					"handle": "ta-if",
					"description": "Ta’if is the fragrance for a confident evening out, with luminous rose, warm saffron, and date sweetness giving it a rich dusk-like glow. It feels elegant and magnetic, like the moment just before the night turns interesting."
				},
				{
					"handle": "drunk-saffron",
					"description": "DRUNK SAFFRON is made for slow, intimate evenings, with cognac warmth, saffron spice, and leather giving it a sultry, skin-close glow. The vanilla softens it into something indulgent and deeply comforting to wear for yourself."
				},
				{
					"handle": "rouge-avignon",
					"description": "Rouge Avignon is for when you want drama: dark rose, cocoa, and truffle create a gothic gourmand mood that feels plush and mysterious. It’s the kind of scent that makes a quiet dinner feel cinematic."
				},
				{
					"handle": "sirio",
					"description": "Sirio suits someone who likes romance with texture, opening with tart, juicy fruit before settling into rose, musk, oud, and vanilla. It feels airy yet grounded, like a soft-focus glow with real depth."
				},
				{
					"handle": "honey-oud",
					"description": "Honey Oud is a beautiful choice if you want your date-night scent to feel smooth, golden, and quietly seductive rather than loud. The honeyed sweetness and rose warmth make oud feel plush, wearable, and irresistibly cozy."
				}
			]
		},
		"bedtime-fragrances": {
			"title": "Bedtime Fragrances",
			"subtitle": "Calming, cocooning scents for the pillow",
			"category": "By Function",
			"description": "For the nights you want to feel gently held, these cocooning scents turn your pillow into a private little luxury.",
			"products": [
				{
					"handle": "cashmere-100",
					"description": "Cashmere 100 is a beautiful self-purchase if you love the idea of fragrance as a soft layer against skin: orris, musk and sandalwood make it feel polished, calm and quietly indulgent."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"description": "PG4.1 Le Musc & La Peau is perfect when you want something intimate and nearly invisible, with that warm-skin musk effect that feels personal, comforting and effortlessly elegant."
				},
				{
					"handle": "cherry-passion",
					"description": "Cherry Passion suits a mood that wants bedtime to feel a little more playful; the fruity sweetness is lush and cozy, while the sandalwood and vanilla keep it from feeling too bright or sugary."
				},
				{
					"handle": "cloud-essence-28-08",
					"description": "Cloud Essence 28.08 is the one to choose for a true wind-down scent, with its milky rice-powder softness and dreamy warmth that feels like slipping into fresh sheets after a long day."
				},
				{
					"handle": "tonkade",
					"description": "Tonkade is a lovely treat if you want your bedtime fragrance to feel edible and enveloping, with vanilla and tonka wrapped in woods and incense for a richer, more grown-up comfort."
				},
				{
					"handle": "yawahada",
					"description": "Yawahada (Soft skin) is ideal for someone who likes a tender, skin-close scent that feels soothing rather than showy; its rice powder, sandalwood and musk create a gentle, sensual hush."
				},
				{
					"handle": "figlia-di-eva",
					"description": "Figlia di Eva gives bedtime a brighter twist, balancing juicy freshness with soft musk and oud so it feels comforting but still a little mysterious and chic."
				},
				{
					"handle": "sadonaso",
					"description": "Sadonaso is for the self-purchaser who wants something more daring at night: coffee, tobacco and tonka make it decadent and warm, with a plush, intimate edge."
				},
				{
					"handle": "mauna",
					"description": "Mauna feels like a slow exhale in fragrance form, with honeyed sandalwood, incense and tonka creating a serene, enveloping scent that makes your evening ritual feel deeply luxurious."
				}
			]
		},
		"travel-friendly-fragrances": {
			"title": "Travel-Friendly Fragrances",
			"subtitle": "Compact sizes and versatile scents for packing",
			"category": "By Function",
			"description": "Pack light, smell unforgettable: these easy-to-carry scents are made for treating yourself from takeoff to sunset.",
			"products": [
				{
					"handle": "packed-your-holiday-perfume-wardrobe-unisex",
					"description": "Bloom Sample Packs’ holiday wardrobe is perfect if you like options without the baggage—one set carries you from crisp daytime freshness to a richer evening mood, so you can match your scent to every stop on the trip."
				},
				{
					"handle": "niche-discovery-box",
					"description": "Ortir Apothecari’s Niche Discovery Box is a smart self-gift if you enjoy exploring without committing to a full bottle; the mix of aromatic, green, and resinous notes feels versatile enough for city breaks, cabins, and everything in between."
				},
				{
					"handle": "matcha-mist",
					"description": "Brume Matcha suits a traveler who wants something calm and polished in the bag: its tea-like, minty-green freshness feels restful after a long journey and quietly refined for everyday wear."
				},
				{
					"handle": "eau-de-the-gingembre",
					"description": "Eau de thé & Gingembre is ideal if you love a bright, uplifting scent that won’t overwhelm in close quarters; the citrus-ginger sparkle feels fresh in transit and easy to wear all day."
				},
				{
					"handle": "jawa-cologne",
					"description": "Jawa Cologne is a great pick for someone who wants freshness with character—the lime and bergamot keep it light and easy, while the earthy vetiver gives it enough depth to feel interesting on the move."
				},
				{
					"handle": "exploration-kit",
					"description": "Commodity’s Exploration Kit is made for the indecisive self-purchaser in the best way: you get a compact range of moods, from soft and cozy to airy and smooth, so you can choose based on the day’s plans."
				},
				{
					"handle": "firenze",
					"description": "Firenze feels like a little luxury for your carry-on, with sunny citrus that brightens travel days and a woodier drydown that works beautifully when you want something a bit more elegant at night."
				},
				{
					"handle": "mister-bungalow",
					"description": "Mister Bungalow is for the person who wants vacation energy in a bottle—the lemongrass, orange, and lavender feel breezy and relaxed, like a scent you can wear from poolside to dinner."
				},
				{
					"handle": "blueforest",
					"description": "Blueforest is a strong choice if you prefer a clean, understated signature scent while away; the cedar, pepper, and musk stay polished and close to the skin, making it easy to wear anywhere."
				}
			]
		},
		"for-the-curious-nose": {
			"title": "For the Curious Nose",
			"subtitle": "For adventurous fragrance explorers",
			"category": "Your Style",
			"description": "For the self-curious, this collection is a passport to scents that surprise, seduce, and refuse to sit still.",
			"products": [
				{
					"handle": "monopteros",
					"description": "Monopteros is for the moment you want something creamy, strange, and effortlessly cool—coconut and coffee make it inviting, while aldehydes and cardamom keep it deliciously offbeat."
				},
				{
					"handle": "cavallo-di-fuoco",
					"description": "火马 - Cavallo di fuoco suits you if you like your fragrance with motion and bite: mint, pomelo, and ginger feel vivid and alive, then the oud and honeyed depth make it linger with real character."
				},
				{
					"handle": "black-oregano",
					"description": "Black Oregano is a strong choice when you want a scent that feels sharp, polished, and a little dangerous; the oregano and leather give it edge, while sandalwood and vetiver keep it wearable and refined."
				},
				{
					"handle": "rouge",
					"description": "Rouge is perfect if you’re drawn to fragrances that smell savory, strange, and quietly magnetic—beetroot and incense create a moody contrast that feels both artistic and unexpectedly sensual."
				},
				{
					"handle": "boar",
					"description": "Boar is for days when you want to wear something wild and grounded; the apple and truffle opening is arresting, and the woods, leather, and vetiver build a forest-dark trail that feels unapologetically primal."
				},
				{
					"handle": "indian-grass",
					"description": "Indian Grass suits the exploratory wearer who likes green aromatics with a restless streak—cannabis, coriander, and incense give it a hazy, contemplative feel that stays intriguing close to the skin."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"description": "V.I.T.R.I.O.L. is for the fearless collector who wants perfume as provocation; it’s intentionally abrasive, unsettling, and unforgettable, with a dark beauty that rewards pure curiosity."
				},
				{
					"handle": "iris-altesse",
					"description": "Iris Altesse is ideal if you want elegance with a twist—the iris is cool and poised, but leather, cassis, and tuberose give it a modern crispness that feels quietly luxurious."
				},
				{
					"handle": "sand",
					"description": "Sand is a great pick if you love sandalwood but want it to surprise you; banana, incense, and benzoin turn the familiar into something warm, playful, and a little hypnotic."
				}
			]
		},
		"for-the-minimalist": {
			"title": "For the Minimalist",
			"subtitle": "Clean, calm, elegant fragrances",
			"category": "Your Style",
			"description": "A quiet collection for when you want your fragrance to feel like a deep breath, a clean line, and a little private luxury.",
			"products": [
				{
					"handle": "rainy-city",
					"description": "Rainy City feels like your best solitary walk home: tea-steeped, rain-fresh, and elegantly restrained, with vetiver and woods giving it a polished city calm."
				},
				{
					"handle": "flow",
					"description": "Flow is ideal if you love the look of simplicity but still want nuance—airy lily of the valley, soft musk, and cedar create a scent that feels effortlessly put together."
				},
				{
					"handle": "seychelles-breeze",
					"description": "Seychelles Breeze suits the minimalist who still wants a hint of sunlight; its green, floral clarity is crisp and easy, with a gentle radiance that never feels loud."
				},
				{
					"handle": "lumire-blanche",
					"description": "Lumiere Blanche is for the person who wants comfort to look chic: cardamom and almond-milk softness wrap you in a clean, creamy cocoon that feels serene and expensive."
				},
				{
					"handle": "albatros",
					"description": "Albatros gives you space to exhale, with sea-salt brightness and dry woods that feel open, modern, and quietly assured—clean, but never cold."
				},
				{
					"handle": "pg191-neroli-ad-astra",
					"description": "PG19.1 Néroli ad Astra is a refined choice if you like your freshness with a twist; the neroli is bright and transparent, while the soft musky base keeps it intimate and wearable."
				},
				{
					"handle": "the-ghost-in-the-shell",
					"description": "The Ghost In The Shell is perfect if you want minimalism with a futuristic edge—metallic yuzu and milky skin notes make it feel clean, modern, and a little intriguingly strange."
				},
				{
					"handle": "602",
					"description": "602 Bois Narcotique is a smart pick for someone who prefers structure over sweetness; its dry cedar and patchouli are crisp, grounded, and subtly textured."
				},
				{
					"handle": "in-this-world",
					"description": "In This World feels like a tidy, well-loved wardrobe in fragrance form: green fig, herbs, and soft woods create a calm, tactile scent that stays close and feels personal."
				}
			]
		},
		"for-the-maximalist": {
			"title": "For the Maximalist",
			"subtitle": "Rich, bold, attention-grabbing fragrances",
			"category": "Your Style",
			"description": "If you love fragrance to enter the room before you do, this is your invitation to indulge in something unapologetically rich, dark, and dramatic.",
			"products": [
				{
					"handle": "osang",
					"description": "Osang feels like a decadent signature for someone who wants their perfume to smoulder: the honeyed resin, pepper, and incense make it lush and commanding, while the ashier edges keep it intriguingly untamed."
				},
				{
					"handle": "moonlight-ceremony",
					"description": "Moonlight Ceremony is ideal if you want your boldness wrapped in shadow—smoke, birch tar, and leather create a nocturnal aura that feels expensive, atmospheric, and impossible to ignore."
				},
				{
					"handle": "cavallo-di-fuoco",
					"description": "火马 - Cavallo di fuoco suits the maximalist who likes surprise as much as impact; its minty, peppery opening snaps awake the senses before the honeyed oud settles into a vivid, smoky trail."
				},
				{
					"handle": "myrrh-blood-drop",
					"description": "No. 8 – Drop of Blood is for the person who wants their scent to feel mysterious and slightly dangerous, with mandarin and saffron flashing first before incense, myrrh, and oud take over in a dark, resinous way."
				},
				{
					"handle": "oud-imperial-extrait",
					"description": "Oud Imperial Extrait is a great choice if you want oud with polish and power—the birch tar and labdanum give it a smouldering leather richness that feels luxurious rather than overwhelming."
				},
				{
					"handle": "skin-fetish",
					"description": "Skin Fetish is a deliciously provocative pick if you enjoy contrast: juicy dark cherry and rose draw people in, while incense, oud, and amber give it a smoky, sensual finish."
				},
				{
					"handle": "bohemian",
					"description": "Bohemian will appeal if your taste runs to rugged, lived-in textures; its incense, smoke, and earthy oud feel like a leather jacket worn in all the right places."
				},
				{
					"handle": "eye-contact",
					"description": "Eye Contact is the bold flirt of the group, with pink pepper, saffron, and leather creating a charged, intimate heat that feels perfect for nights when you want to leave a lasting impression."
				},
				{
					"handle": "velvet-bold",
					"description": "Velvet | Bold is for someone who loves opulence with a darker edge—the rose, saffron, and vanilla are plush, but the patchouli, amber, and leather keep it deep, dramatic, and full-bodied."
				}
			]
		},
		"for-the-collector": {
			"title": "For the Collector",
			"subtitle": "Rare, distinctive, conversation-starting fragrances",
			"category": "Your Style",
			"description": "For the collector who loves to wear something rare, these are the bottles that turn a private indulgence into a story worth telling.",
			"products": [
				{
					"handle": "iris-altesse",
					"description": "Iris Altesse is for the iris lover who wants elegance with an edge: its chalky leather and cool floral lift feel polished, but never predictable. A beautifully composed choice if you enjoy understated luxury with a modern bite."
				},
				{
					"handle": "myrrh-blood-drop",
					"description": "No. 8 – Drop of Blood suits you if you’re drawn to fragrances with drama and a little shadow. The mandarin-saffron opening flashes before the resinous incense, oud and myrrh settle into something magnetic and defiantly individual."
				},
				{
					"handle": "ambre-de-noir",
					"description": "Ambre de Noir is a rich, intimate amber for someone who wants warmth with character, not sweetness for its own sake. The cola-dark spices, tobacco and old-world vanilla make it feel plush, enveloping and quietly unforgettable."
				},
				{
					"handle": "cambodian-oud",
					"description": "Cambodian Oud is the obvious pick if you want the material itself to speak: smoky, leathery and unapologetically natural. It wears like a collector’s piece, rewarding anyone who appreciates oud in its raw, self-possessed form."
				},
				{
					"handle": "high-to-lotus",
					"description": "High to Lotus is for the adventurous wearer who likes their greens strange, briny and almost otherworldly. Artichoke, seaweed and peppermint create a daring opening, while oud and sandalwood give it a dark, memorable finish."
				},
				{
					"handle": "nuit-de-baklite",
					"description": "Nuit de Bakélite is perfect if you love florals that refuse to be pretty in the usual way. Its green, stalky tuberose and dry leather facets make it sharp, tactile and thrillingly unconventional."
				},
				{
					"handle": "rosa-umami-v2",
					"description": "Rosa umami V2 is for the collector who wants rose with depth, tension and a savoury twist. The miso, seaweed and tea give it a fascinating dark texture before the rose blooms into something layered and unexpected."
				},
				{
					"handle": "a-masquerade",
					"description": "A Masquerade is a wonderful self-purchase if you want something versatile but still distinctive enough to spark conversation. Raspberry, tuberose, leather and rum give it a polished, evening-ready glamour with a playful secret."
				},
				{
					"handle": "athanor",
					"description": "Athanor is for the wearer who likes perfume as an experience, not just a scent. Its smoky, heated resins and unusual sulphuric spark make it feel elemental, intense and utterly worth collecting."
				}
			]
		},
		"for-the-romantic": {
			"title": "For the Romantic",
			"subtitle": "Soft, sensual, warm fragrances",
			"category": "Your Style",
			"description": "For the days you want to indulge your softer side, these romantic fragrances wrap you in warmth, sweetness, and a little heart-fluttering allure.",
			"products": [
				{
					"handle": "lamar",
					"description": "Lamar is a gorgeous self-treat if you love your romance lush and generous: the pineapple and berries make it instantly joyful, while the rose, amber, and vanilla settle into a warm, sensual glow."
				},
				{
					"handle": "mathilde",
					"description": "Mathilde suits you if you want something elegant but full-bodied, with tropical fruit and opulent white florals giving it a painterly richness that feels beautifully indulgent on your own skin."
				},
				{
					"handle": "guava-sorbet",
					"description": "Guava Sorbet is perfect for a playful romantic mood, balancing juicy guava and passionfruit with creamy vanilla and jasmine for a scent that feels sunlit, sweet, and softly seductive."
				},
				{
					"handle": "damask",
					"description": "Damask is a lovely choice when you want your rose to feel fresh and sophisticated rather than sugary; the blackcurrant, pear, and mineral amber add a breezy sensuality that feels quietly luxurious."
				},
				{
					"handle": "the-empress-kiss",
					"description": "The Empress Kiss is for moments when you want to feel unmistakably alluring, with saffron, cardamom, rose, and oud creating a rich, spicy sweetness that lingers beautifully and confidently."
				},
				{
					"handle": "asoral",
					"description": "Asoral feels like a tender, intimate luxury, with dewy roses, magnolia, and soft musk giving you a delicate floral aura that’s gentle, polished, and quietly romantic."
				},
				{
					"handle": "ensang-noir",
					"description": "Ensang Noir is ideal if your romantic side leans dramatic and mysterious, because its dark rose, smoky woods, and opoponax create a deep, atmospheric signature with vintage glamour."
				},
				{
					"handle": "juice-personal",
					"description": "Juice | Personal is a charming pick for everyday self-gifting, since the raspberry and rhubarb feel bright and flattering while the rose and musk keep it close, soft, and subtly sensual."
				},
				{
					"handle": "koiame",
					"description": "恋雨 Koiame is for when you want romance to feel tender and contemplative, with rain-fresh florals and clean musk creating a delicate, memory-like scent that feels comforting and personal."
				}
			]
		},
		"for-the-trend-lover": {
			"title": "For the Trend-Lover",
			"subtitle": "Modern, fashionable, social-friendly fragrances",
			"category": "Your Style",
			"description": "Treat yourself to fragrances that feel current, polished, and effortlessly social—the kind that get compliments before you even say hello.",
			"products": [
				{
					"handle": "ziveli",
					"description": "Živeli is perfect if you want to smell like the most interesting person in the room: juicy plum and raspberry make it instantly appealing, while leather, tobacco, and saffron give it a chic, grown-up edge."
				},
				{
					"handle": "agrani",
					"description": "Agrani suits you if you like your trend-forward scents with attitude. Cherry and almond feel lush and easy to love, but oud, leather, and tobacco give it a bold, fashion-editor presence."
				},
				{
					"handle": "ruby",
					"description": "Ruby is the one to reach for when you want playful glamour with a soft-focus finish. Its cherry, berries, and pineapple sparkle up top, then whipped cream and brown sugar wrap you in an irresistible, crowd-pleasing warmth."
				},
				{
					"handle": "skin-fetish",
					"description": "Skin Fetish is a great pick if you want something sensual and a little mysterious. The dark cherry keeps it wearable and modern, while incense, rose, and oud add a smoky, provocative twist."
				},
				{
					"handle": "vettii",
					"description": "Vettii feels ideal for someone who likes quiet luxury with a fashionable silhouette. Fig and quince keep it fresh and refined, then saffron, amber, and oud bring a smooth, expensive-looking depth."
				},
				{
					"handle": "andevalo",
					"description": "Andevalo is for the person who wants fruity brightness with a darker, more styled finish. Strawberry, raspberry, and black cherry feel juicy and modern, while leather and smoky amber make it feel sleek and distinctive."
				},
				{
					"handle": "vietnamese-coffee",
					"description": "Vietnamese Coffee is a delicious choice if you love a fragrance that feels cozy but still cool. The roasted coffee and condensed milk are instantly addictive, and the chocolate, tonka, and woods make it feel intimate and polished."
				},
				{
					"handle": "drunk-saffron",
					"description": "DRUNK SAFFRON gives you that warm, nightlife-ready vibe in one spray. Cognac, saffron, and leather create a rich, intoxicating impression, while vanilla keeps it smooth and easy to wear."
				},
				{
					"handle": "closeup",
					"description": "Close-Up is a smart choice if you want something moody but not heavy. The cherry-and-coffee opening feels contemporary and a little addictive, while tobacco, cedar, and amber give it a refined, dressed-up finish."
				}
			]
		},
		"sandalwood": {
			"title": "Sandalwood",
			"subtitle": "Creamy, warm sandalwood-centred fragrances",
			"category": "By Scent",
			"description": "For when you want your scent to feel like a private luxury, these sandalwood fragrances wrap warmth, softness, and quiet confidence around you.",
			"products": [
				{
					"handle": "a-pas-de-loup",
					"description": "If you’re craving a walk-in-the-woods mood with a refined edge, this is the one: damp earth and oakmoss feel freshly alive, while sandalwood and benzoin make it quietly comforting on skin."
				},
				{
					"handle": "cherry-passion",
					"description": "For a treat-yourself fragrance that feels playful but polished, the juicy cherry-and-strawberry opening melts into creamy sandalwood, vanilla, and tonka, giving the sweetness a warm, grown-up finish."
				},
				{
					"handle": "japanese-whiskey",
					"description": "This is a luxurious choice if you love cozy, atmospheric scents with depth: smoky whisky, toasted chestnut, and mizunara oak are beautifully cushioned by sandalwood, making it feel rich and contemplative."
				},
				{
					"handle": "mauna",
					"description": "Mauna suits you if you want something enveloping and a little mysterious, with honeyed sandalwood, incense, and resins creating a serene, almost meditative warmth that lingers beautifully."
				},
				{
					"handle": "moko",
					"description": "Choose Moko if you like your sandalwood with freshness and life: rain-soaked greenery, fig leaves, and raspberry keep it vibrant, while moss and woods give it a grounding, restorative feel."
				},
				{
					"handle": "monsieur",
					"description": "This is for the person who prefers their sandalwood dark, elegant, and a bit untamed: cedar, vetiver, and papyrus build a moody forest atmosphere that feels sophisticated rather than heavy."
				},
				{
					"handle": "pg-111-indian-wood",
					"description": "If you want a sandalwood scent with a clever contrast, PG11.1 balances cool peppermint against warm spices and woody depth, making it feel relaxed, modern, and subtly sensual."
				},
				{
					"handle": "smokin-gun",
					"description": "SMOKIN' GUN is ideal if you’re drawn to smoky, intimate fragrances with character, where peated whisky and incense meet sandalwood for a campfire warmth that feels bold but wearable."
				},
				{
					"handle": "yawahada",
					"description": "Yawahada is a beautiful self-gift if you love the idea of soft skin in scent form: pear, rice powder, and musk settle into sandalwood and amber for a tender, powdery, quietly sensual finish."
				}
			]
		},
		"neroli-orange-blossom": {
			"title": "Neroli & Orange Blossom",
			"subtitle": "Luminous neroli and orange blossom compositions",
			"category": "By Scent",
			"description": "A bright, self-gifting edit of neroli and orange blossom scents that feel like sunshine you can wear for yourself.",
			"products": [
				{
					"handle": "neroli-petitgrain",
					"description": "If you want something easy, comforting, and quietly uplifting, this is a lovely everyday pick. The mandarin-and-neroli brightness settles into a soft vanilla warmth that feels like a clean, happy ritual just for you."
				},
				{
					"handle": "tennis-club",
					"description": "For a more polished, sunlit mood, this brings Mediterranean freshness with a cool, sporty edge. The salty marine breeze and woody musk make it feel effortless and expensive without trying too hard."
				},
				{
					"handle": "dancing-light",
					"description": "Choose this if you like your florals crisp, airy, and a little unexpected. The minty, piney opening gives way to luminous neroli and jasmine, so it feels like a breath of cold air turning creamy and calm."
				},
				{
					"handle": "hiraeth",
					"description": "This is a beautiful choice when you want something personal and quietly emotional. Neroli and jasmine are wrapped in frankincense and amber, giving it a cocooning softness that feels intimate rather than loud."
				},
				{
					"handle": "flower-frost-19-12",
					"description": "If you love clean florals with a frosted, modern feel, this one is very wearable. The cool ozonic brightness is softened by magnolia, musk, and woods, making it feel fresh but never sharp."
				},
				{
					"handle": "lacura",
					"description": "This is the richest, most indulgent option in the collection, perfect if you want your self-purchase to feel luxurious. Bitter orange, neroli, and orange blossom bloom into musky woods, vanilla, and caramel for a nectarous, comforting glow."
				},
				{
					"handle": "pg191-neroli-ad-astra",
					"description": "Pick this if you prefer neroli with a drier, more transparent finish. The geranium and agave flower add a subtle green twist, while vetiver keeps it refined, airy, and a little bit intellectual."
				},
				{
					"handle": "seychelles-breeze",
					"description": "This feels like a relaxed escape in fragrance form, ideal for days when you want brightness without effort. Fresh greens and orange blossom rest on airy musk and ambroxan, giving it a crisp, radiant ease."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-6",
					"description": "If you want citrus-floral brightness with a tender, affectionate feel, this is a standout. Yuzu, grapefruit, and orange blossom are softened by neroli and white musks, making it feel like a warm, luminous hug."
				}
			]
		},
		"cherry-red-fruits": {
			"title": "Cherry & Red Fruits",
			"subtitle": "Juicy cherry and red fruit fragrances",
			"category": "By Scent",
			"description": "Treat yourself to a burst of cherry-red brightness: these juicy, sensual scents feel like slipping into something deliciously indulgent just for you.",
			"products": [
				{
					"handle": "ziveli",
					"description": "If you want cherry with a grown-up edge, Živeli gives you boozy plum, raspberry and honeyed leather in a warm, convivial glow. It feels rich and self-assured, like a night out you bought for yourself."
				},
				{
					"handle": "chateau-de-velours",
					"description": "Château de Velours is perfect if you love your berries plush and decadent, with raspberry, blackcurrant and wild strawberry wrapped in red wine and dark chocolate. It’s velvety, sensual, and just a little edible in the best way."
				},
				{
					"handle": "ruby",
					"description": "Ruby is the most unapologetically indulgent pick here, with cherry, red berries and pineapple whipped into cream and brown sugar. It’s playful, glossy and comforting, like a sweet reward you don’t need to share."
				},
				{
					"handle": "andevalo",
					"description": "Andevalo suits someone who wants fruit with depth: strawberry, raspberry and black cherry are pulled into smoky labdanum, leather and dark woods. The result is juicy at first, then beautifully dense and mysterious on skin."
				},
				{
					"handle": "tokyo-nights",
					"description": "Tokyo Nights | ナイト is ideal if you like your fruit moody and intimate, with jammy plum and plum wine softened by tobacco and amber. It has a quiet, late-night elegance that feels personal rather than loud."
				},
				{
					"handle": "juice-balanced",
					"description": "Juice | Balanced & Expressive is for the berry lover who wants brightness with presence, thanks to raspberry, strawberry and tart rhubarb over airy florals and amberwood. It’s cheerful, polished and easy to wear when you want to feel vividly yourself."
				},
				{
					"handle": "cherry-passion",
					"description": "Cherry Passion leans into a flirtier, more romantic cherry, with strawberry, almond and a lush floral heart of jasmine, rose and tuberose. The sandalwood and vanilla drydown make it soft, warm and very wearable."
				},
				{
					"handle": "jany",
					"description": "Jany is the one to choose if you want your red-fruit craving to feel cozy and nostalgic, with baked apple tart, cinnamon and buttery pastry. It’s deliciously comforting, like a dessert you can wear all day."
				},
				{
					"handle": "fraisi",
					"description": "Fraisi is a fun pick if you like strawberry scents with a twist, blending red berries and vanilla-almond cream with an unexpected herbal oud frame. It’s sweet, quirky and a little rebellious, which makes it memorable."
				}
			]
		},
		"tea-scents": {
			"title": "Tea Scents",
			"subtitle": "Elegant tea-inspired compositions",
			"category": "By Scent",
			"description": "For the tea lover treating yourself, these scents turn a quiet cup into something intimate, polished, and a little indulgent.",
			"products": [
				{
					"handle": "matcha-soft-serve",
					"description": "Matcha Soft Serve is perfect if you want your tea scent to feel like a reward: creamy matcha, vanilla, and waffle-cone warmth make it softly gourmand without losing its green calm."
				},
				{
					"handle": "rainy-city",
					"description": "Rainy City suits you if you love a composed, city-smart fragrance—black and green tea, vetiver, and transparent woods feel like a rainy walk with a warm cup in hand."
				},
				{
					"handle": "osmanthus-oolong",
					"description": "Osmanthus & Oolong is for the days you want something uplifting but gentle; apricot-floral osmanthus and savory oolong create a smooth, elegant tea scent with a quiet woody finish."
				},
				{
					"handle": "culot-the",
					"description": "Culot The is a playful pick if you like your comfort scents with a twist—sparkling green tea and jasmine are sharpened by ginger and wasabi for something fresh, witty, and unexpected."
				},
				{
					"handle": "tea-rock-n-roll",
					"description": "Tea & Rock ‘n‘ Roll works beautifully when you want tea with attitude: bergamot tea, smoky maté, and tonka give it a crisp, textured chic that feels polished but unruly."
				},
				{
					"handle": "leaf-crush",
					"description": "Leaf Crush is ideal if you’re drawn to the raw, green side of tea—bruised leaves, bitter tea, thyme, and yuzu make it vivid and energizing, with honey and mossy depth to round it out."
				},
				{
					"handle": "chaco",
					"description": "Chaco is a great choice if you prefer tea scents that stay light but feel refined; bergamot, petitgrain, and maté keep it bright, while cedar and guaiac add a softly smoky finish."
				},
				{
					"handle": "moonmilk",
					"description": "Moonmilk is for when you want tea to feel intimate and quietly sensual—black tea, lime, and pepper meet milky sandalwood and a tender leather nuance for a smooth, skin-close effect."
				},
				{
					"handle": "konya-konya-kolonyasi",
					"description": "Konya Kolonyasi suits you if you enjoy nostalgic freshness with character: bergamot and cherry sparkle up top, then soft black tea gives the whole scent a comforting, slightly bitter depth."
				}
			]
		},
		"incense-resin": {
			"title": "Incense & Resin",
			"subtitle": "Sacred, smoky, resinous compositions",
			"category": "By Scent",
			"description": "For the self-purchaser who loves a little ritual, these smoky resins feel like private ceremonies lit just for you.",
			"products": [
				{
					"handle": "osang",
					"description": "Osang is for when you want your fragrance to feel unapologetically intense: honeyed, myrrh-dark and animalic, with iris and incense keeping it beautifully unsettled. It’s a bold treat for evenings when you want to wear something with real presence."
				},
				{
					"handle": "liquid-incense",
					"description": "Liquid Incense suits someone who wants incense with a brighter, more luminous edge. Bergamot and mineral warmth make it feel like sunlight caught in resin, while oud and dry woods keep it elegant rather than heavy."
				},
				{
					"handle": "sand",
					"description": "Sand is a clever indulgence if you love sandalwood but want it to surprise you. The banana nuance and cardamom give it a playful lift, while incense, benzoin and labdanum keep the whole thing creamy, smoky and addictive."
				},
				{
					"handle": "moonlight-ceremony",
					"description": "Moonlight Ceremony is ideal if you’re drawn to dark, atmospheric scents that feel like a late-night escape. Birch tar, smoked tea and incense create a dramatic opening, then leather and amber soften it into something quietly luxurious."
				},
				{
					"handle": "sunken-tobacco",
					"description": "Sunken Tobacco feels like a moody, salt-air companion for someone who likes their scents earthy and a little raw. The tobacco and resins are dry and weathered, with juniper adding a green bite that keeps it from becoming too heavy."
				},
				{
					"handle": "berbera",
					"description": "Berbera is a strong choice if you want incense with depth, texture and a hint of old-world opulence. Frankincense, myrrh and opoponax give it a charred amber glow, making it especially satisfying when you want something powerful and layered."
				},
				{
					"handle": "volcano",
					"description": "Volcano is for the person who likes their smoke wrapped in something unexpectedly romantic. The rose adds a plush sweetness to the incense and woods, while ginger and nutmeg keep the whole composition warm, vivid and alive."
				},
				{
					"handle": "woodoo",
					"description": "Woodoo will suit you if you enjoy smoky scents that feel green, shadowy and a little untamed. Pepper, ginger and cedarwood give it spine, while iris and balsamic resins soften the edges into something quietly magnetic."
				},
				{
					"handle": "bois-dascse",
					"description": "Bois d’Ascese is the one to choose if you prefer incense that feels austere, contemplative and grown-up. The peaty whisky, frankincense and tobacco create a smouldering, dry warmth that lingers like a private moment of stillness."
				}
			]
		},
		"vanilla-beyond-vanilla": {
			"title": "Vanilla Beyond Vanilla",
			"subtitle": "Complex, layered vanilla compositions",
			"category": "By Scent",
			"description": "For the vanilla lover who wants more than sweetness, this collection wraps comfort in shadow, texture and surprise.",
			"products": [
				{
					"handle": "anti-blues",
					"description": "If you want vanilla with a darker, more seductive mood, Anti-Blues is the one to reach for. The chocolate, incense and tobacco make it feel grown-up and intimate, while the vanilla keeps it smooth enough to wear for yourself on quiet nights out."
				},
				{
					"handle": "mooncake",
					"description": "Mooncake | 月餅 is perfect if you’re drawn to vanilla with a nostalgic, edible softness. Its honeyed cake warmth and salted-egg richness make it feel like a little celebration you can keep close to skin."
				},
				{
					"handle": "vanilla-for-breakfast",
					"description": "Vanilla For Breakfast is for the days you want your perfume to feel generous and unmistakable. The four-vanilla blend, milky almond and roasted tonka give it a cozy, toasted richness that reads as pure indulgence."
				},
				{
					"handle": "vanille-creme",
					"description": "Vanille Creme suits you if you love a classic gourmand that feels plush and comforting. It leans into custard, caramel and buttery pastry notes, making it an easy self-treat when you want something sweet and enveloping."
				},
				{
					"handle": "bake",
					"description": "Bake is a bright choice if you like vanilla with a playful lift. The lemon cupcake effect keeps the sweetness lively, while praline and vanilla make it feel like a cheerful dessert with a polished finish."
				},
				{
					"handle": "far",
					"description": "Far is ideal if you prefer your vanilla airy rather than heavy. The yuzu and lime sparkle over a creamy biscuit base, giving you a fresh gourmand that feels clean, uplifting and quietly delicious."
				},
				{
					"handle": "god-bless-cola",
					"description": "God Bless Cola is for the person who likes their sweetness with personality. The cola fizz, caramel popcorn and vanilla create a fun, movie-night scent that feels a little nostalgic and very easy to enjoy for yourself."
				},
				{
					"handle": "vanille-glace",
					"description": "Vanille Glacé will suit you if you want vanilla with deeper, toastier edges. Brown sugar, cinnamon, espresso and sandalwood make it feel warm and textured, like a rich dessert with a sophisticated finish."
				},
				{
					"handle": "celestial-object",
					"description": "Celestial Object is a lovely pick if you want something soft, intimate and close to the skin. Its sugared almond, milky warmth and buttery rice-paper nuance create an understated gourmand that feels comforting without being loud."
				}
			]
		},
		"fig-green-herbaceous": {
			"title": "Fig, Green & Herbaceous",
			"subtitle": "Green, leafy, herbal compositions",
			"category": "By Scent",
			"description": "For the days you want to wear something fresh just for you, these green, leafy scents feel like a private stroll through herbs, figs, and sunlit gardens.",
			"products": [
				{
					"handle": "matcha-mist",
					"description": "Brume Matcha is a lovely self-indulgence if you want your scent to feel like a calm reset: matcha’s roasted softness, basil, and peppermint make it quietly uplifting, not loud."
				},
				{
					"handle": "a-grove-by-the-sea",
					"description": "A Grove By The Sea is for when you want your treat to feel transportive and a little more luxurious—fig leaf, rosemary, sea salt, and olive give it a sun-warmed, coastal richness."
				},
				{
					"handle": "play-green",
					"description": "Play Green suits you if you like your freshness crisp and straightforward, with mint, basil, and lime delivering an energizing burst that still feels polished and wearable."
				},
				{
					"handle": "you-or-someone-like-you",
					"description": "You or Someone Like You is a beautiful choice for someone who wants green to feel airy and modern; mint, shiso, and violet leaf create a clean, intriguing scent with real character."
				},
				{
					"handle": "in-this-world",
					"description": "In This World is ideal if you love the idea of fig but want it sheer and close to the skin—blood orange, sage, and soft woods make it feel intimate, easy, and quietly elegant."
				},
				{
					"handle": "fig-lemonade",
					"description": "Fig Lemonade is the one to reach for when you want your treat to feel bright and cheerful: juicy fig, lemon, and bergamot make it playful, while cedar keeps it grounded."
				},
				{
					"handle": "hakka-japanese-mint",
					"description": "Hakka (Japanese Mint) is a refreshing pick if you want something cool and delicate at once; the minty clarity is softened by jasmine, lotus, and a gentle musky finish."
				},
				{
					"handle": "masa",
					"description": "Masa feels like a more luminous, substantial green fragrance for treating yourself to something a little richer—grapefruit, ginger, and violet leaf glow into warm amber and woods."
				},
				{
					"handle": "provence",
					"description": "Provence is perfect if you’re drawn to herbal scents with a refined, comforting edge; lavender, clary sage, spearmint, and woods create a classic fougère that lingers beautifully."
				}
			]
		},
		"salt-sea-mineral": {
			"title": "Salt, Sea & Mineral",
			"subtitle": "Marine, mineral, salty compositions",
			"category": "By Scent",
			"description": "For the self-purchaser who craves a little coastal escape, these salt-sprayed scents bring sea air, mineral skin and sunlit calm straight to you.",
			"products": [
				{
					"handle": "entre-ciel-et-mer",
					"description": "Entre Ciel et Mer feels like a private walk along a windswept shoreline, with icy algae, sea salt and ambergris giving you that clean, quietly animalic sea-spray aura. If you love marine scents with a refined woody drydown, this is a beautifully grown-up treat."
				},
				{
					"handle": "falaise-49-5-n",
					"description": "Falaise 49.5°N is perfect if you want your freshness with a little juiciness and texture: tart blackberry and blackcurrant bud make the salt and pebbles feel vivid rather than austere. It wears like late summer on a cliff path, bracing but still deliciously wearable."
				},
				{
					"handle": "sous-le-pont-mirabeau",
					"description": "Sous Le Pont Mirabeau suits someone who wants a softer, more romantic take on the mineral theme. Fig, violet leaf and airy aquatic notes drift into cedar, sandalwood and incense, creating the feeling of a quiet Paris riverside moment just for you."
				},
				{
					"handle": "beso-de-sal",
					"description": "Beso de Sal is the one to choose when you want your marine fragrance bright and uplifting rather than strictly briny. Lemon, mandarin, sage and minty shiso give it a sparkling island energy, while cedar and musk keep it polished and easy to wear."
				},
				{
					"handle": "breathe-out-01-02",
					"description": "Breathe Out 01.02 is ideal if you like your freshness to feel crisp, contemplative and a little unusual. The cold air, salty steam and incense make it feel almost meditative, with frangipani and sandalwood adding enough warmth to keep it comforting on skin."
				},
				{
					"handle": "sydney-rock-pool",
					"description": "Sydney Rock Pool is a gorgeous pick if you want something that feels sun-warmed and skin-close rather than sharp. Coconut, driftwood, sea salt and sandalwood create that effortless after-swim glow, like carrying a holiday memory with you all day."
				},
				{
					"handle": "sal-y-limon",
					"description": "Sal y Limón is for the person who wants instant brightness and a cheerful salty lift. Fizzy citrus and sea-salt accord make it sparkling and easygoing, while orange blossom and musk soften it into something that feels fresh, modern and very wearable."
				},
				{
					"handle": "803",
					"description": "803 Givre Patchouli is a smart choice if you love patchouli but want it cleaned up with a frosted, marine edge. The icy mineral opening gives the earthy heart a striking contrast, making it feel both cool and grounded at once."
				},
				{
					"handle": "playground-1",
					"description": "Playground is a lovely option if you prefer your marine scents airy, soft and quietly comforting. Bergamot, jasmine and honeydew melon keep it transparent and breezy, while cashmere wood and musk make it feel like a gentle second skin."
				}
			]
		},
		"oud-collection": {
			"title": "Oud Collection",
			"subtitle": "The king of perfumery ingredients",
			"category": "More Scents",
			"description": "For the self-gifter drawn to depth, drama, and a little danger, these oud scents turn your own skin into the main event.",
			"products": [
				{
					"handle": "vi-et-armis",
					"description": "If you want your treat to feel unapologetically bold, Vi et Armis gives you smoky tea, pepper, and incense over tar-dark woods for a moody, intellectual kind of power. It suits someone who likes their fragrance to challenge as much as charm."
				},
				{
					"handle": "osang",
					"description": "Osang is the one to choose when you want richness with a wild pulse: fenugreek, honey, myrrh, and animalic warmth make it feel lavish and slightly untamed. It’s a beautiful pick if you love dark scents that seem to move on the skin."
				},
				{
					"handle": "565",
					"description": "565 is for the days you want oud’s depth with a more glamorous, crowd-pleasing shine. The candied bergamot and fruit wrapped in amber make it feel indulgent and sensual without losing its shadowy edge."
				},
				{
					"handle": "al-sarab",
					"description": "Al Sarab feels like a private luxury: intense oud oils, leather, and earthy woods softened by a hint of fruit for a mirage-like softness. It’s ideal if you want something serious, enveloping, and quietly opulent."
				},
				{
					"handle": "rouge-karkade",
					"description": "Rouge Karkade gives oud a vivid, wearable twist with hibiscus tea, pomegranate, vanilla, and smoky cedar. Choose it if you want something lush and expressive that still feels elegant rather than heavy."
				},
				{
					"handle": "tiger-rouge",
					"description": "Tiger Rouge is for the wearer who likes a fragrance with bite and brilliance: pepper, clove, citrus, rose, and oud create a fierce, polished heat. It’s especially suited to someone who enjoys a bold entrance and a lingering trail."
				},
				{
					"handle": "agarwood",
					"description": "Agarwood offers a more refined, temple-like oud experience, with incense, cedar, and vetiver keeping it serene and balanced. It’s a lovely self-purchase if you prefer restraint, texture, and quiet sophistication."
				},
				{
					"handle": "black-afgano",
					"description": "Black Afgano is the decadent, enveloping choice for nights when you want your fragrance to feel almost velvety with smoke, coffee, tobacco, and oud. It suits someone who loves dark, substantial perfumes that leave a lasting impression."
				},
				{
					"handle": "liquid-incense",
					"description": "Liquid Incense is the softer, golden side of oud: luminous resin, bergamot, dry woods, and a touch of animalic depth. It’s perfect if you want incense with elegance and warmth rather than ash and heaviness."
				}
			]
		},
		"rose-lovers": {
			"title": "Rose Lovers",
			"subtitle": "Every shade of rose, from dewy to dark",
			"category": "More Scents",
			"description": "For the rose devotee who shops for pleasure, this collection lets you wear every mood of the flower—from fresh morning dew to velvet-dark dusk.",
			"products": [
				{
					"handle": "in-principio-v2",
					"description": "In principio V2 is for the days you want your rose to feel refined and quietly luxurious: the lily, ylang-ylang and ambered woods create a contemplative floral glow that lingers close to you. It suits someone who loves elegance with texture, not sweetness alone."
				},
				{
					"handle": "rose-shiso",
					"description": "Rose Shiso is a brilliant pick if you like your roses freshened with a green, savory twist. The shiso, cedar and sandalwood keep the rose crisp and modern, making it perfect for a self-gift that feels uplifting and a little unexpected."
				},
				{
					"handle": "asoral",
					"description": "Asoral is the rose for someone who wants softness without losing character: dewy petals, creamy magnolia and musks give it a plush, skin-like charm. The tea and hay nuances add a delicate depth that feels intimate and beautifully wearable."
				},
				{
					"handle": "musc-oli",
					"description": "Musc Oli is ideal if you’re drawn to the idea of rose as a whisper on skin rather than a bouquet. Its musk, cashmeran and sandalwood make the rose feel clean, warm and quietly sensual—an effortless everyday indulgence."
				},
				{
					"handle": "ensang-noir",
					"description": "Ensang Noir is the one to choose when you want your rose darker, smokier and more dramatic. The aldehydes, opoponax and patchouli give it a vintage, ink-stained intensity that feels bold and unforgettable."
				},
				{
					"handle": "rosi-rosa",
					"description": "Rosi Rosa is a playful rose for someone who likes a touch of whimsy with their florals. Raspberry and fir brighten the petals, while the hinoki and woods keep it crisp and woodland-fresh rather than sugary."
				},
				{
					"handle": "experimentum-crucis",
					"description": "Experimentum Crucis suits a rose lover who enjoys a statement scent with sparkle and bite. Apple, honey and cumin make the rose vivid and unexpected, while patchouli and musk give it the kind of depth that turns heads."
				},
				{
					"handle": "narcotic-v",
					"description": "Narcotic V is for the rose admirer who also loves lush white florals and unapologetic sensuality. Though tuberose leads, its heady floral richness feels decadent and enveloping, like a treat you wear purely for yourself."
				},
				{
					"handle": "rose-cardamome",
					"description": "Rose Cardamome is a gorgeous choice if you want your rose to feel cool, dark and slightly spiced. Cardamom, violet leaf and blackcurrant sharpen the petals, while leather and ambergris give it a supple, grown-up finish."
				}
			]
		},
		"white-flowers": {
			"title": "White Flowers",
			"subtitle": "Tuberose, jasmine, gardenia and intoxicating blooms",
			"category": "More Scents",
			"description": "For the days you want to indulge yourself, these white florals bloom with creamy, luminous sensuality and a little irresistible drama.",
			"products": [
				{
					"handle": "delirium",
					"description": "Delirium is the richest treat in the lineup: its gardenia, jasmine and tuberose feel plush and decadent, with vanilla and raw sugar adding a soft, self-indulgent glow. If you want a fragrance that feels like saying yes to pleasure, this is it."
				},
				{
					"handle": "mathilde",
					"description": "Mathilde is a beautifully rounded floral for someone who loves elegance with a twist of fruit. The jasmine and ylang-ylang are radiant, while passion fruit and papaya give it a lush, painterly sweetness that feels special without being overpowering."
				},
				{
					"handle": "narcotic-v",
					"description": "Narcotic V is for when you want your white florals to be bold, creamy and unapologetic. Tuberose leads the way with jasmine and orange blossom in full bloom, making it a confident, sensual choice that wears like a statement."
				},
				{
					"handle": "bo-bo",
					"description": "Bo-Bo suits you if you want your florals to feel lighter, brighter and easy to wear every day. The mandarin and lily of the valley keep it fresh and relaxed, while musk and vetiver give it a clean finish that still feels polished."
				},
				{
					"handle": "fleur-de-rocaille",
					"description": "Fleur de Rocaille is ideal if you love a classic floral bouquet with a touch of old-world glamour. Its aldehydic sparkle and ylang-ylang warmth make it feel airy yet luxurious, like a beautiful dress you reach for just because."
				},
				{
					"handle": "suze",
					"description": "Suze is the most intriguing self-gift here: minty, floral and softly smoky, it feels unusual in the best way. The jasmine and magnolia are wrapped in tea, woods and amber, giving you something quietly memorable and full of character."
				},
				{
					"handle": "in-principio-v2",
					"description": "In principio V2 is a wonderful pick if you want your white florals to feel creamy, textured and contemplative. The lily and ylang-ylang are enriched by ambergris, resins and woods, creating a fragrance that feels intimate and deeply composed."
				},
				{
					"handle": "jasmin-ylang",
					"description": "Jasmin & Ylang is the easygoing pleasure buy for a sunlit floral mood. Jasmine, ylang-ylang and frangipani are cushioned by vanilla and benzoin, so it feels comforting, radiant and effortlessly wearable."
				},
				{
					"handle": "fathom-v",
					"description": "Fathom V is for the person who likes their florals with depth and a little mystery. The lily and mimosa drift through sea salt, green leaves and vetiver, giving you a moody, mossy floral that feels vivid and uncommon."
				}
			]
		},
		"boozy-scents": {
			"title": "Boozy Scents",
			"subtitle": "Whisky, rum, cognac and after-hours warmth",
			"category": "More Scents",
			"description": "Pour yourself into the velvet hour with boozy scents that feel like a private toast after dark.",
			"products": [
				{
					"handle": "drunk-saffron",
					"description": "DRUNK SAFFRON is for the moment you want warmth with a little glamour: cognac, saffron and leather feel effortlessly sensual, while vanilla and patchouli keep it plush on skin."
				},
				{
					"handle": "tokyo-nights",
					"description": "Tokyo Nights | ナイト suits you if you like your indulgence quieter and more poetic, with plum wine, tobacco and osmanthus creating a smoky-fruity haze that feels intimate rather than loud."
				},
				{
					"handle": "chateau-de-velours",
					"description": "Château de Velours is a delicious choice when you want a scent that feels like dessert and red wine at once; the berries and boozy davana are softened by chocolate and honey, then grounded by mossy depth."
				},
				{
					"handle": "ziveli",
					"description": "Živeli is made for someone who wants their sweetness with a sharper edge, as plum brandy, raspberry and honeyed leather read rich and convivial without losing their dry, textured sophistication."
				},
				{
					"handle": "or-du-srail",
					"description": "Or du Sérail wraps you in golden excess: rum, tobacco, mango and candied fruit make it feel generous and festive, like the most luxurious treat you could keep for yourself."
				},
				{
					"handle": "celestial-object",
					"description": "Celestial Object is ideal if you prefer boozy warmth translated into something airy and close to the skin, with almond, milk and tonka giving you a soft, comforting glow."
				},
				{
					"handle": "far",
					"description": "Far offers a brighter kind of self-indulgence, turning yuzu and lime into a creamy citrus dessert that feels uplifting, clean and still satisfyingly sweet."
				},
				{
					"handle": "milk",
					"description": "Milk | Personal is a lovely pick when you want comfort that stays discreet, because marshmallow, tonka and soft woods create a sheer, skin-close warmth that feels personal and easy."
				},
				{
					"handle": "dark-honey",
					"description": "Dark Honey is for the unapologetic gourmand lover: rum, plum liqueur and dark vanilla make it lush and intoxicating, while animalic honey gives it a wild, addictive edge."
				}
			]
		},
		"coffee-fragrances": {
			"title": "Coffee Fragrances",
			"subtitle": "Roasted, rich, caffeine-fuelled scents",
			"category": "More Scents",
			"description": "For the coffee lover who wants their treat to feel like a private ritual, these scents turn roasted warmth into something deliciously indulgent.",
			"products": [
				{
					"handle": "vanille-glace",
					"description": "Vanille Glacé is perfect if you want your coffee note wrapped in dessert-like comfort: espresso keeps the vanilla and cinnamon from becoming too sweet, while the sandalwood and musk make it feel polished on skin."
				},
				{
					"handle": "pg10-aomassa",
					"description": "PG10 Aomassaï suits you if you like your gourmand with a darker edge. The roasted coffee and caramel are tempered by incense and scorched woods, giving you a smoky, ember-like richness that feels grown-up and compelling."
				},
				{
					"handle": "roissant-afe",
					"description": "Сroissant Сafe is a lovely choice when you want something cozy, intimate, and easy to wear. The cappuccino-and-pastry opening feels like a slow French breakfast, with soft woods and tonka leaving a warm, quietly sensual trail."
				},
				{
					"handle": "vietnamese-coffee",
					"description": "Vietnamese Coffee is for the days you want your fragrance to feel bold but comforting. The condensed milk sweetness is balanced by bitter chocolate and roast, so it lands as rich, creamy, and beautifully addictive rather than cloying."
				},
				{
					"handle": "cacao-pimento",
					"description": "Cacao Pimento will suit you if you love your coffee with spice and depth. Saffron, rum, allspice and tobacco give the cacao a glowing warmth, while the coffee and sandalwood make it feel lush, textured, and memorable."
				},
				{
					"handle": "deer",
					"description": "Deer is a strong pick if you prefer coffee notes that lean earthy and rugged rather than sweet. Green coffee, hay, leather and cedar create a dry woodland character that feels quietly powerful and very self-assured."
				},
				{
					"handle": "sugar-addict",
					"description": "SUGAR ADDICT is ideal when you want full-on indulgence with a darker twist. The cocoa, coffee and rum are wrapped in vanilla and cashmere wood, making it feel plush, addictive, and unapologetically decadent."
				},
				{
					"handle": "23-59",
					"description": "23:59 is the one to choose if you want your coffee fragrance to feel like evening itself. Espresso, cognac and crème brûlée melt into smoky woods and vanilla, creating a toasted, elegant warmth that lingers beautifully."
				},
				{
					"handle": "sadonaso",
					"description": "Sadonaso is for you if you like coffee scents that feel intimate and a little provocative. Coffee, tobacco and tonka soften into sandalwood and powdery warmth, giving it a sensual, skin-close richness that feels deeply personal."
				}
			]
		},
		"chocolate-cocoa": {
			"title": "Chocolate & Cocoa",
			"subtitle": "Dark, rich, dessert-like indulgence",
			"category": "More Scents",
			"description": "For the nights you want to spoil yourself, these chocolate-rich scents turn dessert into something dark, sensual, and beautifully addictive.",
			"products": [
				{
					"handle": "anti-blues",
					"description": "If you want your indulgence with a smoky, mysterious edge, Anti-Blues is the one to reach for. Its dark chocolate, incense and tobacco feel luxurious and a little dangerous, like a treat with secrets."
				},
				{
					"handle": "agrani",
					"description": "Agrani suits you if you love gourmand notes that feel opulent rather than sugary. Cherry, almond and cacao are deepened by leather, oud and tobacco, giving you a rich, commanding scent that lingers beautifully."
				},
				{
					"handle": "pg04-musc-maori",
					"description": "Musc Maori is perfect when you want chocolate comfort that stays soft and close to the skin. The cocoa-milk sweetness is lifted by bergamot and white musk, making it feel intimate, luminous and easy to wear."
				},
				{
					"handle": "boccanera",
					"description": "Boccanera is for the self-purchaser who likes dessert notes with bite. The dark chocolate and cocoa are sharpened by ginger and wrapped in an animalic warmth, creating a sensual scent that feels bold and unforgettable."
				},
				{
					"handle": "07-orris-cacao",
					"description": "07 Orris+Cacao will appeal if you prefer your sweetness understated and textured. Its cocoa and orris combination feels powdery, mineral and quietly addictive, like chocolate dusted over rain-warmed stone."
				},
				{
					"handle": "chateau-de-velours",
					"description": "Château de Velours is a lovely choice if you want your gourmand to feel plush and romantic. The berry-and-wine opening melts into dark chocolate and honey, giving you a velvety, indulgent scent with earthy depth."
				},
				{
					"handle": "tabac-exquis",
					"description": "Tabac Exquis is ideal if you want a classic-feeling luxury gourmand with a polished finish. The cocoa and tobacco accord is softened by iris and benzoin, making it rich, smoky and elegantly wearable."
				},
				{
					"handle": "mezcal-chocolat",
					"description": "Mezcal Chocolat is for when you want your treat to feel a little wild. Lime, chili and mezcal give the dark chocolate a spirited, smoky kick, so it wears like a daring dessert with a boozy glow."
				},
				{
					"handle": "cacao-pimento",
					"description": "Cacao Pimento is the pick for someone who wants warmth, spice and plenty of presence. Rum, allspice and coffee wrap the cacao in a generous woody sweetness that feels comforting, sensual and long-lasting."
				}
			]
		},
		"leather-suede": {
			"title": "Leather & Suede",
			"subtitle": "Tough, textured, skin-like compositions",
			"category": "More Scents",
			"description": "For the days you want to feel a little tougher, a little richer, and entirely your own, these leather-and-suede scents wrap you in texture with attitude.",
			"products": [
				{
					"handle": "hey-bitch",
					"description": "If you want a bold signature that still feels plush on skin, Hey Bxtch delivers: tart rhubarb and orange blossom make the leather sparkle before it melts into suede, sandalwood and vanilla. It’s sensual, assertive, and perfect for treating yourself to something with real presence."
				},
				{
					"handle": "almond-suede",
					"description": "Almond Suede is the indulgent one for when you want comfort with polish. The marzipan-and-suede pairing feels warm and expensive, while neroli, rosemary and saffron keep it from becoming too sweet."
				},
				{
					"handle": "flight-mode",
					"description": "Flight Mode suits the person who likes their leather scent airy rather than heavy. Smoky guaiac and suede give it backbone, but lime, lavender and vanilla keep it clean, modern and easy to wear."
				},
				{
					"handle": "orange-patina",
					"description": "Orange Patina is for when you want something playful but still textured. Juicy orange and watermelon give it a bright, almost surreal opening, then leather and guaiac wood pull it into a worn-in, skin-close finish."
				},
				{
					"handle": "al-sarab",
					"description": "Al Sarab is the most dramatic choice here, ideal if you want your fragrance to feel deep, mysterious and a little untamed. The oud-rich leather character is intense, but the fruity nuances keep it from feeling too severe."
				},
				{
					"handle": "iris-tannat",
					"description": "Iris Tannat is for someone who loves elegance with a stern edge. Velvety iris, tannic wine and weathered oak create a dry, sophisticated texture that feels classic, moody and unmistakably confident."
				},
				{
					"handle": "malt-night-fighter",
					"description": "No. 10 – Night Warrior is a great pick if you’re drawn to dark florals with a smoky, candlelit mood. The clove heat, toasted malt and smoked tea give it depth, while the softer floral notes keep it intriguing rather than harsh."
				},
				{
					"handle": "cuoium",
					"description": "Cuoium is the most rugged, uncompromising option in the set. Its smoky animalic leather and green violet leaf create a tactile, forceful scent that feels raw, confident and beautifully untamed."
				},
				{
					"handle": "kar-wai",
					"description": "Kar-Wai is perfect if you want leather with a softer, more intimate pulse. Smoked tea, osmanthus and rose create a sensual tension, making it feel refined, romantic and quietly magnetic."
				}
			]
		},
		"powdery-iris": {
			"title": "Powdery & Iris",
			"subtitle": "Soft, sophisticated iris and powdery elegance",
			"category": "More Scents",
			"description": "Slip into soft iris and powdery elegance—these are the scents that feel like your best-kept secret, chosen just for you.",
			"products": [
				{
					"handle": "alyazia",
					"description": "Alyazia is for when you want your treat to feel plush, feminine, and a little nostalgic, with violet, iris, and orange blossom wrapped in vanilla warmth. The wild strawberry gives it a charming spark, like a powder compact with a playful wink."
				},
				{
					"handle": "almaz",
					"description": "Almaz suits you if you love powdery iris but want it brightened by juicy fruit and a glossy, modern sweetness. Blackcurrant and raspberry keep it vivid, while musk and brown sugar make it feel enveloping and indulgent on skin."
				},
				{
					"handle": "afrique-en-fleurs",
					"description": "Afrique en Fleurs is a lovely pick if you want your iris to feel sunlit rather than shy, with orange and ginger lifting the floral heart. It dries down creamy and warm, giving you that elegant contrast between freshness and softness."
				},
				{
					"handle": "baise-moi-on-the-dancefloor",
					"description": "Baise Moi On The Dancefloor is ideal if you like your powdery scents with a nightlife edge—smudged lipstick, cashmeran, and sandalwood make it feel intimate and effortlessly cool. It’s soft, but never sleepy."
				},
				{
					"handle": "les-cahiers-secrets",
					"description": "Les Cahiers Secrets feels especially personal, like a fragrance you choose when you want quiet sensuality rather than obvious glamour. Orris and heliotrope stay tender and powdery, while cumin and musk add a flushed, skin-close warmth."
				},
				{
					"handle": "iris-cendr",
					"description": "Iris Cendré is for the iris lover who prefers something refined, austere, and beautifully vintage-tinged. Its incense, tobacco, and amber give the powdery core a smoky depth that feels elegant and self-assured."
				},
				{
					"handle": "merina",
					"description": "Merina is a beautiful choice if you want your iris wrapped in something creamy, comforting, and quietly luxurious. The milky almond and fennel make it feel soft and distinctive, like a refined second skin."
				},
				{
					"handle": "intime-extime",
					"description": "Intime-Extime is a great self-purchase if you want a powdery iris that feels intimate, fresh, and softly cocooning. The milk accord and vanilla make it comforting, while white tea and incense keep it polished."
				},
				{
					"handle": "iris-coriandre",
					"description": "Iris Coriandre will suit you if you enjoy iris with a more tailored, vintage attitude and a subtle savory twist. Bergamot and coriander sharpen the powdery florals, while osmanthus adds a gentle apricot-leather warmth."
				}
			]
		},
		"lavender-reimagined": {
			"title": "Lavender Reimagined",
			"subtitle": "Modern, unexpected takes on lavender",
			"category": "More Scents",
			"description": "If you’re treating yourself, this is lavender with a twist—fresh, smoky, marine, and unexpectedly luxurious, made to feel like a private indulgence.",
			"products": [
				{
					"handle": "ara",
					"description": "ARС is for the days you want lavender to feel textured and grown-up: its herbal, powdery fougère depth has a tactile richness that feels like a reward you can wear."
				},
				{
					"handle": "oblivion",
					"description": "Oblivion suits someone who loves lavender but wants it sharpened and polished—citrus brightness, soft woods, and iris-like powder make it quietly elegant and easy to reach for."
				},
				{
					"handle": "entre-ciel-et-mer",
					"description": "Entre Ciel et Mer is a striking choice if you want lavender in a colder, more dramatic mood; the salty marine air and mineral woods give it a chic, skin-close edge."
				},
				{
					"handle": "pour-un-homme",
					"description": "Pour Un Homme de Caron is the comforting self-purchase here: classic lavender softened with vanilla and musk, it feels clean, warm, and reassuringly timeless."
				},
				{
					"handle": "provence",
					"description": "Provence is ideal if you want lavender to feel modern and herbaceous rather than nostalgic, with minty freshness and a smooth woody finish that stays composed all day."
				},
				{
					"handle": "rusalka-s-embrace",
					"description": "Rusalka’s Embrace is for the bold lavender lover who prefers atmosphere over prettiness; its seaweed, ambergris, and resinous musk create a haunting, intimate trail."
				},
				{
					"handle": "brume-dete",
					"description": "Brume d'été is the easiest everyday treat in the collection—light, citrusy, and gently herbal, it gives you that fresh-out-of-the-shower feeling without trying too hard."
				},
				{
					"handle": "flight-mode",
					"description": "Flight Mode suits a self-purchaser who likes their lavender with an edge: the smoky suede and clean lift make it feel sleek, modern, and slightly offbeat."
				},
				{
					"handle": "forbidden-rouge",
					"description": "Forbidden Rouge is the most decadent take here, pairing lavender with dark berries and saffron for a plush, melancholic sweetness that feels special rather than loud."
				}
			]
		},
		"made-in-britain": {
			"title": "Made in Britain",
			"subtitle": "The best of British niche perfumery",
			"category": "By Origin",
			"description": "Treat yourself to a distinctly British scent wardrobe, where rugged coastlines, tea rooms and tailored woods feel polished, personal and a little bit daring.",
			"products": [
				{
					"handle": "cape-wrath",
					"description": "Cape Wrath is for the days you want to feel sharpened by the sea: its salty vetiver, oakmoss and resinous incense give you that windswept, confident edge. A brilliant pick if you like your green scents austere and memorable."
				},
				{
					"handle": "afternoon-tea",
					"description": "Afternoon Tea feels like self-care with a clever twist, pairing bright tea and lemon with floral richness and a smoky vetiver hum. It suits you if you want something elegant, comforting and just a little unexpected."
				},
				{
					"handle": "guava-sorbet",
					"description": "Guava Sorbet is the fragrance equivalent of a well-earned escape, with juicy tropical fruit, soft florals and a creamy finish that stays inviting on skin. Choose it when you want your perfume to feel sunny, indulgent and easy to love."
				},
				{
					"handle": "montabaco",
					"description": "Montabaco suits a person who wants sophistication without heaviness: tea, cardamom, leather and tobacco create a textured, quietly sensual trail. It’s ideal if you like your perfumes polished, modern and effortlessly expensive-feeling."
				},
				{
					"handle": "mulberry-fig",
					"description": "Mulberry Fig is a lovely choice if you want something refined but soft-focus, with leafy fig, creamy coconut and warm woods giving a graceful, park-side calm. It wears like a gentle luxury you can reach for often."
				},
				{
					"handle": "a-masquerade",
					"description": "A Masquerade is for when you want your scent to feel a little dramatic and utterly charming, with raspberry, tuberose, leather and rum creating a layered evening glow. It’s a great self-purchase if you enjoy perfumes with intrigue and depth."
				},
				{
					"handle": "tea-rock-n-roll",
					"description": "Tea & Rock ‘n‘ Roll is crisp British chic with a rebellious streak, blending tea, smoky maté and tonka into something clean, textured and cool. Pick it if you want an everyday signature that still feels interesting up close."
				},
				{
					"handle": "monkeyflower",
					"description": "Monkeyflower is wonderfully easygoing and nostalgic, with chamomile, fig, hay and a sunscreen warmth that feels like golden skin after a slow afternoon. It suits you if you prefer soft, sunny scents that feel comforting rather than loud."
				},
				{
					"handle": "arashiyama",
					"description": "Arashiyama is perfect if you’re drawn to quiet, contemplative fragrances: bamboo, rain-soaked earth and violet create a serene green atmosphere. It’s a beautiful choice for someone who likes subtle elegance and a sense of stillness."
				}
			]
		},
		"french-perfumery": {
			"title": "French Perfumery",
			"subtitle": "The art of French fragrance-making",
			"category": "By Origin",
			"description": "For the self-gifter who loves French elegance with a twist, this collection turns every spritz into a small, beautifully chosen indulgence.",
			"products": [
				{
					"handle": "monsieur",
					"description": "Monsieur feels like a private walk through a rain-dark forest in tailored clothes—woody, mineral, and quietly sensual. If you like depth without heaviness, this is a polished signature to keep for yourself."
				},
				{
					"handle": "patchouli-labdanum",
					"description": "Patchouli & Labdanum is the easygoing way to wear patchouli: earthy, smoky, and warmly resinous, but never overpowering. It suits you if you want something grounded and confident that still feels effortless day to day."
				},
				{
					"handle": "entre-ciel-et-mer",
					"description": "Entre Ciel et Mer is the scent equivalent of sea air on bare skin—salty, mineral, and beautifully fresh, with a soft animalic depth underneath. It suits you if you love cleanness that still feels sophisticated and alive."
				},
				{
					"handle": "pg-111-indian-wood",
					"description": "PG11.1 Indian Wood gives you that chic French contrast of cool mint and warm spice, then settles into sandalwood and vetiver with calm assurance. It’s a great pick if you enjoy fragrances that feel relaxed, textured, and subtly seductive."
				},
				{
					"handle": "302",
					"description": "302 Groovy Amber is for treating yourself to something plush and comforting, with amber richness lifted by coconut and cardamom. It’s sensual and enveloping, ideal when you want a scent that feels like a little luxury worn close to the skin."
				},
				{
					"handle": "tea-rock-n-roll",
					"description": "Tea & Rock ‘n‘ Roll has the charm of a refined tea ritual with a rebellious smoky edge, making it wonderfully distinctive without losing elegance. Choose this if you want something crisp, cool, and a bit unexpected on your own terms."
				},
				{
					"handle": "coton-egyptien",
					"description": "Coton Egyptien is perfect if your self-care style leans to effortless cleanliness with a soft, airy finish. Its musks and green freshness make it feel like freshly pressed linen, but with enough presence to linger beautifully."
				},
				{
					"handle": "intime-extime",
					"description": "Intime-Extime wraps iris, incense, and vanilla into a skin-close haze that feels intimate, elegant, and quietly comforting. It’s a lovely choice if you want something powdery and refined that still feels deeply personal."
				},
				{
					"handle": "lumieres",
					"description": "Lumières shines with urban brightness—pink pepper, juniper, iris, and musk creating a radiant, polished skin scent. If you want something modern, airy, and subtly sensual, this is a beautiful way to wear French restraint with glow."
				}
			]
		},
		"japanese-scents": {
			"title": "Japanese Scents",
			"subtitle": "Minimalist beauty from Japan",
			"category": "By Origin",
			"description": "Treat yourself to scents that feel quietly refined, fresh as a tea room, and beautifully intimate on your skin.",
			"products": [
				{
					"handle": "matcha-soft-serve",
					"description": "If you love soft comfort with a polished edge, this is the one to wear when you want to smell like your favorite matcha dessert—creamy, green, and gently addictive."
				},
				{
					"handle": "ramune",
					"description": "A bright little mood-lifter for days when you want something playful and airy; it wears like a nostalgic summer sip with just enough musky softness to feel grown-up."
				},
				{
					"handle": "resonant",
					"description": "For when you want your self-purchase to feel modern and a bit cool, this crisp citrus-green scent brings a sleek, mineral freshness that feels effortlessly composed."
				},
				{
					"handle": "leaf-crush",
					"description": "This is a beautiful pick if you’re drawn to green scents with texture—leafy, bitter, and alive, but softened just enough to stay wearable and elegant."
				},
				{
					"handle": "you-or-someone-like-you",
					"description": "Choose this if you like understated freshness with a clever twist; the minty shiso and violet leaf give it a clean, intriguing character that feels quietly distinctive."
				},
				{
					"handle": "oblivion",
					"description": "A soothing, polished option for someone who likes their fragrances calm and refined; the citrus and lavender sparkle first, then settle into a powdery woodiness that feels timeless."
				},
				{
					"handle": "shikoku-cologne",
					"description": "If you want a cologne that feels like a deep breath on a clear day, this one offers bright yuzu and ginger with a meditative woody haze that’s both energizing and serene."
				},
				{
					"handle": "naju-spring",
					"description": "This is for the scent lover who enjoys sweetness with a savory edge—matcha, pear, and toasted rice make it unusual and cozy, with a memorable finish that lingers."
				},
				{
					"handle": "still-life-in-rio",
					"description": "A luminous choice if you want something cheerful but not simple; the yuzu-coconut warmth feels sunlit and relaxed, with a smooth, softly sweet trail that’s easy to love."
				}
			]
		},
		"italian-houses": {
			"title": "Italian Houses",
			"subtitle": "Italian craftsmanship and Mediterranean soul",
			"category": "By Origin",
			"description": "For the days you want to spoil yourself, these Italian-crafted scents bring sun-warmed beauty, smoky depth, and a little Mediterranean drama.",
			"products": [
				{
					"handle": "195-a-c",
					"description": "If you love a fragrance that feels bright but still grown-up, 195 A.C. gives you juicy fig and green freshness with a smoky incense finish, like a shaded orchard in Pompeii. It’s ideal when you want something uplifting that still feels luxurious on skin."
				},
				{
					"handle": "sirio",
					"description": "Sirio is a lovely choice if you want your treat-yourself scent to feel plush and romantic without being too sweet. The tart fruit, rose, and white musk are wrapped in oud, guaiac wood, and vanilla for a soft but richly textured finish."
				},
				{
					"handle": "baraonda",
					"description": "Baraonda is for evenings when you want your perfume to feel like the reward at the end of the day. Whisky, honey, and amber create a warm, boozy glow that feels indulgent, relaxed, and a little seductive."
				},
				{
					"handle": "cuoium",
					"description": "Cuoium suits you if you’re drawn to bold, tactile scents with real presence. Its smoky leather and violet leaf give it a rugged elegance that feels confident and uncompromising, like a statement piece you wear for yourself."
				},
				{
					"handle": "sacreste",
					"description": "Sacreste is a beautiful pick if you want something contemplative and quietly enveloping. The incense, saffron, and cedarwood feel refined and meditative, with a soft woody texture that sits close and comforting."
				},
				{
					"handle": "fig-lemonade",
					"description": "Fig Lemonade is perfect if your self-gift needs to feel cheerful, easy, and instantly wearable. The sparkling fig-and-citrus profile has a playful Mediterranean brightness, with just enough cedarwood to keep it polished."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"description": "V.I.T.R.I.O.L. is only for the moment you want to go all in on something provocative and unforgettable. Its abrasive animalic texture is intentionally challenging, making it a daring choice for someone who likes perfume with teeth."
				},
				{
					"handle": "via-dell-incenso",
					"description": "Via dell’Incenso is a great match if you enjoy incense scents that feel clean, cool, and composed rather than heavy. Pine, lemony elemi, and smoky guaiac wood give it a serene, reflective character that feels beautifully put together."
				},
				{
					"handle": "bois-dascse",
					"description": "Bois d’Ascese is the one to choose if you want your fragrance to feel austere, smoky, and quietly luxurious. The peaty whisky, frankincense, and tobacco create a sober, elegant mood that lingers like a private ritual."
				}
			]
		},
		"middle-eastern-luxury": {
			"title": "Middle Eastern Luxury",
			"subtitle": "Oud, saffron, rose and opulent amber",
			"category": "By Origin",
			"description": "For the moments you want to spoil yourself, these oud-rich treasures drape saffron, rose and amber in pure, luxurious warmth.",
			"products": [
				{
					"handle": "vettii",
					"description": "If you love a fragrance that starts bright and then sinks into something more sensual, Vettii is a beautiful self-gift. The fig and quince opening feels polished and uplifting before saffron, myrrh and oud turn it into a quietly addictive, enveloping trail."
				},
				{
					"handle": "arabesque",
					"description": "Arabesque is for the days you want to feel composed, lavish and a little mysterious. Its saffron-and-rose heart, darkened by oud and patchouli, gives you that rich, decorative opulence without ever feeling heavy."
				},
				{
					"handle": "ruby-wood",
					"description": "Ruby Wood suits you if you prefer your luxury a little earthier and more intimate. The rose, frankincense and balsamic woods create a warm autumnal glow, with the coffee nuance adding a comforting, grown-up depth."
				},
				{
					"handle": "au-pied-du-rosier",
					"description": "Au Pied du Rosier is a bold choice when you want your rose to feel modern and cheeky. Honeyed floral softness meets saffron and oud, so you get contrast, projection and a sensual edge that feels unmistakably special."
				},
				{
					"handle": "parfum-sacre",
					"description": "Parfum Sacré is perfect if you’re drawn to classic glamour with a resinous, ambered finish. The spiced rose and jasmine feel plush and intimate, making it an elegant treat for evenings when you want old-school sensuality."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"description": "Le Musc & La Peau Extrait is ideal if you want something luxurious but close to the skin. Its powdery white musk and sandalwood create a soft, polished warmth that feels like the best version of your own scent."
				},
				{
					"handle": "blond-redhead-16-11",
					"description": "Blond Redhead 16.11 is a playful indulgence for when you want sweetness with sophistication. The chocolate, vanilla and rose are deliciously inviting, while saffron and woods keep it from becoming too innocent."
				},
				{
					"handle": "osang",
					"description": "Osang is for the self-purchaser who wants drama, texture and depth. Its honeyed, smoky animalic core feels powerful and uncompromising, with iris and incense giving it a strangely beautiful, magnetic tension."
				},
				{
					"handle": "mansa-musa",
					"description": "Mansa Musa is the one to choose if you want your oud to feel dense, textured and unmistakably luxurious. The leathery, ambered woods and restrained rose create a dark opulence that lingers with confidence."
				}
			]
		},
		"spring-scents": {
			"title": "Spring Scents",
			"subtitle": "Light florals and fresh green compositions for spring",
			"category": "By Season",
			"description": "A little spring treat for yourself: these scents feel like fresh air, new leaves, and flowers just beginning to open.",
			"products": [
				{
					"handle": "seychelles-breeze",
					"description": "Seychelles Breeze is perfect if you want that just-stepped-outdoors feeling—crisp grass, freesia, and orange blossom feel effortless and bright, with a soft musk that stays close to skin."
				},
				{
					"handle": "dancing-light",
					"description": "Dancing Light suits you if you love a cleaner, cooler spring mood; mint and pine make it feel airy and invigorating, while jasmine and sandalwood keep it polished and wearable."
				},
				{
					"handle": "muguet-fleuri",
					"description": "Muguet Fleuri is a lovely self-gift if you’re drawn to delicate florals with character—its lily-of-the-valley heart feels fresh and elegant, then melts into a softly intimate, creamy finish."
				},
				{
					"handle": "bamboo",
					"description": "Bamboo is a beautiful choice when you want something tranquil and green; the dewy leaves and floral notes feel calming and natural, with earthy woods that make it quietly grounding."
				},
				{
					"handle": "matcha-soft-serve",
					"description": "Matcha Soft Serve is for the spring day when you want something comforting but still fresh—green tea and matcha give it a clean vegetal edge, while milk and vanilla make it feel cozy and indulgent."
				},
				{
					"handle": "neon-night",
					"description": "Neon Night works well if you like your spring scents bright and modern; sparkling citrus, green tea, and peony create an easy lift, with smooth woods that keep it chic and breezy."
				},
				{
					"handle": "juli",
					"description": "Juli feels like a personal little bouquet with a twist—pear, cassis, and rose give it juicy softness, while spearmint and violet leaf keep it fresh and lightly green."
				},
				{
					"handle": "shaft-of-light",
					"description": "Shaft of Light (Sunbeams) is ideal if you want a scent that feels like warm sunlight after rain; lemon, grapefruit, and magnolia brighten beautifully, then settle into a gentle wooded softness."
				},
				{
					"handle": "flower-frost-19-12",
					"description": "Flower Frost 19.12 is a great pick if you enjoy florals with a cool, clean edge—the frosty citrus and ozonic opening feels refreshing, while the musky woods make it softly comforting."
				}
			]
		},
		"summer-in-a-bottle": {
			"title": "Summer in a Bottle",
			"subtitle": "Sun-drenched, holiday-ready scents",
			"category": "By Season",
			"description": "Treat yourself to a bottle of summer: bright, sun-warmed scents that feel like a holiday you can wear.",
			"products": [
				{
					"handle": "sal-y-limon",
					"description": "Sal y Limón is the one for when you want to feel instantly brighter—its lemon, lime and sea-salt sparkle like a first sip of something cold by the coast. The orange blossom and musky drydown keep it polished enough for everyday self-indulgence."
				},
				{
					"handle": "32-c",
					"description": "32°C is pure skin-on-skin vacation comfort, with creamy jasmine, tiare and coconut wrapped in a soft sun-lotion haze. If you love perfumes that feel warm, plush and quietly sensual, this is an easy treat-yourself choice."
				},
				{
					"handle": "a-grove-by-the-sea",
					"description": "A Grove By The Sea suits you if you want your summer scent to feel a little more grown-up and windswept, with fig leaf, rosemary and sea salt over a mineral, Adriatic backdrop. It’s fresh, green and textured, like a seaside escape with depth."
				},
				{
					"handle": "fiery-fig-24-05",
					"description": "Fiery Fig 24.05 gives you the lushness of ripe fig and coconut without losing that leafy Mediterranean freshness. It’s a lovely pick if you want something comforting but still airy, with a gentle woody finish that feels effortless."
				},
				{
					"handle": "beso-pasion",
					"description": "Beso Pasión is made for golden-hour dressing: citrus and ginger bring the lift, while fig, amber and musk add a warm, sun-kissed sensuality. Wear it when you want to feel relaxed, flattering and just a little irresistible."
				},
				{
					"handle": "kolada",
					"description": "Kolada is for the days you want your perfume to make a statement—pineapple and coconut feel playful and tropical, but the metallic floral edge keeps it unexpected. It’s a fun self-purchase if you like sweet scents with attitude."
				},
				{
					"handle": "santorini",
					"description": "Santorini is a soft, easygoing island scent for when you want freshness with a creamy, intimate finish. Fig, coconut and white musk make it feel smooth and wearable, like a quiet luxury you can reach for often."
				},
				{
					"handle": "divina-flora",
					"description": "Divina Flora is the most luminous floral in the collection, ideal if you want your summer perfume to feel radiant and a bit romantic. The citrusy opening and green herbs keep the bouquet from becoming too sweet, while sandalwood and vanilla make it linger beautifully."
				},
				{
					"handle": "in-love-with-the-cocos",
					"description": "In Love With the Cocos is a beautiful choice if you want something airy, creamy and close to the skin, with lemon, coconut water and soft florals. It feels intimate rather than loud, like a holiday memory you keep for yourself."
				}
			]
		},
		"autumn-mood": {
			"title": "Autumn Mood",
			"subtitle": "Warm spices, amber and golden-hour compositions",
			"category": "By Season",
			"description": "For your own golden-hour ritual, these warm, spiced scents wrap you in amber light, soft woods, and the feeling of choosing something just for you.",
			"products": [
				{
					"handle": "this-is-not-a-blue-bottle-1-4",
					"description": "This is Not a Blue Bottle 1/.4 – Yin feels like a velvet scarf for cool evenings: davana and opoponax give it a boozy glow, while cardamom, tonka and amber keep it softly sensual. It’s a lovely pick if you want something polished, cozy, and quietly magnetic."
				},
				{
					"handle": "tonkade",
					"description": "Tonkade is the kind of gourmand that feels grown-up and comforting rather than sugary, with vanilla and tonka cushioned by incense, cedar and patchouli. If you’re treating yourself, it’s an easy everyday indulgence that still has a darker, more intimate edge."
				},
				{
					"handle": "ziveli",
					"description": "Živeli has a celebratory richness that suits anyone craving a scent with personality: plum brandy, raspberry, leather and tobacco create a warm, convivial trail. It’s especially good when you want your perfume to feel like a toast to yourself."
				},
				{
					"handle": "drunk-lovers",
					"description": "DRUNK LOVERS is unapologetically decadent, with cognac, berries, ginger and cinnamon giving it a sparkling, spicy lift over ambered woods. Choose this if you want a bold, date-night fragrance that feels indulgent and a little mischievous."
				},
				{
					"handle": "montabaco",
					"description": "Montabaco offers a more refined autumn mood, where tobacco leaf, black tea, cardamom and leather feel elegant rather than heavy. It’s ideal if you like warmth with airiness, and want something sophisticated you can wear on repeat."
				},
				{
					"handle": "patchouli-cannelle",
					"description": "Patchouli Cannelle is a comforting cocoon of spice, resin and soft woods, with patchouli made smoother by vanilla, musk and ambergris. It’s a beautiful self-gift if you love earthy perfumes that feel calming, enveloping and quietly luxurious."
				},
				{
					"handle": "vanille-mafonja",
					"description": "Vanille Mafonja turns vanilla into something sultry and textured, adding cinnamon, nutmeg, incense and dark amber for depth. If you want a vanilla that feels more mysterious than sweet, this is a very rewarding treat."
				},
				{
					"handle": "le-mat",
					"description": "Le Mat has a restless, artistic energy, with rose, patchouli, immortelle and dark spices creating a warm chypre full of character. It suits someone who wants their autumn scent to feel bold, enigmatic and a little untamed."
				},
				{
					"handle": "wolf-extrait",
					"description": "Wolf (Extrait) is for when you want the forest at dusk: pine, moss and incense are deepened by amber, leather and a flash of spiced plum. It’s a striking choice if you prefer your warmth to feel wild, resinous and atmospheric."
				}
			]
		},
		"winter-warmers": {
			"title": "Winter Warmers",
			"subtitle": "Deep, warm, comforting winter fragrances",
			"category": "By Season",
			"description": "For the nights when you want your fragrance to feel like a private fire, these winter warmers wrap you in smoke, sweetness and deep comfort.",
			"products": [
				{
					"handle": "honey-and-tar",
					"description": "If you love a plush, edible warmth with a rugged streak, this is the one to treat yourself to: honeyed sweetness, birch tar and smoky leather make it feel cocooning yet intriguing."
				},
				{
					"handle": "lu-bu",
					"description": "For a more dramatic winter signature, Lü Bu gives you clove, gunpowder smoke and iris with a soft vanilla-benzoin finish—sharp at first, then quietly sensual on skin."
				},
				{
					"handle": "skin-fetish",
					"description": "Skin Fetish is a seductive pick if you want your comfort scent to feel a little dangerous: dark cherry, incense, rose and oud melt into ambered woods with a rich, provocative glow."
				},
				{
					"handle": "milk-bold",
					"description": "Milk | Bold is the easiest self-gift here if you want maximum coziness; marshmallow and tonka are wrapped in firewood smoke and amber, so it feels sweet, warm and unapologetically present."
				},
				{
					"handle": "rasputin",
					"description": "Rasputin is for the person who wants their winter fragrance to feel dense and luxurious: cardamom, clove, chocolate and oud create a spicy, smoky depth that lingers beautifully."
				},
				{
					"handle": "veles",
					"description": "Veles suits you if you like your warmth with a wild edge—tarred leather, smoky oud and saffron are softened by labdanum and tonka into something earthy, primal and addictive."
				},
				{
					"handle": "23-59",
					"description": "23:59 feels like the reward scent for late evenings in: cognac, espresso and crème brûlée meet benzoin and vanilla, giving you a toasted, smoky sweetness that feels indulgent and grown-up."
				},
				{
					"handle": "nebula-viridis",
					"description": "Nebula Viridis is a great choice if you want winter warmth without losing brightness; lemon and bergamot lift the saffron, woods and amber into a golden, smooth, quietly sensual scent."
				},
				{
					"handle": "moonlight-ceremony",
					"description": "Moonlight Ceremony is the moodiest treat in the set, with birch tar, smoked tea and incense over leather and guaiac wood—perfect if you want a dark, resinous fragrance that feels contemplative and elegant."
				}
			]
		},
		"festive-scents": {
			"title": "Festive Scents",
			"subtitle": "Spiced, warm, celebratory fragrances",
			"category": "By Season",
			"description": "For the person treating themselves, these festive scents feel like wrapping up in spice, warmth, and a little seasonal indulgence.",
			"products": [
				{
					"handle": "patchouli-cannelle",
					"description": "Patchouli Cannelle is the one to choose if you want something quietly luxurious and comforting: its cinnamon, clove, and resinous heart feel like a polished winter cocoon with depth. The vanilla and musk keep it soft enough for close wear, making it feel personal rather than loud."
				},
				{
					"handle": "vanille-mafonja",
					"description": "Vanille Mafonja suits you if you love vanilla but want it grown-up and atmospheric, with saline brightness and spice that stop it from becoming too sweet. The incense, myrrh, and dark amber give it a sensual, candlelit richness that feels perfect for festive evenings."
				},
				{
					"handle": "lu-bu",
					"description": "Lü Bu is ideal if your idea of festive is darker and more intriguing than sugary, with clove, smoke, and powdery iris creating a tense, elegant contrast. The vanilla and benzoin soften it just enough, so it still feels wearable and alluring on your own terms."
				},
				{
					"handle": "or-du-srail",
					"description": "Or du Sérail is a generous treat for anyone who wants their perfume to feel decadent and celebratory, with rum, honey, and candied fruit creating a lush, golden glow. It has the sort of comforting excess that feels like a reward in itself."
				},
				{
					"handle": "tiger-rouge",
					"description": "Tiger Rouge is for the self-purchaser who wants confidence and drama, with pepper, clove, citrus, and oud giving it a fierce, textured presence. The rose and cherry add a vivid lift, making it feel bold, sensual, and unmistakably festive."
				},
				{
					"handle": "fat-cola",
					"description": "Fat Cola is a cheerful, nostalgic pick if you want something sweet, fizzy, and unapologetically fun, with cola, gingerbread, donut, and marshmallow all in the mix. It has an easy festive charm that feels like pure self-indulgence without taking itself too seriously."
				},
				{
					"handle": "ladano",
					"description": "Ladano is a beautiful choice if you prefer your warmth resinous and understated, with labdanum, cinnamon leaf, and vanilla creating a smoky, wool-soft comfort. It feels grounding and intimate, like a scent you wear when you want to feel wrapped up and at ease."
				},
				{
					"handle": "meek-passion",
					"description": "Meek Passion works if you like your festive perfume playful at first, then unexpectedly deep, with banana, pineapple, and cinnamon turning into patchouli and vetiver. It’s a fun self-gift for someone who enjoys a fragrance with a mischievous, darker finish."
				},
				{
					"handle": "sugar-addict",
					"description": "SUGAR ADDICT is the most indulgent option here, made for when you want to lean fully into dessert-like comfort without losing sophistication. Cocoa, coffee, rum, and cashmere wood give it a rich, enveloping warmth that feels like a proper treat."
				}
			]
		},
		"sunshine-scents": {
			"title": "Sunshine Scents",
			"subtitle": "Sun-kissed, radiant, warm-weather fragrances",
			"category": "By Season",
			"description": "Treat yourself to a little sunlight in a bottle—these fragrances feel like warm skin, bright air, and the easy glow of a perfect day.",
			"products": [
				{
					"handle": "still-life-in-rio",
					"description": "If you want something that feels instantly uplifting but still lush, Still Life in Rio is a gorgeous pick: the yuzu and ginger sparkle, then coconut and rum wrap you in a softly tropical warmth."
				},
				{
					"handle": "aurantium-jasmine",
					"description": "Aurantium Jasmine is for the days you want to feel polished and radiant; its orange blossom and neroli are crisp at first, then the jasmine and musk turn it into a glowing, elegant floral embrace."
				},
				{
					"handle": "divina-flora",
					"description": "Divina Flora suits anyone craving a more luxurious, full-bodied floral treat—the passion fruit and lemongrass keep it bright, while sandalwood and vanilla make it feel indulgent and beautifully smooth."
				},
				{
					"handle": "tennis-club",
					"description": "Tennis Club is the one to choose if you love clean, breezy summer scents with a sporty edge; the green neroli and salty marine note feel fresh and effortless, like a sunlit afternoon outdoors."
				},
				{
					"handle": "elisa",
					"description": "Elisa is a lovely self-gift if you like your freshness with a soft, comforting sweetness; bergamot and yuzu lift it up, then musk and benzoin settle in like warm skin and freshly laundered clothes."
				},
				{
					"handle": "32-c",
					"description": "32°C is made for someone who wants creamy, sun-kissed comfort with a little freshness to keep it wearable; the jasmine, tiare and coconut feel decadent, but the ginger and white tea stop it from becoming too heavy."
				},
				{
					"handle": "firenze",
					"description": "Firenze is ideal if you prefer citrus with a refined, classic feel—its bright bergamot and bitter orange are lively, then sandalwood and vetiver give it a handsome, sun-warmed finish."
				},
				{
					"handle": "sun-bleached",
					"description": "Sun Bleached works beautifully if you like the idea of a clean, luminous scent that still feels soft and human; the citrus and ginger are bright, while the white musk gives it that easy sun-dried cotton effect."
				},
				{
					"handle": "neroli-petitgrain",
					"description": "Neroli & Petitgrain is a wonderful pick if you want something affordable, gentle and quietly comforting; it smells fresh and leafy at first, then the soft vanilla adds a tender warmth that feels instantly familiar."
				}
			]
		},
		"smell-expensive": {
			"title": "Smell Expensive",
			"subtitle": "Fragrances that radiate luxury and quality",
			"category": "Editorial",
			"description": "For the days you want to feel quietly lavish, these scents drape you in polished materials, rich florals, and impeccable finish.",
			"products": [
				{
					"handle": "rose-shiso",
					"description": "Rose Shiso is for when you want elegance with a modern edge: the rose feels plush and expensive, while shiso keeps it crisp, green, and unexpectedly chic. It’s a beautiful self-treat if you like your florals refined rather than sugary."
				},
				{
					"handle": "mathilde",
					"description": "Mathilde is a gorgeous choice if you want to smell radiant and softly indulgent, with lush jasmine and ylang-ylang wrapped in juicy fruit. The iris and sandalwood give it that polished, well-bred finish that feels effortlessly luxurious."
				},
				{
					"handle": "ambre-de-noir",
					"description": "Ambre de Noir suits you if you love a fragrance that feels like velvet in a dimly lit room—dark, warm, and deeply textured. Its cola-like amber, tobacco, and creamy woods make it a decadent, intimate signature."
				},
				{
					"handle": "mansa-musa",
					"description": "Mansa Musa is for the person who wants presence and depth, with oud that feels rich, leathery, and unmistakably opulent. The sandalwood and amber smooth the edges just enough to keep it wearable while still feeling powerful."
				},
				{
					"handle": "montabaco",
					"description": "Montabaco is a superb pick if you want luxury that feels tailored rather than heavy: tobacco leaf, leather, and tea create a refined, expensive air. It has that rare balance of sensuality and brightness that reads very polished on skin."
				},
				{
					"handle": "rouge-avignon",
					"description": "Rouge Avignon is ideal if you’re drawn to dramatic florals with a gourmand shadow, like a rose dressed for an evening out. The cocoa, truffle, and smoky woods give it a gothic richness that feels indulgent and memorable."
				},
				{
					"handle": "afrique-en-fleurs",
					"description": "Afrique en Fleurs works beautifully if you like your luxury with sunlight and softness rather than darkness. Its bright citrus, powdered florals, and creamy woods feel elegant, airy, and quietly expensive."
				},
				{
					"handle": "vettii",
					"description": "Vettii is a wonderful choice for someone who wants opulence with a sophisticated unfolding: fruity brightness first, then resin, oud, and soft leather. It feels enveloping and refined, like a fragrance with real depth and taste."
				},
				{
					"handle": "high-to-lotus",
					"description": "High to Lotus is for the adventurous self-purchaser who wants something rare and impeccably composed. Its green, briny, floral oddness turns luxurious through the oud and sandalwood base, making it intriguing rather than pretty."
				}
			]
		},
		"scents-theyll-remember-you-for": {
			"title": "Scents They'll Remember You For",
			"subtitle": "Distinctive, memorable, signature-worthy fragrances",
			"category": "Editorial",
			"description": "For the days you want your scent to speak before you do, these are the memorable signatures worth wearing for yourself.",
			"products": [
				{
					"handle": "cambodian-oud",
					"description": "Cambodian Oud is for when you want to feel unmistakably composed: its rare oud, smoke and leather create a bold, self-assured trail that doesn’t try to please everyone. If you like your fragrance with character and depth, this is a striking personal statement."
				},
				{
					"handle": "ensang-noir",
					"description": "Ensang Noir suits the person who loves drama with elegance. The dark rose, metallic shimmer and smoky woods feel like a beautifully written secret—intense, memorable, and a little dangerous in the best way."
				},
				{
					"handle": "jany",
					"description": "Jany is perfect if you want comfort to feel luxurious. Its warm apple tart, caramel and vanilla wrap around you like a favorite dessert, but the apricot and walnut keep it polished enough to become your signature indulgence."
				},
				{
					"handle": "195-a-c",
					"description": "195 A.C. is made for someone who likes freshness with a sensual twist. The fig, pomegranate leaf and mint feel vivid and sunlit, while incense and cedar give it the kind of depth that lingers in memory."
				},
				{
					"handle": "le-mat",
					"description": "Le Mat will suit you if you’re drawn to fragrances with mystery and a touch of rebellion. Rose, patchouli and immortelle create a rich, earthy floral that feels confident, intriguing and wonderfully unconventional."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"description": "Le Musc & La Peau Extrait is ideal if you want to smell like the most polished version of your own skin. Its white musk, sandalwood and soft ylang-ylang create a clean, intimate aura that feels effortlessly alluring."
				},
				{
					"handle": "the-nirvana",
					"description": "The Nirvana is for the person who wants serenity without disappearing. The black tea, jasmine and bright citrus make it airy and luminous, yet the musky warmth keeps it personal and quietly memorable."
				},
				{
					"handle": "awake",
					"description": "Awake is a great match if you love scents with instant personality. The coffee and lemon opening feels energizing and modern, while vetiver and cardamom give it a cozy, addictive depth that reads as distinctly you."
				},
				{
					"handle": "grande-lle",
					"description": "Grande Ile is for when you want warmth, richness and a little spice in your wake. The vanilla and pink pepper are creamy and inviting, but the cedar, clove and resin make it feel textured, grown-up and unforgettable."
				}
			]
		},
		"scents-with-personality": {
			"title": "Scents With Personality",
			"subtitle": "Characterful, individual, non-generic fragrances",
			"category": "Editorial",
			"description": "For the person who likes a fragrance with a point of view, these are the scents that feel like a signature only you could wear.",
			"products": [
				{
					"handle": "1805-tonnerre",
					"description": "Tonnerre is for the days you want your perfume to feel bracing and unforgettable, with smoke, salt spray and dark woods giving it a dramatic, tailored edge. It wears like confidence with a scorch mark."
				},
				{
					"handle": "cavallo-di-fuoco",
					"description": "火马 - Cavallo di fuoco suits someone who likes their scent vivid and a little unruly: mint, pomelo and ginger flash bright before smoky oud settles in. It’s bold, textured, and impossible to mistake for anything else."
				},
				{
					"handle": "nuit-de-baklite",
					"description": "Nuit de Bakélite is a brilliant pick if you love florals with bite, because its green tuberose feels sharp, stalky and deliberately unsweet. It has the kind of uncompromising personality that makes a perfume feel like a statement."
				},
				{
					"handle": "cambodian-oud",
					"description": "Cambodian Oud is ideal when you want depth without fuss, letting the oud’s smoky, leathery richness speak plainly and beautifully. It feels self-possessed, sensual and quietly powerful on skin."
				},
				{
					"handle": "pear-pavlova",
					"description": "Pear Pavlova is for the moodier sweet tooth: airy pear and meringue make it playful, while almond, jasmine and musk keep it from disappearing into dessert. It’s charming, but with enough backbone to feel grown-up."
				},
				{
					"handle": "pink-sweats",
					"description": "Pink Sweats is a lovely choice if you want something flirtatious but not precious, with rhubarb, lychee and rosé Champagne giving it a sparkling lift. The soft musk and marshmallow make it feel relaxed, witty and easy to wear."
				},
				{
					"handle": "figo",
					"description": "Figo is perfect if you like freshness with character, because it captures the whole fig tree rather than just the fruit. Green leaves, milky fig and warm woods make it feel natural, sunlit and quietly distinctive."
				},
				{
					"handle": "rose-de-taif",
					"description": "Rose de Taif is a beautiful self-gift if you want rose with clarity and poise rather than heaviness. Its lemon, green spice and creamy musk give the flower a luminous, intimate softness."
				},
				{
					"handle": "arcadia-1",
					"description": "Arcadia will suit you if you’re drawn to perfumes that feel reflective and a little offbeat, with iris, incense and vetiver creating a serene but savory depth. It’s elegant, contemplative and wonderfully individual."
				}
			]
		},
		"perfumes-for-compliments": {
			"title": "Perfumes for Compliments",
			"subtitle": "Crowd-pleasers that draw positive attention",
			"category": "Editorial",
			"description": "If you’re buying for your own pleasure, these are the scents that make you feel noticed in the loveliest way—bright, polished, and impossible to ignore.",
			"products": [
				{
					"handle": "guava-sorbet",
					"description": "Guava Sorbet is the easy smile of the collection: juicy, tropical, and softly creamy, with enough floral polish to feel charming rather than sugary. It’s ideal if you want compliments that sound like \"you smell amazing\" all afternoon."
				},
				{
					"handle": "lamar",
					"description": "Lamar brings big, luxurious fruity-floral energy, so it suits anyone who wants to make an entrance without feeling too sharp or austere. The warm amber-vanilla base keeps all that sweetness plush, sensual, and very wearable."
				},
				{
					"handle": "davana-vanille",
					"description": "Davana & Vanille is the most effortless crowd-pleaser here, with a light gourmand feel that reads cozy, delicious, and approachable. If you want something pretty and addictive without the weight of a full dessert scent, this is a lovely self-treat."
				},
				{
					"handle": "damask",
					"description": "Damask is for the person who likes rose with a twist: juicy fruit, a mineral breeze, and a smooth musky finish that feels elegant and modern. It’s refined enough for daily wear, but distinctive enough to invite a second sniff."
				},
				{
					"handle": "rouge-avignon",
					"description": "Rouge Avignon suits someone who loves their compliments a little darker and more intriguing, with rose, cocoa, and truffle giving it a moody, luxurious edge. It’s sensual and memorable, like a velvet dress in fragrance form."
				},
				{
					"handle": "mathilde",
					"description": "Mathilde is lush, radiant, and beautifully textured, with tropical fruit lifting the floral heart so it feels abundant rather than heavy. If you want a perfume that seems expensive, artistic, and softly romantic, this is a strong pick."
				},
				{
					"handle": "32-c",
					"description": "32°C is the fragrance for warm-weather self-indulgence: creamy coconut, jasmine, and vanilla that feel like sun on skin, but with a fresh white-tea brightness. It’s incredibly easy to love and tends to draw very natural, \"what are you wearing?\" attention."
				},
				{
					"handle": "the-empress-kiss",
					"description": "The Empress Kiss is your choice if you want compliments with drama—spiced rose, saffron, and a caramelized sweetness that feels rich and magnetic. It has a confident, regal presence that still stays smooth and inviting on skin."
				},
				{
					"handle": "juice-personal",
					"description": "Juice | Personal is the most intimate compliment-magnet here, with raspberry and rhubarb wrapped in a sheer musky floral haze. It’s perfect if you want something playful and polished that stays close, like your own fruity glow."
				}
			]
		},
		"the-easy-winners": {
			"title": "The Easy Winners",
			"subtitle": "Effortless, reliable, universally loved scents",
			"category": "Editorial",
			"description": "For the one who wants an easy yes, these are the polished, comforting scents that feel instantly right on your skin.",
			"products": [
				{
					"handle": "vetiver-santal",
					"description": "Vetiver Santal is a smart first reach if you want something fresh but grounded: bergamot and vetiver keep it crisp, while sandalwood and musk make it feel quietly expensive and easy to wear every day."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"description": "PG4.1 Le Musc & La Peau is the kind of scent that makes you feel naturally put together, with soft musks and skin warmth that sit close and flattering, like your best version on a low-effort day."
				},
				{
					"handle": "ramune",
					"description": "Ramune is pure mood-lifting charm, with fizzy lemon and mint that feel playful and clean, then a gentle musky vanilla drydown that makes it an easy compliment magnet."
				},
				{
					"handle": "santal",
					"description": "Santal has that tailored, universally appealing balance of freshness and warmth, so if you like smelling refined without trying too hard, this is an elegant everyday sandalwood to make your own."
				},
				{
					"handle": "musc-oli",
					"description": "Musc Oli is for when you want something intimate and quietly addictive: the white musk and cashmeran feel like soft skin, with just enough rose and sandalwood to keep it graceful."
				},
				{
					"handle": "concrete",
					"description": "Concrete is the interesting easy winner here, because its smooth sandalwood and metallic rose feel modern and distinctive without becoming difficult, making it a great signature if you like clean with character."
				},
				{
					"handle": "oud-cannelle",
					"description": "Oud Cannelle gives you the comfort of spice and woods with a softer, more wearable edge, so it suits anyone who wants oud’s depth without the heaviness—warm, cosy, and polished."
				},
				{
					"handle": "tears-on-mystic-sands",
					"description": "Tears On Mystic Sands is a lovely choice if you want a fragrance that feels both pretty and grown-up, with bright fruit and floral lift over incense, oud and sandalwood for a more memorable finish."
				},
				{
					"handle": "neon-night",
					"description": "Neon Night is the easy bright option for someone who likes a fragrance that feels fresh, modern and cheerful, with sparkling citrus, tea and soft woods that stay light on the skin."
				}
			]
		},
		"unexpected-favourites": {
			"title": "Unexpected Favourites",
			"subtitle": "Surprising gems that become instant favourites",
			"category": "Editorial",
			"description": "For the self-gifter who loves a surprise, these are the offbeat scents that win you over fast and feel like a secret you’re glad you kept.",
			"products": [
				{
					"handle": "mezcal-chocolat",
					"description": "Mezcal Chocolat is perfect if you want indulgence with bite: the boozy cacao and smoky woods feel plush and grown-up, while lime and chili keep it bright and addictive."
				},
				{
					"handle": "1805-tonnerre",
					"description": "Tonnerre suits you if you’re drawn to dramatic, unconventional scents with presence; its salt spray, smoke and birch tar feel bold and bracing, like a storm you can wear."
				},
				{
					"handle": "dancing-light",
					"description": "Dancing Light is a lovely pick if you want something crisp but comforting, with icy mint and pine opening into a soft floral heart that feels clean, luminous and quietly elegant."
				},
				{
					"handle": "fantomas",
					"description": "Fantomas is for when you want something playful but strange: the melon sweetness is polished by metallic, woody-musky depth, creating a scent that stays intriguing all day."
				},
				{
					"handle": "scrtions-magnifiques",
					"description": "Secretions Magnifiques is only for the adventurous, but if you like perfumes that provoke a reaction, its saline, milky and sandalwood facets make it memorably daring and artful."
				},
				{
					"handle": "milk-bold",
					"description": "Milk | Bold is ideal if you crave cosy sweetness with a darker mood; the marshmallow and tonka feel enveloping, while smoke and mahogany give it a sultry, expensive finish."
				},
				{
					"handle": "solar-wind",
					"description": "Solar Wind is a great self-treat if you love fruity scents with a twist, because the rhubarb-citrus sparkle feels juicy and uplifting, then elemi adds a sharper, more modern edge."
				},
				{
					"handle": "comme-des-garcons-2",
					"description": "Comme des Garçons 2 is for you if you like abstract, polished perfumes that feel intelligent rather than obvious; ink, tea and incense create a clean-but-shadowed aura that’s endlessly wearable."
				},
				{
					"handle": "mistpouffer",
					"description": "Mistpouffer suits a person who likes subtle oddities: fig leaf, pine and vetiver feel green and airy, while the smoky haze and immortelle make it quietly magnetic and distinctive."
				}
			]
		},
		"perfumes-that-dont-smell-like-perfume": {
			"title": "Perfumes That Don't Smell Like Perfume",
			"subtitle": "Unconventional, abstract, genre-defying scents",
			"category": "More Editorial",
			"description": "For the self-purchaser who wants fragrance to feel like discovery, these are the beautifully strange scents that slip past “perfume” and into mood, texture, and memory.",
			"products": [
				{
					"handle": "monopteros",
					"description": "Monopteros is a clever treat if you like your comfort with a twist: creamy coconut and coffee feel familiar, but the aldehydes and cardamom keep it sparkling, weird, and quietly sensual."
				},
				{
					"handle": "cashmere-beige",
					"description": "Cashmere Beige suits someone who wants softness without sweetness — its rain-washed florals, cool orris, and cedar-tonka base feel polished, airy, and easy to wear close to the skin."
				},
				{
					"handle": "black-oregano",
					"description": "Black Oregano is for the person who wants their indulgence to have bite: herbal, leathery, and smoky, it feels expensive, untamed, and just a little dangerous."
				},
				{
					"handle": "scrtions-magnifiques",
					"description": "Secretions Magnifiques is the choice if you’re buying for your most fearless self; it’s intentionally provocative, saline and bodily in a way that turns discomfort into an experience."
				},
				{
					"handle": "boar",
					"description": "Boar is wonderfully raw and forested, perfect if you want something earthy and wild with a plush, animalic warmth that feels like stepping into damp woods after rain."
				},
				{
					"handle": "indian-grass",
					"description": "Indian Grass works beautifully for someone drawn to green, restless scents — cannabis, incense, and guaiac give it a hazy, meditative edge that feels offbeat but wearable."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"description": "V.I.T.R.I.O.L. is for the collector who wants perfume as confrontation: it’s abrasive, feral, and unforgettable, more art object than accessory."
				},
				{
					"handle": "rouge",
					"description": "Rouge is a smart pick if you like savory oddities with elegance; beetroot, incense, and pepper make it strange and polished at once, with a quietly magnetic drydown."
				},
				{
					"handle": "dawn-whispers",
					"description": "Dawn Whispers is the most comforting kind of strange — metallic and cool at first, then milky and musky, like a soft blanket pulled over an alien sunrise."
				}
			]
		},
		"scents-that-tell-a-story": {
			"title": "Scents That Tell a Story",
			"subtitle": "Narrative, cinematic, emotionally rich fragrances",
			"category": "More Editorial",
			"description": "If you’re treating yourself, these are the fragrances that feel like private chapters—lush, cinematic scents that linger like a scene you can’t stop replaying.",
			"products": [
				{
					"handle": "damask",
					"description": "Damask is for the moment you want romance with a little salt on the skin: its rose-and-blackcurrant glow feels indulgent, but the mineral amber keeps it airy and modern."
				},
				{
					"handle": "talento",
					"description": "Talento suits you if you love the idea of a rose with a pulse—green, minty, and luminous, like wearing a protective charm that still feels beautifully alive."
				},
				{
					"handle": "liquid-incense",
					"description": "Liquid Incense is a gorgeous self-gift for anyone drawn to quiet drama; it wears like sunlight caught in resin, with incense that feels warm, polished, and deeply calming."
				},
				{
					"handle": "lu-bu",
					"description": "Lü Bu has that smoky, shadowed allure that makes a fragrance feel like a scene from a film—clove, iris, and vanilla give it tension, softness, and a slightly dangerous charm."
				},
				{
					"handle": "mistpouffer",
					"description": "Mistpouffer is ideal if you prefer your stories subtle and strange: fig, pine, and airy smoke create a misty trail that feels contemplative, earthy, and wonderfully offbeat."
				},
				{
					"handle": "sirenide",
					"description": "Sirenide is for the bolder mood-lifter in you, with marine freshness twisted through leather and almond-like strangeness—clean at first glance, then deliciously unsettling."
				},
				{
					"handle": "princess-of-china",
					"description": "Princess of China feels like choosing elegance for yourself, not for anyone else: peony, lotus, pear, and lychee give it a silk-smooth grace that’s tender and effortless."
				},
				{
					"handle": "vanille-mafonja",
					"description": "Vanille Mafonja turns vanilla into something far more captivating than sweet alone; the spice, incense, and amber make it feel sensual, textured, and beautifully grown-up."
				},
				{
					"handle": "01-nagarmotha",
					"description": "01 Nagarmotha is a strong, self-assured pick if you want something earthy and composed—lime leaf, vetiver, leather, and incense create a savory, quietly magnetic signature."
				}
			]
		},
		"brands-before-they-blow-up": {
			"title": "Brands Before They Blow Up",
			"subtitle": "Emerging houses and hidden gems to discover now",
			"category": "More Editorial",
			"description": "Treat yourself to the thrill of being early: these hidden-gem fragrances feel like finding your signature before everyone else does.",
			"products": [
				{
					"handle": "mezcal-chocolat",
					"description": "If you love a gourmand with bite, Mezcal Chocolat is the one to try first: the lime-and-chili sparkle keeps the dark chocolate and mezcal from feeling sweet, making it sultry, smoky, and a little dangerous."
				},
				{
					"handle": "monopteros",
					"description": "Monopteros suits you if you want something truly offbeat but still wearable; its coconut-creamy, coffee-spiced core feels unexpected and luxurious, with just enough animalic intrigue to keep it personal."
				},
				{
					"handle": "1805-tonnerre",
					"description": "Reach for Tonnerre when you want your perfume to feel like a statement piece: the gunpowder, smoke, and salt spray create a dramatic maritime mood that’s bold, refined, and unforgettable."
				},
				{
					"handle": "fantomas",
					"description": "Fantomas is ideal if you’re drawn to fragrances that toy with expectation; the juicy melon is oddly addictive against the metallic, musky woods, giving you something sleek, strange, and very modern."
				},
				{
					"handle": "scrtions-magnifiques",
					"description": "If you enjoy scents that provoke a reaction, Secretions Magnifiques is for the fearless collector in you; it’s intentionally unsettling, experimental, and fascinating in the way only a truly boundary-pushing perfume can be."
				},
				{
					"handle": "moko",
					"description": "Moko feels like a fresh start in a bottle, perfect if you want something green, earthy, and quietly uplifting; the fig, rain-soaked woods, and raspberry make it vivid without losing depth."
				},
				{
					"handle": "closeup",
					"description": "Close-Up is a great choice if you like your perfumes dark but polished; its boozy cherry, coffee, tobacco, and amber feel intimate and grown-up, with enough brightness to keep it from going heavy."
				},
				{
					"handle": "lola-at-coat-check",
					"description": "Lola at Coat Check suits you if you want sensuality with a crisp first impression; cardamom and grapefruit lift the soft white chocolate and amber into something chic, cozy, and a little flirtatious."
				},
				{
					"handle": "tears",
					"description": "Tears is for the person who prefers texture and atmosphere over sweetness; its rain-damp cotton, sage, and earthy vetiver create a dry, poetic scent that feels quietly distinctive."
				}
			]
		},
		"best-value-per-ml": {
			"title": "Best Value Per ML",
			"subtitle": "Maximum quality for your money",
			"category": "More Editorial",
			"description": "If you’re treating yourself, these are the bottles that feel far pricier than they are—lush, distinctive, and wonderfully rewarding per wear.",
			"products": [
				{
					"handle": "1993",
					"description": "1993 is for the self-purchaser who wants maximum drama for the money: its peach-chocolate richness and chypre bite feel far more opulent than the price suggests. It’s a confident, vintage-leaning signature with real presence."
				},
				{
					"handle": "matcha-mist",
					"description": "Brume Matcha is a smart buy if you love the calm luxury of tea scents but still want something textured and distinctive. The roasted matcha, basil and peppermint make it feel fresh, composed and easy to reach for often."
				},
				{
					"handle": "osmanthus-oolong",
					"description": "Osmanthus & Oolong suits you if you want something quietly elegant that still feels special every time you spray it. Its apricot-tea softness and smooth woody finish give it an expensive, polished air."
				},
				{
					"handle": "vanille-creme",
					"description": "Vanille Creme is the indulgence pick for anyone who wants a comforting, dessert-like fragrance that feels like a treat in itself. The custard, caramel and vanilla create a rich, wearable sweetness with excellent cozy payoff."
				},
				{
					"handle": "blueforest",
					"description": "Blueforest is ideal if you want a clean, woody scent that feels understated but never boring. Its cedar, pepper and soft musk make it versatile, modern and easy to justify as an everyday luxury."
				},
				{
					"handle": "cloud-essence-28-08",
					"description": "Cloud Essence 28.08 is a lovely choice if you’re drawn to soft, skin-close scents that feel comforting rather than loud. The milky rice-powder warmth and sandalwood give it a dreamy, cocooning quality that wears beautifully."
				},
				{
					"handle": "figo",
					"description": "Figo is the one to pick if you want a fresh fig scent with enough warmth to feel complete and satisfying. The leafy fruitiness and gentle ambered woods make it feel polished, natural and very easy to love."
				},
				{
					"handle": "santorini",
					"description": "Santorini suits you if you want an easygoing scent that still feels sensual and a little sunlit. Green fig, coconut and musk create a relaxed, creamy freshness that feels like a small escape."
				},
				{
					"handle": "301",
					"description": "301 Noces de Santal is the best value if you love creamy sandalwood with a nostalgic, 90s-style richness. The coconut, benzoin and ylang-ylang make it feel generous, smooth and beautifully enveloping."
				}
			]
		},
		"cult-classics": {
			"title": "Cult Classics",
			"subtitle": "The bestsellers everyone talks about",
			"category": "More Editorial",
			"description": "A cult-classic edit for the self-gifter who wants a fragrance with personality, polish, and a little bragging rights.",
			"products": [
				{
					"handle": "comme-des-garcons-2",
					"description": "Comme des Garcons 2 is ideal if you love smelling intriguing rather than obvious: its tea, ink, and incense create a clean-but-shadowed signature that feels quietly expensive and endlessly wearable."
				},
				{
					"handle": "20359",
					"description": "20359 suits the self-purchaser who wants their perfume to feel like a night out in bottle form—sweet cherry and heliotrope wrapped in leather and tobacco for a plush, confident, slightly dangerous mood."
				},
				{
					"handle": "ayiti",
					"description": "Ayiti is for when you want something grounding and elemental, with Haitian vetiver and rain-soaked earth giving you that calm, mineral freshness that feels beautifully lived-in."
				},
				{
					"handle": "kolada",
					"description": "Kolada is a brilliant pick if you like your compliments immediate: tropical pineapple and coconut are lifted by a metallic edge, making it playful, modern, and just a bit subversive."
				},
				{
					"handle": "lumieres",
					"description": "Lumières works beautifully for someone drawn to understated luxury, with pepper, mineral notes, and iris creating a cool, radiant skin scent that feels crisp, urban, and softly sensual."
				},
				{
					"handle": "mango-sticky-rice",
					"description": "Mango Sticky Rice is the treat-yourself fragrance for anyone who wants comfort with charm—juicy mango, creamy coconut, and tender rice make it feel delicious without becoming heavy."
				},
				{
					"handle": "pear-pavlova",
					"description": "Pear Pavlova is perfect if you enjoy sweet scents with structure: pear and meringue feel airy and joyful, while almond and musk add enough depth to keep it from fading into fluff."
				},
				{
					"handle": "monkeyflower",
					"description": "Monkeyflower is a lovely choice for the nostalgic romantic, blending chamomile, fig, and hay into a sun-warmed meadow scent that feels easygoing, comforting, and a little dreamy."
				},
				{
					"handle": "panorama",
					"description": "Panorama suits the green-fragrance enthusiast who likes a twist: wasabi, fig leaf, and cut grass make it vivid and fresh, while resin and fir bring a smooth, polished finish."
				}
			]
		},
		"gender-bending-scents": {
			"title": "Gender-Bending Scents",
			"subtitle": "Fragrances that blur the lines beautifully",
			"category": "More Editorial",
			"description": "For the days you want to wear something that feels a little daring, a little intimate, and entirely your own.",
			"products": [
				{
					"handle": "fantomas",
					"description": "Fantomas is for when you want a scent that toys with comfort and coolness at once: juicy melon softens the edge, while Cashmeran gives it that sleek, modern bite. It’s a brilliant pick if you like your fruit fragrances with a strange, magnetic twist."
				},
				{
					"handle": "anti-blues",
					"description": "Anti-Blues suits the self-purchaser who loves indulgence with shadow in it. The chocolate, incense and tobacco feel plush and sensual, while saffron and vetiver keep it sophisticated rather than sugary."
				},
				{
					"handle": "cashmere-beige",
					"description": "Cashmere Beige is ideal if you want something quietly beautiful and easy to wear, but still unusual enough to feel personal. Its floral transparency and cedar-tonka softness create a clean, skin-close elegance that never feels bland."
				},
				{
					"handle": "fig-porn",
					"description": "FIG PORN is a lovely choice if you’re drawn to creamy fruit scents with a seductive streak. The fig and pear feel lush and inviting, while the vanilla-musk finish gives it a soft, confident warmth."
				},
				{
					"handle": "mezcal-chocolat",
					"description": "Mezcal Chocolat is for treating yourself to something bold and a little addictive. The lime, chili and mezcal snap first, then the chocolate and incense settle into a smoky gourmand glow that feels deliciously offbeat."
				},
				{
					"handle": "iris-altesse",
					"description": "Iris Altesse is perfect if you love refined scents that still have a modern pulse. Its cool iris and clean leather feel polished and distinctive, while the floral-fruity lift keeps it from ever becoming too austere."
				},
				{
					"handle": "kar-wai",
					"description": "Kar-Wai is a beautiful pick for someone who likes fragrance to feel cinematic and intimate. The smoked tea, osmanthus and musks create a sensual tension that’s elegant, fluid and just a little mysterious."
				},
				{
					"handle": "monopteros",
					"description": "Monopteros is for the self-gifter who enjoys contrasts and doesn’t mind a perfume with personality. Coconut and coffee make it creamy and surprising, while aldehydes and neroli keep it bright, playful and slightly wild."
				},
				{
					"handle": "myrrh-blood-drop",
					"description": "No. 8 – Drop of Blood suits you if you want something uncompromising and atmospheric. Its resinous woods, saffron and incense build a dark, enigmatic aura that feels powerful, polished and deeply memorable."
				}
			]
		},
		"nostalgia-in-a-bottle": {
			"title": "Nostalgia in a Bottle",
			"subtitle": "Scents that trigger memories and emotion",
			"category": "More Editorial",
			"description": "For the days you want a scent that feels like opening a memory box, this collection wraps nostalgia in something beautifully wearable just for you.",
			"products": [
				{
					"handle": "iris-cendr",
					"description": "If you love the idea of a polished, slightly severe iris, Iris Cendré is a gorgeous self-treat: creamy orris and incense feel intimate, while tobacco and leather give it that forgotten-vintage allure."
				},
				{
					"handle": "alyazia",
					"description": "Alyazia is for when you want your nostalgia to feel soft, pretty, and a little flirtatious; the violet, iris, and wild strawberry read like old cosmetics with a tender, modern glow."
				},
				{
					"handle": "josephine",
					"description": "Joséphine suits the romantic who likes her comfort with a bit of drama: rose, violet, and opoponax create a warm, powdery haze that feels like inherited jewelry and candlelit evenings."
				},
				{
					"handle": "lances",
					"description": "Lances is a cozy-but-clever choice if you want something unusual on your own skin; toasted hazelnut and lavender make it comforting, while cedar keeps it crisp and quietly elegant."
				},
				{
					"handle": "oblivion",
					"description": "Oblivion is perfect for the person who wants freshness with a memory-laced softness: bergamot and yuzu lift the lavender, then iris and woods settle into a calm, old-school composure."
				},
				{
					"handle": "ambre-de-noir",
					"description": "Ambre de Noir is the richest self-indulgence here, with cola-dark amber, tobacco, and vanilla creating a warm, intimate scent that feels like a cherished keepsake from another era."
				},
				{
					"handle": "fawaki-fawaki",
					"description": "Fawaki Fawaki gives nostalgia a sunnier twist, blending tropical fruit with coconut and vanilla in a way that feels playful, comforting, and a little retro-glamorous."
				},
				{
					"handle": "ara",
					"description": "ARC is for the herbal scent lover who wants depth and texture; basil, thyme, lavender, and oakmoss feel classic and grounded, with smoky sweetness that lingers beautifully."
				},
				{
					"handle": "iris-coriandre",
					"description": "Iris Coriandre is a refined choice if you like your memories a touch austere: the iris and violet are elegant and powdery, while coriander and osmanthus add a savory, vintage edge."
				}
			]
		},
		"under-50": {
			"title": "Under £50",
			"subtitle": "Beautiful gifts without breaking the bank",
			"category": "By Budget",
			"description": "Treat yourself to something lovely and low-stakes: these under-£50 picks feel indulgent, personal, and easy to wear every day.",
			"products": [
				{
					"handle": "2025-bestsellers-for-her",
					"description": "If you love soft, flattering scents that feel like a little luxury on skin, this bestselling set is a smart self-gift. It gives you nine moods in one box, from milky comfort to bright fruit and gentle florals."
				},
				{
					"handle": "patchouli-labdanum",
					"description": "For someone who wants a fragrance with depth and a touch of edge, this is beautifully grounding. The smoky patchouli and labdanum feel warm and confident, but the eau de cologne style keeps it easy to wear."
				},
				{
					"handle": "exploration-kit",
					"description": "This kit is ideal if you like your perfume to feel modern, cozy, and a bit addictive. Cedarwood, milk, marshmallow, sandalwood, and vanilla give you that clean-sweet comfort with a polished finish."
				},
				{
					"handle": "j-scent-discovery-set",
					"description": "If you enjoy discovering scents that feel fresh, quirky, and quietly elegant, this set is a delight. The mix of yuzu, peppermint, violet, green grass, and vanilla makes it perfect for sampling your way to a signature scent."
				}
			]
		},
		"under-100": {
			"title": "Under £100",
			"subtitle": "The sweet spot of quality and value",
			"category": "By Budget",
			"description": "A carefully chosen treat under £100: polished, personality-filled perfumes that feel indulgent without tipping into excess.",
			"products": [
				{
					"handle": "1993",
					"description": "For a vintage-loving self-purchase, 1993 feels like a statement piece: peachy richness and chocolate depth sharpened by earthy patchouli. It’s bold, nostalgic, and nicely uncompromising for the price."
				},
				{
					"handle": "cherry-passion",
					"description": "Cherry Passion is the playful pick if you want something instantly cheerful and a little flirty. The ripe cherry and strawberry sweetness is cushioned by almond, jasmine and vanilla so it still feels grown-up."
				},
				{
					"handle": "fig-lemonade",
					"description": "Fig Lemonade brings a sparkling, easygoing brightness that makes it ideal for everyday wear. The fizzy citrus and green fig keep it fresh, while cedarwood gives it just enough structure to feel considered."
				},
				{
					"handle": "osmanthus-oolong",
					"description": "Osmanthus & Oolong is a quietly elegant choice for someone who likes softness with character. Its apricot-tea warmth feels uplifting and refined, with a calm woody finish that lingers beautifully."
				},
				{
					"handle": "vanille-creme",
					"description": "Vanille Creme is pure comfort in a bottle if you’re craving something cozy and decadent. The custardy vanilla and caramel make it deliciously indulgent, with a soft muskiness that keeps it wearable."
				},
				{
					"handle": "blueforest",
					"description": "Blueforest suits you if you want something clean, modern and understated rather than sweet. Cedarwood, pepper and musk create a crisp, skin-close mood that feels effortless and expensive."
				},
				{
					"handle": "fiery-fig-24-05",
					"description": "Fiery Fig 24.05 is the one to choose if you want fig with a sunnier, creamier twist. Leafy freshness, coconut and fig milk give it a warm Mediterranean feel without losing its green edge."
				},
				{
					"handle": "figo",
					"description": "Figo is a lovely everyday fig fragrance for someone who likes freshness with a subtle warmth underneath. The orange and leafy notes feel bright and natural, while guaiacwood and amber add a smooth finish."
				},
				{
					"handle": "series-3-incense-avignon",
					"description": "Series 3: Incense | Avignon is for the self-gifter who loves atmosphere and a touch of solemn beauty. Its incense, stone-cool mineral feel and balsamic woods create a refined, contemplative aura."
				}
			]
		},
		"under-150": {
			"title": "Under £150",
			"subtitle": "Premium gifts for a special occasion",
			"category": "By Budget",
			"description": "Treat yourself to a polished, special-occasion edit under £150—luxurious, distinctive scents that feel like a gift you chose with care.",
			"products": [
				{
					"handle": "565",
					"description": "565 is for when you want your splurge to turn heads: its citrusy sweetness, amber glow and dark musk give you a rich, sensual statement without losing its sparkle."
				},
				{
					"handle": "a-pas-de-loup",
					"description": "À Pas De Loup suits the person who finds luxury in texture and atmosphere; its damp moss, sage and sandalwood feel intimate, earthy and quietly expensive."
				},
				{
					"handle": "afternoon-tea",
					"description": "Afternoon Tea is a lovely pick if you want elegance with a modern edge, blending bright tea and citrus with creamy florals and a whisper of smoke for a refined, self-indulgent finish."
				},
				{
					"handle": "arashiyama",
					"description": "Arashiyama is ideal when you crave calm sophistication: the rain-damp bamboo, cut grass and violet create a serene, airy scent that feels beautifully composed."
				},
				{
					"handle": "deer",
					"description": "Deer works well if you like your fragrance a little rugged and charismatic, with tobacco, coffee and leather giving it a warm, wooded depth that feels confident and grounded."
				},
				{
					"handle": "milk",
					"description": "Milk Personal is the easiest kind of indulgence—soft marshmallow and tonka wrapped in skin musk and woods, making you smell deliciously close and effortlessly cozy."
				},
				{
					"handle": "misty-cypress-ash",
					"description": "Misty Cypress Ash is for someone who prefers restraint over sweetness; its cypress, wormwood and mineral smoke feel crisp, austere and intriguingly atmospheric."
				},
				{
					"handle": "paper-soap",
					"description": "Soap Paper is a comforting choice if you love a clean, nostalgic scent with polish, as its aldehydes, florals and soft musk create a freshly washed softness that still feels special."
				},
				{
					"handle": "pg31-tonka-bodycon",
					"description": "PG31 Tonka Bodykon is the treat-yourself fragrance with presence: rum, apple and smoky tonka give it a warm, glossy richness that feels both edible and grown-up."
				}
			]
		},
		"for-her": {
			"title": "For Her",
			"subtitle": "Beautiful feminine and feminine-leaning fragrances",
			"category": "By Gender",
			"description": "A little love letter to fragrances that feel flattering, feminine, and made even more special when you choose them for yourself.",
			"products": [
				{
					"handle": "bubble-tea",
					"description": "Bubble Tea is perfect if you want a cozy, skin-close gourmand that feels indulgent without being sugary; the roasted tea and creamy brown sugar make it comforting, while the floral dusting keeps it elegant."
				},
				{
					"handle": "gratia-aestiva",
					"description": "Gratia Aestiva suits you if you like your sweetness bright and confident, with juicy tropical fruit that feels sunlit and joyful, then jasmine and woods adding a polished, grown-up finish."
				},
				{
					"handle": "misty-cypress-ash",
					"description": "Misty Cypress Ash is for the woman who likes a more unexpected kind of femininity: cool, misty, and quietly magnetic, with cypress and tobacco giving it depth and a beautifully unbothered edge."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"description": "PG16 Jardins de Kérylos feels like a graceful signature if you love soft, luminous scents; fig and peach make it tender and pretty, while the woody-floral base keeps it refined and wearable."
				},
				{
					"handle": "pink-sweats",
					"description": "Pink Sweats is a lovely pick if you want something playful but not childish, with rhubarb, lychee, and rosé champagne giving a sparkling, flirty lift before the marshmallow softens everything beautifully."
				},
				{
					"handle": "roasted-green-tea",
					"description": "ほうじ茶 Hojicha is ideal if you’re drawn to gentle comfort with a twist; the roasted tea and milk feel soothing and intimate, while the savory nuances make it more intriguing than a simple cozy scent."
				},
				{
					"handle": "talento",
					"description": "Talento is a beautiful choice if you want a fragrance that feels like a treasured piece of jewelry—fresh rose, mint, and aldehydes create a radiant, protective aura that feels both delicate and strong."
				},
				{
					"handle": "god-bless-cola",
					"description": "God Bless Cola is for treating yourself to pure fun: fizzy cola, caramel popcorn, and vanilla make it feel deliciously nostalgic, with just enough savory sparkle to keep it interesting."
				},
				{
					"handle": "narcotic-v",
					"description": "Narcotic V is the one to reach for if you want to feel undeniably glamorous and sensual; the tuberose and jasmine are lush and head-turning, making it a bold feminine statement with real presence."
				}
			]
		},
		"for-him": {
			"title": "For Him",
			"subtitle": "Masculine and masculine-leaning fragrances",
			"category": "By Gender",
			"description": "A collection for when you want to smell composed, magnetic, and unmistakably in your own lane.",
			"products": [
				{
					"handle": "misty-cypress-ash",
					"description": "Misty Cypress Ash 青岚柏烬 feels like stepping out on a cold morning in a tailored coat: bitter herbs, cypress, and smoky vetiver give you a rugged, mountain-air confidence with just enough warmth underneath."
				},
				{
					"handle": "monsieur",
					"description": "Monsieur is for the man who likes his woods dark, polished, and a little mysterious; the wet-stone freshness keeps the cedar, vetiver, and patchouli feeling refined rather than heavy."
				},
				{
					"handle": "pg-111-indian-wood",
					"description": "PG11.1 Indian Wood suits you if you want something easy to wear but never bland—the peppermint-cardamom opening gives a cool, lifted start before sandalwood and oakmoss settle into a quietly sensual rhythm."
				},
				{
					"handle": "rasputin",
					"description": "Rasputin is the bold choice for nights when you want presence; its chocolate, incense, oud, and leather create a dense, seductive trail that feels rich, dramatic, and unapologetically masculine."
				},
				{
					"handle": "roasted-green-tea",
					"description": "ほうじ茶 Hojicha (Roasted Green Tea) is a comforting signature if you prefer softness over swagger, with roasted tea, milk, and a savory edge that feels intimate, calm, and unexpectedly addictive."
				},
				{
					"handle": "602",
					"description": "602 Bois Narcotique is ideal if you like your woods dry, clean, and understated—the cedar, patchouli, and vetiver read as effortlessly put-together, with just enough spice to keep things interesting."
				},
				{
					"handle": "apotheose",
					"description": "Apothéose gives you vintage elegance with a twist: the aldehydes and eucalyptus feel bright and commanding, while the floral-musky heart adds a plush, slightly uncanny sophistication."
				},
				{
					"handle": "kajal-i",
					"description": "Kajal I is a great pick when you want your fragrance to feel generous and inviting; the juicy citrus opening melts into warm woods, oud, and vanilla for a smooth, confident sensuality."
				},
				{
					"handle": "paradoxial",
					"description": "Paradoxical works beautifully if you want freshness with depth—the citrus, lavender, and incense stay crisp and modern, while vetiver and amber leave a clean, quietly seductive finish."
				}
			]
		},
		"unisex": {
			"title": "Unisex",
			"subtitle": "Gender-fluid fragrances for anyone",
			"category": "By Gender",
			"description": "For the self-gifter who loves freedom over labels, this unisex edit offers scents that feel like choosing your own mood, one beautiful spritz at a time.",
			"products": [
				{
					"handle": "195-a-c",
					"description": "195 A.C. is a lovely pick if you want something fresh but not plain: fig, mint and green pomegranate feel sunlit and alive, while cedar and incense give it a sensual, grown-up finish."
				},
				{
					"handle": "1993",
					"description": "1993 suits you if you like your perfume with attitude—juicy peach and dark chocolate are made sharper by patchouli, creating a nostalgic chypre that feels both plush and commanding."
				},
				{
					"handle": "20359",
					"description": "20359 is for the nights when you want to feel a little daring: cherry sweetness, latex and vanilla make it addictive, while leather, tobacco and cedar keep it sleek and intriguing."
				},
				{
					"handle": "565",
					"description": "565 is a good choice if you enjoy a fragrance that announces itself beautifully; the bright citrus-and-fruit opening melts into amber, musk and incense for a rich, glamorous trail."
				},
				{
					"handle": "afternoon-tea",
					"description": "Afternoon Tea feels perfect if you want something polished and calming, with lemon and maté tea lifted by champaca and jasmine, then grounded by vetiver and a whisper of smoke."
				},
				{
					"handle": "ara",
					"description": "ARС will suit you if you love herbal, old-school elegance: basil, thyme, lavender and pine feel crisp and aromatic, while oakmoss, styrax and benzoin add depth and warmth."
				},
				{
					"handle": "arashiyama",
					"description": "Arashiyama is ideal if you prefer quiet, restorative scents—the bamboo, cut grass and rain-wet earth feel serene and airy, with violet and bamboo wood adding a soft, comforting glow."
				},
				{
					"handle": "arcadia-1",
					"description": "Arcadia is a thoughtful choice if you like your perfume reflective and textured: iris and olibanum create a powdery calm, while tonka and vetiver give it a savory, smoky edge."
				},
				{
					"handle": "bois-dascse",
					"description": "Bois d’Ascese is for when you want something introspective and smoky, with frankincense, tobacco and dry woods wrapped around a peaty whisky accord that feels austere and unforgettable."
				}
			]
		},
		"discovery-sets": {
			"title": "Discovery Sets",
			"subtitle": "Curated sets for fragrance exploration",
			"category": "By Format",
			"description": "A discovery set is a little treat for your curious side—an easy, indulgent way to sample new signatures without committing too soon.",
			"products": [
				{
					"handle": "dark-drunk-discovery-kit",
					"description": "DARK & DRUNK DISCOVERY KIT by BORNTOSTANDOUT feels made for the moodier self-gifter: bold, playful, and perfect if you want your next signature to surprise you. It’s a fun way to explore daring scents when you’re in the mood for something less polished and more magnetic."
				},
				{
					"handle": "j-scent-discovery-set",
					"description": "J-Scent Discovery Set by J-Scent is a lovely pick if you want your self-purchase to feel fresh and quietly uplifting, with green grass, peppermint, yuzu, and violet giving each wear a crisp, poetic edge. The mix of vanilla and geranium keeps it versatile, so you can move from bright daytime spritzes to softer evening moods."
				},
				{
					"handle": "oddity-discovery-set",
					"description": "Oddity discovery set by Oddity suits the person who enjoys fragrance as an artful experiment, with ambroxan, blackcurrant, mandarin, iris, and vetiver creating a modern, textured trail. It’s a smart choice if you like discovering scents that feel a little unusual, polished, and very wearable at the same time."
				}
			]
		},
		"sample-packs": {
			"title": "Sample Packs",
			"subtitle": "Themed sample sets to explore fragrance",
			"category": "By Format",
			"description": "A little self-gifted discovery set is the sweetest way to find a fragrance that feels like it was made for you.",
			"products": [
				{
					"handle": "2025-bestsellers-for-her",
					"description": "Bloom Sample Packs’ 2025 Bestsellers | For Her is perfect if you want an easy, flattering signature to wear on repeat: the milky peach, soft florals and vanilla feel cosy and close to skin, like a polished comfort scent with a gentle glow."
				},
				{
					"handle": "sample-set-1",
					"description": "Stora Skuggan’s Sample set suits the more curious self-purchaser who likes a fragrance with atmosphere and edge; the angelica, chamomile, pine and incense create a cool, woodland haze that feels artistic, calming and a little mysterious."
				}
			]
		},
		"full-bottles-worth-owning": {
			"title": "Full Bottles Worth Owning",
			"subtitle": "Commitment-worthy full bottles that make exceptional gifts",
			"category": "By Format",
			"description": "For the bottle you’ll reach for again and again, these are the polished signatures and indulgent comforts that feel especially rewarding when chosen just for you.",
			"products": [
				{
					"handle": "you-or-someone-like-you",
					"description": "If you love scents that feel clean but never plain, this is a brilliant self-gift: mint, shiso and eucalyptus give you a crisp, green lift, while the woody drydown keeps it quietly addictive."
				},
				{
					"handle": "cashmere-beige",
					"description": "This is for the person who wants softness with a modern edge. The floral haze stays airy and elegant, while cedar and musk make it feel like a beautifully tailored comfort scent you can wear on repeat."
				},
				{
					"handle": "honey-and-tar",
					"description": "Choose this if you want something richer and more enveloping than a typical gourmand. Honey, tar and smoky leather create a decadent, grown-up warmth that feels wonderfully personal on your own skin."
				},
				{
					"handle": "iris-pallida",
					"description": "A refined iris like this makes an excellent treat-yourself bottle because it feels polished without being fussy. The creamy sandalwood and hazelnut soften the powdery iris into something sensual, calm and expensive-smelling."
				},
				{
					"handle": "mauna",
					"description": "This suits someone who likes quiet luxury with a meditative streak. Incense, sandalwood and resin give it a cocooning depth, while the ginger keeps it luminous enough to wear whenever you want to feel centered."
				},
				{
					"handle": "carine",
					"description": "If you’re drawn to florals with backbone, this is a superb full bottle to own. Jasmine and gardenia feel lush and vintage-leaning, but patchouli and vetiver keep the whole composition sharp, confident and beautifully composed."
				},
				{
					"handle": "tonkade",
					"description": "This is the kind of bottle you buy when you want a cozy signature with a darker side. The vanilla and tonka are plush and inviting, yet the woods and incense stop it from ever feeling too sweet."
				},
				{
					"handle": "20359",
					"description": "For a self-purchase with personality, this is charmingly unexpected. Cherry, heliotrope and latex make it playful and slightly provocative, while tobacco and leather give it a night-out depth that lingers."
				},
				{
					"handle": "agarwood",
					"description": "A restrained oud with temple-like serenity, this is ideal if you want something elegant and contemplative. The orange peel and cinnamon add just enough brightness to keep the incense and woods feeling lifted, not heavy."
				}
			]
		},
		"small-but-mighty": {
			"title": "Small but Mighty",
			"subtitle": "Compact-format gifts with outsized character",
			"category": "By Format",
			"description": "Small in size, big on personality—these are the little luxuries that let you treat yourself to something distinctive, playful, and deeply you.",
			"products": [
				{
					"handle": "luca-turin-loves-sample-pack",
					"description": "If you want a smart way to explore without committing, this Luca Turin-linked sample pack is a brilliant self-gift: smoky woods, iris, and florals with real range and personality."
				},
				{
					"handle": "matcha-mist",
					"description": "Brume Matcha is perfect for the moments when you want your fragrance to feel like a calm reset—green, gently bitter, and softly refreshing, like a private tea break."
				},
				{
					"handle": "patchouli-labdanum",
					"description": "Patchouli & Labdanum suits anyone who likes their comfort with backbone: earthy, smoky, and warm, but in a compact format that feels easy to wear every day."
				},
				{
					"handle": "sample-set-1",
					"description": "Stora Skuggan’s sample set is ideal if you love odd, atmospheric scents and want a little cabinet of curiosities for yourself—green, herbal, resinous, and intriguingly textured."
				},
				{
					"handle": "marrakech-cologne",
					"description": "Marrakech Cologne brings a vivid, travel-inspired lift, with orange, mint tea, and incense giving you that bright-but-smoldering feeling in a neat, wearable size."
				},
				{
					"handle": "god-bless-cola",
					"description": "God Bless Cola is a fun, unapologetic treat for when you want your scent to feel like a guilty pleasure—sparkling, sweet, and just a little mischievous."
				},
				{
					"handle": "santorini",
					"description": "Santorini is a lovely self-purchase if you’re drawn to clean sensuality: fig, coconut, and musk make it feel relaxed, polished, and quietly indulgent."
				},
				{
					"handle": "202",
					"description": "202 Frutti Solari is for the mood-lifter in you—juicy mango, melon, and grapefruit make it bright and easygoing, with enough softness to stay elegant."
				},
				{
					"handle": "come-hell-or-high-water-discovery-set",
					"description": "Beaufort’s discovery set is a strong choice if you like your perfume with drama and depth; smoke, leather, and tobacco make each sample feel bold and memorable."
				}
			]
		},
		"my-signature-scent": {
			"title": "My Signature Scent",
			"subtitle": "Distinctive fragrances that become uniquely yours",
			"category": "For You",
			"description": "Choose a scent that feels like your own secret signature—polished, personal, and impossible to mistake for anyone else.",
			"products": [
				{
					"handle": "santal",
					"description": "Santal is ideal if you want a signature that feels composed and effortless, with citrus and lavender giving the sandalwood a crisp, tailored lift. It wears like a well-cut jacket: understated, but unmistakably yours."
				},
				{
					"handle": "match",
					"description": "Match suits someone drawn to quiet intrigue, with soft suede, orris and musky sandalwood creating a clean, skin-close sensuality. Its dry texture gives it an individual edge that feels modern and self-assured."
				},
				{
					"handle": "vetiver-santal",
					"description": "Vetiver Santal is for the person who wants freshness with depth, opening bright before settling into earthy vetiver and milky sandalwood. It has that refined, lived-in warmth that makes a fragrance feel naturally personal."
				},
				{
					"handle": "502-iris-cartagena",
					"description": "502 Iris Cartagena is a beautiful choice if you like your signature scent with a little richness and mystery, where coffee, cocoa and rum are softened by iris and vanilla. The woody base keeps it intimate rather than loud."
				},
				{
					"handle": "iris-trousseau",
					"description": "Iris Trousseau suits a self-purchaser who loves a more expressive signature, with powdery iris, hazelnut and tonka creating a plush, vintage-leaning charm. It feels sensual and distinctive without trying too hard."
				},
				{
					"handle": "vanille-des-afriques",
					"description": "Vanille des Afriques is perfect if you want vanilla with restraint and elegance, wrapped in orris, amber and dry cedar rather than sweetness. It feels luxurious, smooth and quietly addictive on skin."
				},
				{
					"handle": "dirty-rice",
					"description": "DIRTY RICE is a wonderful pick if you’re after something unusual but comforting, with rice, milk and soft woods creating a tender, close-to-body effect. It has a gentle green freshness that makes it feel intimate and original."
				},
				{
					"handle": "lola-at-coat-check",
					"description": "Lola at Coat Check is for someone who likes a fragrance with personality and polish, where grapefruit and cardamom brighten a creamy heart of orris and white chocolate. It lands in that sweet-woody space that feels both flirty and grown-up."
				},
				{
					"handle": "book-personal",
					"description": "Book | Personal is a strong choice for a signature scent that feels calm, intelligent and quietly magnetic, with black tea, cedar and sandalwood creating a clean woody trail. It has the nostalgic softness of pages and air, made for close encounters."
				}
			]
		},
		"confidence-boost": {
			"title": "Confidence Boost",
			"subtitle": "Bold scents that make you feel unstoppable",
			"category": "For You",
			"description": "For the days you want to walk taller, these bold scents wrap you in warmth, smoke and spice that feel like a private power suit.",
			"products": [
				{
					"handle": "agrani",
					"description": "Agrani feels like the most luxurious kind of self-assertion: cherry, saffron and almond open beautifully, then leather, tobacco and oud settle into a warm, vintage swagger that lingers with confidence."
				},
				{
					"handle": "castiamanti",
					"description": "Castiamanti is for when you want your treat to feel sensual and polished at once; its spiced rose, incense and amber glow softly, then vanilla and oud leave you with an intimate, expensive-feeling trail."
				},
				{
					"handle": "velvet-bold",
					"description": "Velvet | Bold gives you that dramatic, dressed-up energy without losing elegance: smoky leather and saffron sharpen the rose, while patchouli and amber make it feel plush, dark and unmistakably commanding."
				},
				{
					"handle": "kamel-oud",
					"description": "Kamel Oud suits the person who wants their confidence to have bite; raspberry and rose add a tempting brightness, but cumin, smoky oud and leather quickly turn it into something daring and magnetic."
				},
				{
					"handle": "nerosa",
					"description": "Nerosa is a powerful rose with backbone, perfect if you like your florals to feel less pretty and more fearless; saffron, oud and leather give it depth, while the generous trail makes a real entrance."
				},
				{
					"handle": "sombra-24",
					"description": "Sombra feels like quiet confidence with a shadowy edge: pepper, saffron and nutmeg give the rose-and-oud heart a crisp spark, then the leathery warmth turns it into something intriguingly self-possessed."
				},
				{
					"handle": "veles",
					"description": "Veles is ideal if you want your fragrance to feel wild and untamed; the tart rhubarb flash keeps the smoky oud alive, while labdanum, tonka and amber make it feel grounded, warm and powerful."
				},
				{
					"handle": "eye-contact",
					"description": "Eye Contact is made for those moments when you want your scent to do the talking; pink pepper and saffron create instant tension, then leather, oud and musk leave a sleek, sensual impression."
				},
				{
					"handle": "vi-et-armis",
					"description": "Vi et Armis is the most uncompromising choice here, perfect if your self-gift is about making a statement; black tea, pepper, incense and tarry woods create a dense, brooding aura with real presence."
				}
			]
		},
		"self-care-sunday": {
			"title": "Self-Care Sunday",
			"subtitle": "Calming, comforting fragrances for your downtime",
			"category": "For You",
			"description": "For your Self-Care Sunday, these are the fragrances that feel like a soft blanket, a deep breath, and a little luxury chosen just for you.",
			"products": [
				{
					"handle": "lumire-blanche",
					"description": "If you want your downtime to feel cocooning and polished, Lumiere Blanche is a beautiful pick: cardamom gives it a gentle lift, while almond milk, sandalwood and tonka wrap you in creamy calm."
				},
				{
					"handle": "cloud-essence-28-08",
					"description": "Cloud Essence 28.08 is perfect when you want something that feels like a quiet reset, with milky rice-powder softness and white musk creating a dreamy, skin-close comfort."
				},
				{
					"handle": "figlia-di-eva",
					"description": "Figlia di Eva suits a self-care mood with a little more contrast, pairing bright bergamot and green fruit with oud for depth, then smoothing it all into a soft, wearable sweetness."
				},
				{
					"handle": "musc-oli",
					"description": "Musc Oli is the one to reach for when you want to feel effortlessly put together at home, with white musk, Cashmeran and sandalwood melting into a discreet, intimate skin scent."
				},
				{
					"handle": "bo-bo",
					"description": "Bo-Bo brings an easy, sunlit calm to slow days, where mandarin, lily of the valley and orange blossom stay airy and clean, grounded by a relaxed woody finish."
				},
				{
					"handle": "concrete",
					"description": "Concrete is for the Sunday when you want comfort with character: its smooth sandalwood and warm spice feel reassuring, while the metallic rose edge keeps it interesting and modern."
				},
				{
					"handle": "davana-vanille",
					"description": "Davana & Vanille is a lovely treat-yourself fragrance if you like your comfort scents light and playful, blending fruity davana and vanilla into something sweet, airy and never too rich."
				},
				{
					"handle": "no-89",
					"description": "No. 89 feels like the fragrance equivalent of an immaculate morning routine, with crisp citrus, aromatic herbs and a quietly classic woody base that feels composed and soothing."
				},
				{
					"handle": "soap-bubble",
					"description": "Soap Bubble is ideal if you want to feel fresh, bright and a little whimsical, with aldehydes, soft florals and creamy musk giving you that clean, uplifting glow."
				}
			]
		},
		"something-nobody-else-wears": {
			"title": "Something Nobody Else Wears",
			"subtitle": "Rare, niche fragrances for true individualists",
			"category": "For You",
			"description": "For the self-purchaser who wants a scent no one else in the room will be wearing, these are bold signatures with real intrigue.",
			"products": [
				{
					"handle": "the-grudge",
					"description": "If you’re drawn to something uncompromising, The Grudge gives you a bracing mix of sea-salt darkness, incense and leather that feels fiercely individual. It’s the kind of fragrance that announces taste, not trend."
				},
				{
					"handle": "in-principio-v2",
					"description": "In principio V2 suits you if you want your solitude to feel luminous rather than loud. The creamy lily, ylang-ylang and ambergris create a contemplative floral that wears like a private, beautifully composed mood."
				},
				{
					"handle": "myrrh-blood-drop",
					"description": "No. 8 – Drop of Blood is for the person who likes their elegance edged with shadow. Its saffron, incense and oud combination feels enigmatic and slightly dangerous, in the best possible self-indulgent way."
				},
				{
					"handle": "castiamanti",
					"description": "Castiamanti is for the romantic who still wants their sweetness wrapped in resin and smoke. Honeyed florals, incense and oud make it intimate and sensual, with a plush warmth that lingers close to the skin."
				},
				{
					"handle": "cologne-d-agar",
					"description": "Cologne d’Agar is a great choice if you want refinement with a pulse beneath it. The bright bergamot and rose keep it polished, while the oud, leather and frankincense give it a wild, memorable signature."
				},
				{
					"handle": "rasputin",
					"description": "Rasputin is ideal if you enjoy your perfume dense, spicy and a little theatrical. Chocolate, incense and Indian oud make it feel warm and magnetic, like a secret you’re happy to keep to yourself."
				},
				{
					"handle": "oud-imperial-extrait",
					"description": "Oud Imperial Extrait suits you if you want oud in its most smouldering, luxurious form. The birch tar, labdanum and smoky wood create a rich, animalic depth that feels expensive and unmistakably niche."
				},
				{
					"handle": "sand",
					"description": "Sand is perfect if you love sandalwood but want it to surprise you. The banana and incense twist gives the wood a playful, unconventional edge, while the benzoin keeps it smooth and wearable."
				},
				{
					"handle": "h-o",
					"description": "H₂O³ is the one to choose if you want something rare, clean, and quietly arresting. Its mineral greens, cucumber and shoreline woods feel fresh yet unusual, like a private escape bottled for you."
				}
			]
		},
		"everyday-essential": {
			"title": "Everyday Essential",
			"subtitle": "Versatile scents that work for any occasion",
			"category": "For You",
			"description": "An easygoing edit of scents that feel polished, comforting, and ready for whatever your day brings—your perfect everyday treat.",
			"products": [
				{
					"handle": "vetiver-santal",
					"description": "Vetiver Santal feels like the kind of signature you can wear on repeat: bergamot keeps it bright, while vetiver and sandalwood give it a calm, grown-up depth that never overwhelms."
				},
				{
					"handle": "santal",
					"description": "Santal is ideal if you want something effortlessly smart and versatile; its citrus-lavender opening feels fresh and tidy, then the woody-spiced base settles into a warm, quietly confident finish."
				},
				{
					"handle": "beso-pasion",
					"description": "Beso Pasión suits days when you want your fragrance to feel relaxed but a little sun-kissed, with fig, citrus, and ginger giving it an easy brightness that melts into soft woods and musk."
				},
				{
					"handle": "hakka-japanese-mint",
					"description": "Hakka (Japanese Mint) is a lovely choice if you like a clean, cooling scent that still feels refined; the minty freshness is lifted by airy florals and gently rounded with musk and cedar."
				},
				{
					"handle": "502-iris-cartagena",
					"description": "502 Iris Cartagena is for someone who likes their everyday scent with a little more character—coffee, cocoa, and iris create a cozy, polished richness that stays intimate rather than heavy."
				},
				{
					"handle": "albatros",
					"description": "Albatros is a beautiful pick if you want a fresh scent with presence; the sea-salt and cedar combination feels crisp and airy, while rose and musk keep it smooth and wearable."
				},
				{
					"handle": "white-rice",
					"description": "White Rice feels quietly comforting and skin-close, with rice, pandan, and soft florals creating a tender, understated scent that’s easy to reach for any day."
				},
				{
					"handle": "mad",
					"description": "Mad is a great everyday vanilla for someone who wants more depth than sweetness alone; bergamot and blackcurrant keep it lively, while smoky woods and vetiver give it a grounded edge."
				},
				{
					"handle": "portamarina",
					"description": "Portamarina feels like a refined all-rounder with a breezy Mediterranean lift—citrus, sage, and salty air make it bright and polished, while iris adds a soft, elegant finish."
				}
			]
		},
		"weekend-mood": {
			"title": "Weekend Mood",
			"subtitle": "Relaxed, casual fragrances for days off",
			"category": "For You",
			"description": "For your days off, these are the scents that feel like slipping into clean linen, sea air, and a little well-earned freedom.",
			"products": [
				{
					"handle": "sal-y-limon",
					"description": "Sal y Limón is the easiest instant holiday for a self-purchase: fizzy citrus and sea salt feel bright and carefree, with a soft musky drydown that stays relaxed rather than loud."
				},
				{
					"handle": "resonant",
					"description": "Resonant suits you if you want a weekend scent with a modern edge—yuzu, ginger and ozonic metallic notes feel crisp and energising, like a cool breeze on a slow morning."
				},
				{
					"handle": "a-grove-by-the-sea",
					"description": "A Grove By The Sea is ideal when you want something quietly transportive: fig leaves, rosemary and sea salt create a green, coastal calm that feels natural and unforced."
				},
				{
					"handle": "portamarina",
					"description": "Portamarina gives you a more polished weekend mood, with citrus, apple and sage wrapped in soft iris for a fresh, elegant scent that still feels easy to wear off-duty."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"description": "PG16 Jardins de Kérylos is perfect if you like your downtime scents gentle and sunlit—fig, peach and leafy greens make it feel tender, airy and beautifully composed."
				},
				{
					"handle": "beso-de-sal",
					"description": "Beso de Sal is for the person who wants freshness with texture: lemon, minty shiso and mineral salt feel invigorating, while cedar and musk keep it grounded and wearable."
				},
				{
					"handle": "figlia-di-eva",
					"description": "Figlia di Eva brings a slightly moodier twist to casual wear, pairing bright bergamot with oud and tonka for a fresh-but-shadowed scent that feels personal and a little unexpected."
				},
				{
					"handle": "elise",
					"description": "Élise is a lovely choice for a soft, breezy weekend signature—marine citrus, neroli and white rose feel clean and uplifting, then melt into a warm, comforting finish."
				},
				{
					"handle": "sous-le-pont-mirabeau",
					"description": "Sous Le Pont Mirabeau suits a reflective day off, with fig, violet leaf and watery woods creating a quietly romantic scent that feels serene, refined and easy to keep close."
				}
			]
		},
		"power-dressing": {
			"title": "Power Dressing",
			"subtitle": "Commanding fragrances for your most important days",
			"category": "For You",
			"description": "For the days you want to walk in already noticed, these scents dress you in confidence, texture, and a little intrigue.",
			"products": [
				{
					"handle": "match",
					"description": "Match is the one for polished composure: its suede-and-orris softness feels tailored and expensive, while the dry woods and ambroxan keep it modern and self-assured."
				},
				{
					"handle": "black",
					"description": "Black suits you if you want power with grit rather than gloss; the peppery smoke, leather, and birch tar feel like a favorite jacket that makes every entrance stronger."
				},
				{
					"handle": "deer",
					"description": "Deer is a quietly commanding choice for someone who prefers depth over drama, with tobacco, hay, and cedar creating a grounded, intelligent presence that lingers beautifully."
				},
				{
					"handle": "drunk-lovers",
					"description": "DRUNK LOVERS is for your boldest moments, wrapping cognac, berries, cinnamon, and amber into a sensual, celebratory trail that feels unmistakably dressed up."
				},
				{
					"handle": "nettuno",
					"description": "Nettuno offers elegance with restraint: its cool rose, soft leather, and airy musk create a refined aura that feels composed, intimate, and quietly magnetic."
				},
				{
					"handle": "01-nagarmotha",
					"description": "01 Nagarmotha is ideal if you want something sharp, earthy, and distinctive; the lime, vetiver, and nagarmotha give it a crisp confidence that never feels overdone."
				},
				{
					"handle": "mainstream",
					"description": "Mainstream brings a bright, unconventional energy to power dressing, with grapefruit, lime, and cannabis cutting through to a dry, earthy finish that feels fearless and modern."
				},
				{
					"handle": "sirenide",
					"description": "Sirenide is the statement piece here, mixing marine freshness with leather, iris, and PVC for a sleek but unsettling edge that makes you impossible to ignore."
				},
				{
					"handle": "woodoo",
					"description": "Woodoo feels like deep, textured authority: incense, cedar, iris, and musk create a dense, shadowy warmth that’s sensual without ever losing its composure."
				}
			]
		},
		"first-niche-fragrance": {
			"title": "First Niche Fragrance",
			"subtitle": "Approachable entry points into the world of niche perfume",
			"category": "For You",
			"description": "A gentle first step into niche perfume, with easy-to-love scents that still feel like a treat you chose just for you.",
			"products": [
				{
					"handle": "rose-shiso",
					"description": "Rose Shiso feels like a polished way to explore niche: the rose is lush, but the shiso keeps it fresh, green, and a little surprising. If you want something elegant without feeling too serious, this is a beautiful first signature."
				},
				{
					"handle": "2025-bestsellers-for-her",
					"description": "This sample pack is ideal if you’re still discovering what you love, because it lets you try a range of soft, wearable styles without committing to one bottle. It’s a comforting, low-pressure way to find your next personal favorite."
				},
				{
					"handle": "fig-porn",
					"description": "FIG PORN is a great pick if you want your first niche fragrance to feel creamy, modern, and instantly appealing. The fig-and-pear brightness keeps it easy to wear, while the vanilla-musk finish gives it that addictive, skin-close warmth."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"description": "Le Musc & La Peau Extrait is for the person who wants a fragrance that feels like beautifully cared-for skin. Its white musk, sandalwood, and soft florals make it quietly sensual and very easy to reach for every day."
				},
				{
					"handle": "concrete",
					"description": "Concrete is a smart choice if you’re curious about niche but want something clean, structured, and distinctive rather than sweet. The woody base feels smooth and modern, with just enough metallic edge to make it memorable."
				},
				{
					"handle": "ramune",
					"description": "Ramune is a joyful entry point if you like your perfumes light, playful, and a little nostalgic. Its sparkling soda-like freshness feels uplifting and easy to wear, like a scent version of a summer treat."
				},
				{
					"handle": "tears-on-mystic-sands",
					"description": "Tears On Mystic Sands gives you a more dramatic first niche experience without becoming too heavy. The bright fruit-and-floral opening keeps it inviting, while the incense, oud, and leather add depth for when you want to feel a bit more special."
				},
				{
					"handle": "dancing-light",
					"description": "Dancing Light is lovely if you’re drawn to crisp, airy perfumes that still have softness underneath. The minty pine opening feels refreshing and clean, while the jasmine and sandalwood keep it graceful and wearable."
				},
				{
					"handle": "debonaire",
					"description": "Debonaire is perfect if you want an approachable niche scent with a tailored, polished feel. Citrus, lavender, and vetiver make it fresh and confident, like the perfume equivalent of putting on your best jacket."
				}
			]
		},
		"upgrade-from-designer": {
			"title": "Upgrade from Designer",
			"subtitle": "Step up from mainstream — same vibes, better quality",
			"category": "For You",
			"description": "If you’re ready to trade familiar designer sparkle for something more polished, these scents keep the same easy charm and add real depth, texture and character.",
			"products": [
				{
					"handle": "no-89",
					"description": "No. 89 is the elegant “same vibe, better made” upgrade: its crisp citrus-and-wood structure feels familiar, but the neroli, oakmoss and sandalwood give it a quietly tailored finish that lasts beautifully on skin."
				},
				{
					"handle": "oud-cannelle",
					"description": "Oud Cannelle is for when you want your step up to feel richer and more sensual, with cinnamon, incense and rose wrapped around a dark oud base that feels more luxurious than loud."
				},
				{
					"handle": "the-empress-kiss",
					"description": "The Empress Kiss suits you if you like a statement scent with a soft, addictive edge; the saffron and clove bring drama, while raspberry, caramel and vanilla keep it plush and wearable."
				},
				{
					"handle": "fig-porn",
					"description": "FIG PORN is the ideal upgrade if you love fresh, modern designer fig scents but want more creaminess and depth, with juicy pear, milky fig and a sensual vanilla-musk finish."
				},
				{
					"handle": "mansa-musa",
					"description": "Mansa Musa is for the person who wants their upgrade to feel unmistakably expensive and bold: the animalic oud, leathered woods and amber create a dense, opulent trail with serious presence."
				},
				{
					"handle": "marcel",
					"description": "Marcel is a refined choice if you enjoy clean cologne styles but want something smoother and more grown-up, with bergamot, rosemary and vetiver giving it an effortlessly polished feel."
				},
				{
					"handle": "sirio",
					"description": "Sirio is perfect when you want your fragrance to feel luminous yet indulgent, with tart rhubarb, rose and vanilla making the woods feel plush, modern and a little decadent."
				},
				{
					"handle": "agarwood",
					"description": "Agarwood is a smart choice if you’re drawn to incensey designer woods but want a more contemplative, artisanal feel, balancing temple-like smoke with citrus brightness and a clean cedar finish."
				},
				{
					"handle": "debonaire",
					"description": "Debonaire is the easiest everyday upgrade here: it keeps the bright, green freshness of a classic designer masculine, but the lavender, neroli and sandalwood make it feel sharper, calmer and more assured."
				}
			]
		},
		"nothing-lasts-on-me": {
			"title": "Nothing Lasts on Me",
			"subtitle": "Heavy-hitters with exceptional longevity and projection",
			"category": "For You",
			"description": "For the days you want your scent to linger like a signature, these rich, high-impact fragrances wrap you in warmth, depth, and unforgettable presence.",
			"products": [
				{
					"handle": "osang",
					"description": "Osang is for when you want to feel daring, magnetic, and a little untamed; its honeyed smoke, fenugreek bite, and animalic depth make it a true skin-clinger with attitude."
				},
				{
					"handle": "a-pas-de-loup",
					"description": "À Pas De Loup suits the person who loves intimacy over flash: damp earth, oakmoss, and sage create a quietly addictive forest mood that stays close but never fades into the background."
				},
				{
					"handle": "patchouli-cannelle",
					"description": "Patchouli Cannelle is a comforting indulgence if you want something plush and enveloping; the spice, resin, and vanilla soften the patchouli into a warm cocoon you can wear all evening."
				},
				{
					"handle": "drunk-lovers",
					"description": "DRUNK LOVERS is the pick for turning an ordinary night into a statement, with boozy cognac, berries, and cinnamon giving you a sensual, radiant trail that feels unapologetically celebratory."
				},
				{
					"handle": "honey-oud",
					"description": "Honey Oud is ideal if you want opulence with a softer edge; the honeyed vanilla and rose smooth oud into something velvety, elegant, and deeply satisfying on the skin."
				},
				{
					"handle": "nerosa",
					"description": "Nerosa is a bold choice for anyone who loves rose but wants it darker and more powerful; saffron, leather, and oud give it a dramatic presence that lasts beautifully."
				},
				{
					"handle": "nebula-viridis",
					"description": "Nebula Viridis works if you like brightness that settles into warmth, opening with citrus and saffron before melting into smoky woods and amber for a refined, golden glow."
				},
				{
					"handle": "gold-balanced",
					"description": "Gold | Balanced & Expressive is made for easy confidence, offering amber, sandalwood, and vanilla in a smooth, wearable way that feels luxurious without overpowering you."
				},
				{
					"handle": "terram",
					"description": "Terram is perfect when you want something grounding and quietly sensual; its earthy patchouli, vetiver, and benzoin create a cozy autumnal trail that feels both natural and polished."
				}
			]
		}
	},
	"quiz": {
		"steps": [
			{
				"question": "What are you looking for?",
				"sub": "This helps us match the right style to you",
				"key": "goal",
				"options": [
					{
						"id": "signature",
						"icon": "✨",
						"title": "My Signature",
						"sub": "One scent to define me"
					},
					{
						"id": "explore",
						"icon": "🔍",
						"title": "Explore & Try",
						"sub": "Discover something new"
					},
					{
						"id": "occasion",
						"icon": "🎯",
						"title": "For an Occasion",
						"sub": "Date night, office, travel"
					},
					{
						"id": "upgrade",
						"icon": "⬆️",
						"title": "Level Up",
						"sub": "Step up from mainstream"
					}
				]
			},
			{
				"question": "What's your budget?",
				"sub": "Every price range has gems",
				"key": "budget",
				"options": [
					{
						"id": "u50",
						"icon": "💰",
						"title": "Under £50",
						"sub": "Smart & stylish"
					},
					{
						"id": "u100",
						"icon": "💎",
						"title": "£50 – £100",
						"sub": "The sweet spot"
					},
					{
						"id": "u150",
						"icon": "🏆",
						"title": "£100 – £150",
						"sub": "Premium territory"
					},
					{
						"id": "over",
						"icon": "👑",
						"title": "£150+",
						"sub": "Go all out"
					}
				]
			},
			{
				"question": "What kind of scent suits you?",
				"sub": "Trust your instinct",
				"key": "mood",
				"options": [
					{
						"id": "fresh",
						"icon": "🍃",
						"title": "Fresh & Clean",
						"sub": "Light, airy, breezy"
					},
					{
						"id": "warm",
						"icon": "🧣",
						"title": "Warm & Cosy",
						"sub": "Vanilla, amber, soft"
					},
					{
						"id": "sweet",
						"icon": "🍯",
						"title": "Sweet & Gourmand",
						"sub": "Edible, addictive"
					},
					{
						"id": "dark",
						"icon": "🔥",
						"title": "Dark & Smoky",
						"sub": "Leather, incense, oud"
					},
					{
						"id": "floral",
						"icon": "🌺",
						"title": "Floral & Romantic",
						"sub": "Rose, jasmine, soft"
					},
					{
						"id": "surprise",
						"icon": "🎲",
						"title": "Surprise Me",
						"sub": "Dealer's choice"
					}
				]
			}
		],
		"map": {
			"goal": {
				"signature": [
					"My Signature Scent",
					"Scents They'll Remember You For",
					"Long-Lasting Perfumes",
					"Smell Expensive",
					"Quiet Luxury",
					"Perfumes for Compliments"
				],
				"explore": [
					"First Niche Fragrance",
					"For the Curious Nose",
					"Brands Before They Blow Up",
					"Unexpected Favourites",
					"Discovery Sets",
					"Sample Packs"
				],
				"occasion": [
					"Date Night Scents",
					"Best for the Office",
					"Confidence Boost",
					"Power Dressing",
					"Weekend Mood",
					"Bedtime Fragrances"
				],
				"upgrade": [
					"Upgrade from Designer",
					"Full Bottles Worth Owning",
					"Cult Classics",
					"For the Collector",
					"Something Nobody Else Wears",
					"Scents With Personality"
				]
			},
			"budget": {
				"u50": [
					"Under £50",
					"Best Value Per ML",
					"Small but Mighty",
					"Sample Packs",
					"Travel-Friendly Fragrances"
				],
				"u100": [
					"Under £100",
					"Discovery Sets",
					"Everyday Essential",
					"First Niche Fragrance"
				],
				"u150": [
					"Under £150",
					"Full Bottles Worth Owning",
					"Made in Britain",
					"French Perfumery",
					"Italian Houses",
					"Japanese Scents"
				],
				"over": [
					"Smell Expensive",
					"For the Collector",
					"Middle Eastern Luxury",
					"Something Nobody Else Wears",
					"Oud Collection"
				]
			},
			"mood": {
				"fresh": [
					"Fresh Escape",
					"Clean Girl Aesthetic",
					"Joyful Citrus",
					"Tea Scents",
					"Salt, Sea & Mineral",
					"Neroli & Orange Blossom",
					"Spring Scents",
					"Summer in a Bottle",
					"Sunshine Scents",
					"Best for the Office",
					"Lavender Reimagined",
					"Fig, Green & Herbaceous",
					"Everyday Essential"
				],
				"warm": [
					"Cosy Comfort",
					"Vanilla Beyond Vanilla",
					"Sandalwood",
					"Autumn Mood",
					"Winter Warmers",
					"Festive Scents",
					"Bedtime Fragrances",
					"Quiet Luxury",
					"Cottagecore",
					"Powdery & Iris",
					"Soft Skin Scents",
					"Nostalgia in a Bottle",
					"Self-Care Sunday"
				],
				"sweet": [
					"Sweet but Sophisticated",
					"Chocolate & Cocoa",
					"Coffee Fragrances",
					"Boozy Scents",
					"Cherry & Red Fruits",
					"Perfumes for Compliments",
					"Scents With Personality",
					"Date Night Scents"
				],
				"dark": [
					"Dark & Smoky",
					"Oud Collection",
					"Leather & Suede",
					"Incense & Resin",
					"Dark Academia",
					"Main Character Energy",
					"Middle Eastern Luxury",
					"Long-Lasting Perfumes",
					"Scents That Tell a Story",
					"Confidence Boost",
					"Power Dressing",
					"Nothing Lasts on Me"
				],
				"floral": [
					"Rose Lovers",
					"White Flowers",
					"For the Romantic",
					"Spring Scents",
					"Cottagecore",
					"Self-Care Sunday",
					"Soft Skin Scents"
				],
				"surprise": [
					"The Easy Winners",
					"Unexpected Favourites",
					"Perfumes That Don't Smell Like Perfume",
					"For the Curious Nose",
					"Brands Before They Blow Up",
					"Scents They'll Remember You For",
					"Cult Classics",
					"Gender-Bending Scents"
				]
			}
		}
	},
	"browse": {
		"menu": {
			"Budget & Format": {
				"sections": [
					{
						"label": "By Budget",
						"items": [
							"Under £50",
							"Under £100",
							"Under £150",
							"Best Value Per ML"
						]
					},
					{
						"label": "By Format",
						"items": [
							"Discovery Sets",
							"Sample Packs",
							"Full Bottles Worth Owning",
							"Small but Mighty",
							"Travel-Friendly Fragrances"
						]
					}
				]
			},
			"By Mood & Aesthetic": {
				"sections": [
					{
						"label": "By Mood",
						"items": [
							"Cosy Comfort",
							"Fresh Escape",
							"Dark & Smoky",
							"Soft Skin Scents",
							"Sweet but Sophisticated",
							"Joyful Citrus"
						]
					},
					{
						"label": "By Aesthetic",
						"items": [
							"Quiet Luxury",
							"Clean Girl Aesthetic",
							"Dark Academia",
							"Main Character Energy",
							"Cottagecore"
						]
					}
				]
			},
			"By Scent": {
				"sections": [
					{
						"label": "Scent Families",
						"items": [
							"Sandalwood",
							"Vanilla Beyond Vanilla",
							"Rose Lovers",
							"Cherry & Red Fruits",
							"Tea Scents",
							"Incense & Resin",
							"Neroli & Orange Blossom",
							"Fig, Green & Herbaceous",
							"Salt, Sea & Mineral"
						]
					},
					{
						"label": "More Notes",
						"items": [
							"Oud Collection",
							"White Flowers",
							"Leather & Suede",
							"Coffee Fragrances",
							"Chocolate & Cocoa",
							"Boozy Scents",
							"Powdery & Iris",
							"Lavender Reimagined"
						]
					}
				]
			},
			"For You": {
				"sections": [
					{
						"label": "Your Journey",
						"items": [
							"First Niche Fragrance",
							"Upgrade from Designer",
							"My Signature Scent",
							"Something Nobody Else Wears"
						]
					},
					{
						"label": "Your Lifestyle",
						"items": [
							"Everyday Essential",
							"Weekend Mood",
							"Power Dressing",
							"Confidence Boost",
							"Self-Care Sunday",
							"Nothing Lasts on Me"
						]
					},
					{
						"label": "Your Style",
						"items": [
							"For the Curious Nose",
							"For the Minimalist",
							"For the Maximalist",
							"For the Collector",
							"For the Romantic",
							"For the Trend-Lover"
						]
					}
				]
			},
			"Gender & Season": {
				"sections": [
					{
						"label": "By Gender",
						"items": [
							"For Her",
							"For Him",
							"Unisex"
						]
					},
					{
						"label": "By Season",
						"items": [
							"Spring Scents",
							"Summer in a Bottle",
							"Autumn Mood",
							"Winter Warmers",
							"Festive Scents",
							"Sunshine Scents"
						]
					},
					{
						"label": "By Function",
						"items": [
							"Date Night Scents",
							"Best for the Office",
							"Long-Lasting Perfumes",
							"Bedtime Fragrances"
						]
					}
				]
			},
			"Curated & Origin": {
				"sections": [
					{
						"label": "Editorial",
						"items": [
							"Smell Expensive",
							"The Easy Winners",
							"Perfumes for Compliments",
							"Cult Classics",
							"Unexpected Favourites",
							"Nostalgia in a Bottle"
						]
					},
					{
						"label": "More Editorial",
						"items": [
							"Scents With Personality",
							"Scents They'll Remember You For",
							"Scents That Tell a Story",
							"Brands Before They Blow Up",
							"Perfumes That Don't Smell Like Perfume",
							"Gender-Bending Scents"
						]
					},
					{
						"label": "By Origin",
						"items": [
							"Made in Britain",
							"French Perfumery",
							"Italian Houses",
							"Japanese Scents",
							"Middle Eastern Luxury"
						]
					}
				]
			}
		},
		"icons": {
			"Budget & Format": "£",
			"By Mood & Aesthetic": "✨",
			"By Scent": "🌺",
			"For You": "💜",
			"Gender & Season": "🌟",
			"Curated & Origin": "★"
		}
	}
};
